import './style.css';
import * as Tone from 'tone';
import { inject } from '@vercel/analytics';
import {
  makeKick, makeSnare, makeHat, makeClap, makeTone,
  make808Kick, make808Snare, make808Clap, make808ClosedHat, make808Cowbell, make808Tom, make808Rim,
  DEFAULT_808_KICK_DECAY, MIN_808_KICK_DECAY, MAX_808_KICK_DECAY,
  SNARE_DECAY, HAT_DECAY,
  DEFAULT_GLIDE, glideToSeconds,
  startAudio, bounceToWav, audioBufferToWavBlob, ensureSamplesLoaded,
  TONE_TIMBRES, type ToneTimbre, type Voice, type AudioSource,
} from './audio';
import { Sequencer, DEFAULT_STEPS, MAX_STEPS, type Track } from './sequencer';
import { Collab, generateRoomId, parseRoom, type Delta, type Peer } from './collab';
import { initAnalytics, track, recordPlayStart, recordPlayStop, setCollabActive } from './analytics';
import { copyTextNow, ShortLinkCache } from './share-links';

// ---- Timing constants (ms) ----
// Keep a swapped-out voice alive briefly so notes already scheduled on it ring
// out instead of cutting off mid-tail.
const VOICE_DISPOSE_DELAY_MS = 2000;
const TOAST_DURATION_MS = 2400;       // auto-dismiss for the top-center toast pill
const TIP_TOAST_DELAY_MS = 600;       // delay before the first-run drag-to-pitch tip
const MODAL_FADE_MS = 220;            // overlay fade-out before removal
const REMOTE_EDIT_FLASH_MS = 700;     // cell highlight after a collaborator edit
const WAV_ERROR_DURATION_MS = 3500;   // inline WAV-render error chip lifetime
const SPARK_FLASH_MS = 600;           // Inspire button spark animation duration

// Mobile bars chip: big count + a small "BAR(S)" unit so the control reads as
// a length, not a cryptic "+2". The desktop vertical bars bar uses its own
function barsChipMarkup(n: number): string {
  return `+${n}`;
}

// Wraps a voice factory so the actual Tone audio node isn't created until the
// first trigger() (which only happens after the user clicks the splash and
// the AudioContext is allowed to start). setVolume calls before the voice
// exists are stashed and replayed on instantiation. Without this, importing
// audio.ts and constructing the App would build the entire audio graph
// before any user gesture, flooding the console with "AudioContext was not
// allowed to start" warnings.
function deferredVoice(factory: () => Voice): Voice {
  let real: Voice | null = null;
  let pendingVolume: number | null = null;
  let pendingDecay: number | null = null;
  let pendingGlide: number | null = null;
  const ensure = (): Voice => {
    if (!real) {
      real = factory();
      if (pendingVolume != null) real.setVolume(pendingVolume);
      if (pendingDecay != null) real.setDecay?.(pendingDecay);
      if (pendingGlide != null) real.setGlide?.(pendingGlide);
    }
    return real;
  };
  return {
    setVolume(v: number) {
      if (real) real.setVolume(v);
      else pendingVolume = v;
    },
    setDecay(seconds: number) {
      if (real) real.setDecay?.(seconds);
      else pendingDecay = seconds;
    },
    setGlide(seconds: number) {
      if (real) real.setGlide?.(seconds);
      else pendingGlide = seconds;
    },
    trigger(time, vel, pitch, opts) {
      ensure().trigger(time, vel, pitch, opts);
    },
    release() { real?.release?.(); },
    dispose() { real?.dispose?.(); },
  };
}

inject();
initAnalytics();

type TrackDef = {
  name: string;
  // Single-letter-ish short label for narrow viewports (defaults to name[0]).
  short?: string;
  // Hash key for this track's pattern (and its mixer-volume entry).
  key: string;
  color: string;
  // Builds the voice for this slot given the current engine + tone timbre.
  // Drum slots flip electro ↔ organic ↔ 808 here; the 808-only slots ignore
  // the args and always build their dedicated TR-808 voice.
  voice: (source: AudioSource, timbre: ToneTimbre) => Track['voice'];
  defaultPattern: number[];
  pitched?: boolean;
  // True for the supplemental TR-808 voices (cowbell / tom / rim). Their rows
  // are hidden when the engine isn't 808, but the data is preserved so the
  // groove survives a source switch.
  only808?: boolean;
};

// Unified track layout — every engine shares it. Slots 0–4 are the universal
// kick / snare / hat / clap / tone; slots 5–7 are the supplemental TR-808
// voices (cowbell / tom / rim) that only show up in 808 mode. Switching the
// audio source just swaps which synthesizer plays each slot — the groove
// (pattern + pitches) carries across untouched.
const UNIFIED_TRACK_DEFS: TrackDef[] = [
  { name: 'KICK',  short: 'K',  key: 'k', color: '#ff5b1f', defaultPattern: [0, 6, 10],
    voice: (src) => src === '808' ? make808Kick() : makeKick(src) },
  { name: 'SNARE', short: 'S',  key: 's', color: '#d4a44e', defaultPattern: [4, 12],
    voice: (src) => src === '808' ? make808Snare() : makeSnare(src) },
  { name: 'HAT',   short: 'H',  key: 'h', color: '#3ddc84', defaultPattern: [0, 2, 4, 6, 8, 10, 12, 14],
    voice: (src) => src === '808' ? make808ClosedHat() : makeHat(src) },
  { name: 'CLAP',  short: 'C', key: 'c', color: '#7c5cff', defaultPattern: [4, 12],
    voice: (src) => src === '808' ? make808Clap() : makeClap(src) },
  { name: 'TONE',  short: 'T',  key: 't', color: '#4dc3ff', defaultPattern: [], pitched: true,
    voice: (src, timbre) => makeTone(timbre, src) },
  { name: 'COW',   short: 'C', key: 'b', color: '#e8c34a', defaultPattern: [], only808: true,
    voice: () => make808Cowbell() },
  { name: 'TOM',   short: 'T', key: 'm', color: '#ff8a3d', defaultPattern: [], only808: true,
    voice: () => make808Tom() },
  { name: 'RIM',   short: 'R', key: 'r', color: '#9aa0ff', defaultPattern: [], only808: true,
    voice: () => make808Rim() },
];
const TONE_TRACK_IDX = UNIFIED_TRACK_DEFS.findIndex(d => d.pitched);

const AUDIO_SOURCES: { id: AudioSource; label: string; tip: string }[] = [
  { id: 'electro', label: 'Electro', tip: 'Synthesized oscillator voices' },
  { id: 'organic', label: 'Organic', tip: 'Real recordings (drums + piano)' },
  { id: '808',     label: '808',     tip: 'Synthesized TR-808 drum kit' },
];
const AUDIO_SOURCE_IDS = new Set<string>(AUDIO_SOURCES.map(s => s.id));
// Backward-compat: old shared links / saved beats used src=synth | src=sample.
const LEGACY_SOURCE_ALIAS: Record<string, AudioSource> = { synth: 'electro', sample: 'organic' };
const DEFAULT_AUDIO_SOURCE: AudioSource = 'electro';
function parseSource(raw: string | null): AudioSource {
  if (!raw) return DEFAULT_AUDIO_SOURCE;
  if (AUDIO_SOURCE_IDS.has(raw)) return raw as AudioSource;
  return LEGACY_SOURCE_ALIAS[raw] ?? DEFAULT_AUDIO_SOURCE;
}

// Tone-track engine — independent of the drum engine. Only Electro / Organic
// (the 808 is a drum-kit synth; tone rows use the standard FM/sample voices).
// At startup toneSource auto-follows audioSource (Electro/808 → electro, Organic
// → organic). Once the user manually picks a tone source the auto-follow is
// disabled and the choice is sticky.
export type ToneSource = 'electro' | 'organic';
const TONE_SOURCES: { id: ToneSource; label: string; tip: string }[] = [
  { id: 'electro', label: 'Electro', tip: 'Synthesized tone voices' },
  { id: 'organic', label: 'Organic', tip: 'Real recordings (piano / bass / etc.)' },
];
const TONE_SOURCE_IDS = new Set<string>(TONE_SOURCES.map(s => s.id));
const DEFAULT_TONE_SOURCE: ToneSource = 'electro';
function deriveToneSource(audio: AudioSource): ToneSource {
  return audio === 'organic' ? 'organic' : 'electro';
}
function parseToneSourceParam(raw: string | null): ToneSource | null {
  if (!raw) return null;
  if (TONE_SOURCE_IDS.has(raw)) return raw as ToneSource;
  return null;
}

// Transport SVG icons. Inline so they inherit `currentColor`, can scale via
// CSS, and need no extra HTTP requests. All icons drawn on a 24×24 viewBox.
const ICON_PLAY  = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>';
const ICON_PAUSE = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></svg>';
const ICON_CLEAR = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6h18"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><path d="m5 6 1 14a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-14"/></svg>';
const ICON_UNDO  = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 14 4 9l5-5"/><path d="M4 9h11a5 5 0 0 1 0 10h-4"/></svg>';
const ICON_REDO  = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m15 14 5-5-5-5"/><path d="M20 9H9a5 5 0 0 0 0 10h4"/></svg>';
// Standard iOS-style share sheet glyph (box with an arrow leaving the top) —
// instantly recognizable for "send this elsewhere". Used by the Web Share API
// button.
const ICON_SHARE = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7"/><path d="m16 6-4-4-4 4"/><path d="M12 2v14"/></svg>';
// "People+" — two figures plus a small plus mark. Reads as "bring others into
// this session" → the live-collab action, deliberately distinct from share.
const ICON_COLLAB = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M19 8v6"/><path d="M22 11h-6"/></svg>';
// Chain link — used by the ROOM label in the presence row to copy the live URL.
const ICON_LINK  = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>';
const ICON_SAVE  = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z"/></svg>';
const ICON_CHECK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';
const ICON_SPARK = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2 13.5 8 20 9.5 13.5 11 12 17 10.5 11 4 9.5 10.5 8 12 2zM19 14l.8 3 3 .8-3 .8L19 22l-.8-3-3-.8 3-.8L19 14zM5 14l.8 3 3 .8-3 .8L5 22l-.8-3L1 18.2l3-.8L5 14z"/></svg>';
const ICON_PENCIL = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>';
const ICON_X     = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>';
const ICON_ALERT = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M12 8v4"/><path d="M12 16h.01"/></svg>';
// Splash "how it works" step icons: a 2×2 grid (build), mixer sliders (tune),
// and the shared people-glyph (jam) — reuses ICON_COLLAB for step 3.
const ICON_GRID = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="3" width="7" height="7" rx="1.2"/><rect x="14" y="3" width="7" height="7" rx="1.2"/><rect x="3" y="14" width="7" height="7" rx="1.2"/><rect x="14" y="14" width="7" height="7" rx="1.2"/></svg>';
const ICON_SLIDERS = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></svg>';

// Pitch range for tonal track: 4 octaves around C3 (MIDI 48). C1..C5.
const MIN_PITCH = 24;
const MAX_PITCH = 72;
const DEFAULT_PITCH = 48;
// Vertical drag sensitivity for tone-pitch gestures: px of finger/cursor travel
// per semitone.
const TONE_PX_PER_SEMITONE = 8;

// Per-track volume range. Tone starts hotter than the drums so the FM stab
// punches through the mix; users can dial it back if desired.
const VOL_MIN = 0;
const VOL_MAX = 1.5;
const VOL_STEP = 0.05;
// Keyed across both layouts: k/s/h/c/t (standard) + o/b/m/r (808-only).
const DEFAULT_VOLUMES: Record<string, number> = { k: 0.75, s: 0.75, h: 0.75, c: 0.75, t: 0.75, o: 0.75, b: 0.75, m: 0.75, r: 0.75 };

type Preset = {
  label: string;
  bpm: number;
  bars?: number;
  patterns: number[][];
  tonePitches?: Record<number, number>;
  // Optional: when set, loadPreset swaps the tone voice to this timbre so
  // the preset plays back as intended (e.g. Bass voice for 808-bass riffs).
  toneTimbre?: ToneTimbre;
};

// Curated preset library — instantly-recognizable beats. The boom-bap-leaning
// named beats here, plus the TR-808 classics appended further down (see
// PRESETS.push below), all share one array so the Inspire picker treats them
// uniformly. Each tone-bearing preset specifies the voice that best fits the
// original recording. Patterns transcribed from published drum tabs / canonical
// sources. Pitches are tuned to sit inside each sample voice's sweet spot:
//   bass (electric)  E1..C#5  best E1-G3
//   piano (Salamander) A0..C6 full range
//   pluck (electric guitar) C#2..C6 best E2-A4
// Layout: [kick, snare, hat, clap, tone, cowbell, tom, rim] (older entries may
// list only the first 5 slots; loadPreset maps whatever's present onto all 8).
// Multi-bar presets use preset.bars * 16 steps.
const PRESETS: Preset[] = [
  {
    // Michael Jackson, "Billie Jean" (1982). Ndugu Chancler / Louis Johnson.
    // 4-on-the-floor kick, backbeat snare, 8th-note hats. Iconic synth-bass
    // intro riff transcribed as 8 eighth notes in F# minor:
    //   F# C# E F# E C# B C#
    // (Greg Phillinganes' Moog line, also doubled by Louis Johnson's bass.)
    label: 'Billie Jean',
    bpm: 117,
    patterns: [
      [0, 4, 8, 12],
      [4, 12],
      [0, 2, 4, 6, 8, 10, 12, 14],
      [],
      [0, 2, 4, 6, 8, 10, 12, 14],
      // 808 voices: a restrained cowbell on the 1 and 3 — the same machine,
      // the same year as "Planet Rock"; in 808 mode it nudges the four-on-the-
      // floor toward electro without overwriting the original feel.
      [0, 8],   // cowbell
      [],       // tom
      [],       // rim
    ],
    tonePitches: { 0: 54, 2: 49, 4: 52, 6: 54, 8: 52, 10: 49, 12: 47, 14: 49 },
    toneTimbre: 'bass',
  },
  {
    // The Winstons, "Amen, Brother" (1969) — Gregory Coleman.
    // Single-bar simplification of bar 1 of the 4-bar break.
    label: 'Amen Break',
    bpm: 136,
    patterns: [
      [0, 2, 10, 11],
      [4, 7, 9, 12, 15],
      [0, 2, 4, 6, 8, 10, 12, 14],
      [],
      [],
    ],
  },
  {
    // The White Stripes, "Seven Nation Army" (2003). Meg White / Jack White.
    // 7-note E-minor riff in 4/4 at 120 BPM, spanning 2 bars:
    //   E (dotted quarter) E (8th) G (quarter) E (8th) D (8th)
    //   | C (half) B (half)
    label: 'Seven Nation Army',
    bpm: 120,
    bars: 2,
    patterns: [
      [0, 8, 16, 24],
      [4, 12, 20, 28],
      [],
      [4, 12, 20, 28],
      [0, 6, 8, 12, 14, 16, 24],
      [],              // cowbell
      // 808 tom: a quick descending fill on the "e-and-a" of the final beat —
      // a Meg-White-style turnaround that fills the otherwise hatless loop.
      [29, 30, 31],    // tom
      [],              // rim
    ],
    tonePitches: {
      0: 52,   // E3 — dotted quarter
      6: 52,   // E3 — eighth
      8: 55,   // G3 — quarter
      12: 52,  // E3 — eighth
      14: 50,  // D3 — eighth
      16: 48,  // C3 — half (bar 2 beat 1)
      24: 47,  // B2 — half (bar 2 beat 3)
    },
    toneTimbre: 'bass',
  },
  {
    // Mobb Deep, "Shook Ones, Pt. II" (1995). Havoc. Drums only — the
    // iconic Herbie Hancock "Jessica" piano sample is unverifiable as a
    // single-note line, so we ship the boom-bap drum pocket on its own.
    label: 'Shook Ones',
    bpm: 95,
    patterns: [
      [0, 6, 10],
      [4, 12],
      [0, 2, 4, 6, 8, 10, 12, 14],
      [],
      [],
      [],      // cowbell
      [],      // tom
      // 808 rimshot: one cross-stick click on the "a" of 4 — a boom-bap
      // turnaround ghost that adds bounce to an otherwise stark pocket.
      [14],    // rim
    ],
  },
  {
    // Dr. Dre & Snoop, "Still D.R.E." (1999). Scott Storch piano ostinato.
    // The riff is a 3-chord eighth-note pattern (Am 1st-inv / Esus4 2nd-inv /
    // Em 2nd-inv, in tutorial key). Top-voice reduction: A4 across bar 1
    // (Am), A4 over Esus4 (bar 2 first half), G4 over Em (bar 2 second half).
    label: 'Still D.R.E.',
    bpm: 93,
    bars: 2,
    patterns: [
      [0, 6, 10, 16, 22, 26],
      [4, 12, 20, 28],
      [0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24, 26, 28, 30],
      [],
      [0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24, 26, 28, 30],
    ],
    tonePitches: {
      0: 69, 2: 69, 4: 69, 6: 69, 8: 69, 10: 69, 12: 69, 14: 69,    // A4 — bar 1 (Am)
      16: 69, 18: 69, 20: 69, 22: 69,                                // A4 — Esus4 first half
      24: 67, 26: 67, 28: 67, 30: 67,                                // G4 — Em second half
    },
    toneTimbre: 'piano',
  },
  {
    // A Tribe Called Quest, "Can I Kick It?" (1990). Bassline sampled from
    // Lou Reed "Walk on the Wild Side" (Herbie Flowers, 1972).
    //   Bar 1 (C): C2  C2  E2  C2
    //   Bar 2 (F): F2  F2  A2  F2
    // C2=36, E2=40, F2=41, A2=45. Quarter-note pulse on steps 0,4,8,12.
    label: 'Can I Kick It?',
    bpm: 98,
    bars: 2,
    patterns: [
      [0, 10, 16, 26],
      [4, 12, 20, 28],
      [0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24, 26, 28, 30],
      [],
      [0, 4, 8, 12, 16, 20, 24, 28],
    ],
    tonePitches: {
      0: 36, 4: 36, 8: 40, 12: 36,    // C, C, E (slide), C — over C chord
      16: 41, 20: 41, 24: 45, 28: 41, // F, F, A (slide), F — over F chord
    },
    toneTimbre: 'bass',
  },
  {
    // The Incredible Bongo Band, "Apache" (1973) — the most-sampled drum
    // break in hip-hop. Kick syncopated through the "and-of-2 / and-of-3,"
    // backbeat snare, 8th-note hats. The melodic lead nods to the Shadows'
    // original Apache guitar in E minor: sparse E3-G3-B3 climb then a D3
    // resolution, played on the pluck voice (electric guitar sample) to
    // get the twangy single-note feel.
    label: 'Apache',
    bpm: 115,
    patterns: [
      [0, 7, 10],
      [4, 12],
      [0, 2, 4, 6, 8, 10, 12, 14],
      [],
      [0, 6, 10, 14],
      // 808 voices for the percussion-heavy "Apache" break: an offbeat cowbell
      // (the Bongo Band's record is wall-to-wall Latin percussion) and a single
      // tom pickup on the last 16th, nodding to the famous bongo-break turnaround.
      [2, 6, 10, 14],  // cowbell
      [15],            // tom
      [],              // rim
    ],
    tonePitches: {
      0: 52,   // E3
      6: 59,   // B3
      10: 55,  // G3
      14: 50,  // D3
    },
    toneTimbre: 'pluck',
  },
  {
    // Stevie Wonder, "Superstition" (1972). The clavinet ostinato in Eb
    // minor is one of the most recognizable funk riffs ever recorded —
    // a percussive Bb3/Db4/Eb4 hammer-on pattern over a 16th-note groove.
    // Played on pluck (electric guitar sample) since its sharp attack is
    // closer to a clavinet than the FM synth voice. 16th hats, kick on the
    // funk pattern (1, "and-of-2," "and-of-3"), snare on the backbeat.
    label: 'Superstition',
    bpm: 100,
    patterns: [
      [0, 6, 10],
      [4, 12],
      [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
      [],
      [0, 3, 5, 6, 8, 10, 13, 14],
    ],
    tonePitches: {
      0: 63,   // Eb4
      3: 58,   // Bb3
      5: 63,   // Eb4
      6: 61,   // Db4
      8: 63,   // Eb4
      10: 58,  // Bb3
      13: 63,  // Eb4
      14: 61,  // Db4
    },
    toneTimbre: 'pluck',
  },
  {
    // James Brown, "Funky Drummer" (1970) — Clyde Stubblefield's break,
    // the most-sampled drum break in pop music. 16th-note ghost-note hat,
    // snare on 2 + 4 with 16th-note ghosts on 2.e and 3.a, syncopated kick.
    // Bass holds the C7 vamp underneath (C2 root + the G2 push on the
    // and-of-3) — sparse enough to keep the drums in front.
    label: 'Funky Drummer',
    bpm: 100,
    patterns: [
      [0, 3, 8, 11],
      [4, 7, 12, 14],
      [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
      [4, 12],
      [0, 6, 10, 14],
    ],
    tonePitches: { 0: 36, 6: 36, 10: 43, 14: 36 },
    toneTimbre: 'bass',
  },
  {
    // Antonio Carlos Jobim & Joao Gilberto, "Garota de Ipanema" / "The Girl
    // from Ipanema" (1962). Classic bossa nova at 130 BPM. Drums hold the
    // samba pattern — partido-alto rim-click on the snare track, dotted-
    // quarter "bossa kick," 8th-note hats. Piano voice carries the iconic
    // opening melody in F major: "Tall and tan and young and lovely / the
    // girl from Ipanema goes walking" — across 2 bars.
    label: 'Ipanema',
    bpm: 130,
    bars: 2,
    patterns: [
      [0, 6, 10, 16, 22, 26],
      [3, 6, 10, 13, 19, 22, 26, 29],
      [0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24, 26, 28, 30],
      [],
      [0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24, 26, 28, 30],
    ],
    tonePitches: {
      // bar 1: "Tall and tan and young and love-ly"
      0: 67,  2: 65, 4: 67,  6: 65, 8: 67,  10: 65, 12: 64, 14: 62,
      // bar 2: "the girl from I-pa-ne-ma goes"
      16: 65, 18: 65, 20: 64, 22: 62, 24: 60, 26: 60, 28: 60, 30: 62,
    },
    toneTimbre: 'piano',
  },
];

// Hash format:
//   #bpm=N&bars=N&k=...&s=...&h=...&c=...&t=step:midi,step:midi,...&vol=k:1.00,s:1.00,h:1.00,c:1.00,t:1.20
// Drum tracks are comma-separated step indices. The tonal track encodes
// `step:midi` pairs so pitch is preserved across share links. The `vol`
// param encodes the per-track mix as `key:level` pairs so shared links
// preserve the mixer state.
const TONE_TIMBRE_IDS = new Set(TONE_TIMBRES.map(t => t.id));
const DEFAULT_TONE_TIMBRE: ToneTimbre = 'synth';

function isTimbreAvailable(_t: ToneTimbre): boolean {
  return true;
}

// Scale lock — clamps tone-row pitches to in-scale notes (C-rooted for now).
type ScaleId = 'free' | 'major' | 'minor' | 'pentatonic';
const SCALES: { id: ScaleId; label: string; intervals: number[] | null }[] = [
  { id: 'free',       label: 'Free', intervals: null },
  { id: 'major',      label: 'Major', intervals: [0, 2, 4, 5, 7, 9, 11] },
  { id: 'minor',      label: 'Minor', intervals: [0, 2, 3, 5, 7, 8, 10] },
  { id: 'pentatonic', label: 'Pent',  intervals: [0, 2, 4, 7, 9] },
];
const SCALE_IDS = new Set<string>(SCALES.map(s => s.id));
const DEFAULT_SCALE: ScaleId = 'free';
const SCALE_ROOT = 0; // C

function snapToScale(midi: number, scaleId: ScaleId): number {
  const scale = SCALES.find(s => s.id === scaleId);
  if (!scale || !scale.intervals) return midi;
  const intervals = scale.intervals;
  let best = midi;
  let bestDist = Infinity;
  for (let delta = -6; delta <= 6; delta++) {
    const cand = midi + delta;
    if (cand < MIN_PITCH || cand > MAX_PITCH) continue;
    const pc = ((cand - SCALE_ROOT) % 12 + 12) % 12;
    if (intervals.includes(pc)) {
      const d = Math.abs(delta);
      if (d < bestDist) { bestDist = d; best = cand; }
    }
  }
  return best;
}

// Tempo slider range — keep in sync with the bpm <input type=range> setup.
const MIN_BPM = 60;
const MAX_BPM = 180;
function clampBpm(b: number): number | null {
  if (!isFinite(b)) return null;
  return Math.max(MIN_BPM, Math.min(MAX_BPM, Math.round(b)));
}

function clampKickDecay(d: number): number {
  if (!isFinite(d)) return DEFAULT_808_KICK_DECAY;
  return Math.max(MIN_808_KICK_DECAY, Math.min(MAX_808_KICK_DECAY, d));
}

function clampGlide(g: number): number {
  if (!isFinite(g)) return DEFAULT_GLIDE;
  return Math.max(0, Math.min(100, Math.round(g)));
}

// Tone-row octave shift, in semitones. Snapped to a whole-octave multiple in
// the range -2..+2 octaves (-24..+24 semitones).
const OCTAVE_MIN_SEMIS = -24;
const OCTAVE_MAX_SEMIS = 24;
function clampOctaveSemis(n: number): number {
  if (!isFinite(n)) return 0;
  return Math.max(-2, Math.min(2, Math.round(n / 12))) * 12;
}
function clampSnareDecay(d: number): number {
  if (!isFinite(d)) return SNARE_DECAY.default;
  return Math.max(SNARE_DECAY.min, Math.min(SNARE_DECAY.max, d));
}
function clampHatDecay(d: number): number {
  if (!isFinite(d)) return HAT_DECAY.default;
  return Math.max(HAT_DECAY.min, Math.min(HAT_DECAY.max, d));
}

function parseHash(hashStr?: string): {
  bpm: number;
  numSteps: number;
  patterns: number[][];
  pitches: Map<number, number>[];
  volumes: Record<string, number>;
  toneTimbre: ToneTimbre;
  scale: ScaleId;
  audioSource: AudioSource;
  toneSource: ToneSource;
  toneSourceLocked: boolean;
  kickDecay: number;
  glide: number;
  toneOctaveOffset: number;
  snareDecay: number;
  hatDecay: number;
} | null {
  const raw = hashStr ?? window.location.hash;
  const hash = raw.startsWith('#') ? raw.slice(1) : raw;
  if (!hash) return null;
  const params = new URLSearchParams(hash);
  const bpmRaw = params.get('bpm') ? Number(params.get('bpm')) : null;
  if (bpmRaw == null || isNaN(bpmRaw)) return null;
  const bpm = clampBpm(bpmRaw);
  if (bpm == null) return null;
  const bars = params.get('bars') ? Math.max(1, Math.min(4, Number(params.get('bars')) || 1)) : 1;
  const numSteps = bars * DEFAULT_STEPS;
  const audioSource = parseSource(params.get('src'));
  // Unified layout — every source reads the same 8 pattern keys.
  const defs = UNIFIED_TRACK_DEFS;
  const validKeys = new Set(defs.map(d => d.key));
  const patterns: number[][] = [];
  const pitches: Map<number, number>[] = [];
  for (const def of defs) {
    const val = params.get(def.key);
    if (!val || val.trim() === '') {
      patterns.push([]);
      pitches.push(new Map());
      continue;
    }
    if (def.pitched) {
      const pitchMap = new Map<number, number>();
      const idxs: number[] = [];
      for (const tok of val.split(',')) {
        const [s, p] = tok.split(':').map(Number);
        if (!isNaN(s) && s >= 0 && s < numSteps) {
          idxs.push(s);
          if (!isNaN(p) && p >= MIN_PITCH && p <= MAX_PITCH) pitchMap.set(s, p);
          else pitchMap.set(s, DEFAULT_PITCH);
        }
      }
      patterns.push(idxs);
      pitches.push(pitchMap);
    } else {
      patterns.push(val.split(',').map(Number).filter(n => !isNaN(n) && n >= 0 && n < numSteps));
      pitches.push(new Map());
    }
  }
  // Back-compat: pre-unification 808 links carried a separate open-hat track
  // keyed `o`. The unified layout has one hat slot, so fold those hits in.
  const oRaw = params.get('o');
  if (oRaw && oRaw.trim() !== '') {
    const hatIdx = defs.findIndex(d => d.key === 'h');
    if (hatIdx >= 0) {
      const merged = new Set(patterns[hatIdx]);
      for (const n of oRaw.split(',').map(Number)) if (!isNaN(n) && n >= 0 && n < numSteps) merged.add(n);
      patterns[hatIdx] = [...merged].sort((a, b) => a - b);
    }
  }
  const volumes: Record<string, number> = { ...DEFAULT_VOLUMES };
  const volStr = params.get('vol');
  if (volStr) {
    for (const tok of volStr.split(',')) {
      const [k, v] = tok.split(':');
      const num = Number(v);
      if (k && validKeys.has(k) && !isNaN(num)) {
        volumes[k] = Math.max(VOL_MIN, Math.min(VOL_MAX, num));
      }
    }
  }
  const ttRaw = params.get('tt');
  const toneTimbre: ToneTimbre = (ttRaw && TONE_TIMBRE_IDS.has(ttRaw as ToneTimbre) && isTimbreAvailable(ttRaw as ToneTimbre))
    ? (ttRaw as ToneTimbre)
    : DEFAULT_TONE_TIMBRE;
  const scRaw = params.get('sc');
  const scale: ScaleId = (scRaw && SCALE_IDS.has(scRaw)) ? (scRaw as ScaleId) : DEFAULT_SCALE;
  const kdRaw = params.get('kd');
  const kickDecay = kdRaw != null ? clampKickDecay(Number(kdRaw)) : DEFAULT_808_KICK_DECAY;
  const glRaw = params.get('gl');
  const glide = glRaw != null ? clampGlide(Number(glRaw)) : DEFAULT_GLIDE;
  const ocRaw = params.get('oc');
  const toneOctaveOffset = ocRaw != null ? clampOctaveSemis(Number(ocRaw)) : 0;
  const sdRaw = params.get('sd');
  const snareDecay = sdRaw != null ? clampSnareDecay(Number(sdRaw)) : SNARE_DECAY.default;
  const hdRaw = params.get('hd');
  const hatDecay = hdRaw != null ? clampHatDecay(Number(hdRaw)) : HAT_DECAY.default;
  // ts= present → user has manually picked a tone source (locked). Absent →
  // auto-follow the audio source (backward-compat for older links).
  const tsExplicit = parseToneSourceParam(params.get('ts'));
  const toneSource: ToneSource = tsExplicit ?? deriveToneSource(audioSource);
  const toneSourceLocked = tsExplicit != null;
  return { bpm, numSteps, patterns, pitches, volumes, toneTimbre, scale, audioSource, toneSource, toneSourceLocked, kickDecay, glide, toneOctaveOffset, snareDecay, hatDecay };
}

function fmtVol(v: number): string {
  return v.toFixed(2);
}

function buildHash(
  bpm: number, numSteps: number, tracks: Track[], volumes: Record<string, number>,
  toneTimbre: ToneTimbre, scale: ScaleId, audioSource: AudioSource = DEFAULT_AUDIO_SOURCE,
  kickDecay: number = DEFAULT_808_KICK_DECAY, glide: number = DEFAULT_GLIDE,
  toneOctaveOffset = 0, snareDecay: number = SNARE_DECAY.default, hatDecay: number = HAT_DECAY.default,
  toneSource: ToneSource = DEFAULT_TONE_SOURCE, toneSourceLocked = false,
): string {
  const bars = Math.round(numSteps / DEFAULT_STEPS);
  const defs = UNIFIED_TRACK_DEFS;
  const parts = [`bpm=${bpm}`, `bars=${bars}`];
  tracks.forEach((track, i) => {
    const def = defs[i];
    if (!def) return;
    if (def.pitched && track.pitches) {
      const tokens: string[] = [];
      for (let s = 0; s < track.steps.length; s++) {
        if (track.steps[s]) tokens.push(`${s}:${track.pitches[s]}`);
      }
      parts.push(`${def.key}=${tokens.join(',')}`);
    } else {
      const active = track.steps.map((on, idx) => on ? idx : -1).filter(x => x >= 0);
      parts.push(`${def.key}=${active.join(',')}`);
    }
  });
  const volTokens = defs.map(d => `${d.key}:${fmtVol(volumes[d.key] ?? DEFAULT_VOLUMES[d.key] ?? 0.75)}`);
  parts.push(`vol=${volTokens.join(',')}`);
  if (toneTimbre !== DEFAULT_TONE_TIMBRE) parts.push(`tt=${toneTimbre}`);
  if (scale !== DEFAULT_SCALE) parts.push(`sc=${scale}`);
  if (audioSource !== DEFAULT_AUDIO_SOURCE) parts.push(`src=${audioSource}`);
  if (audioSource === '808' && Math.abs(kickDecay - DEFAULT_808_KICK_DECAY) > 1e-4) {
    parts.push(`kd=${kickDecay.toFixed(2)}`);
  }
  if (glide !== DEFAULT_GLIDE) parts.push(`gl=${glide}`);
  if (toneOctaveOffset !== 0) parts.push(`oc=${toneOctaveOffset}`);
  if (Math.abs(snareDecay - SNARE_DECAY.default) > 1e-4) parts.push(`sd=${snareDecay.toFixed(3)}`);
  if (Math.abs(hatDecay - HAT_DECAY.default) > 1e-4) parts.push(`hd=${hatDecay.toFixed(3)}`);
  // ts= is only emitted once the user has manually picked a tone source. Until
  // then it auto-follows audioSource and is reconstructed by parseHash().
  if (toneSourceLocked) parts.push(`ts=${toneSource}`);
  return '#' + parts.join('&');
}

// ==== Inspire Me — curated random pattern generator ====
//
const pick = <T>(arr: readonly T[]): T => arr[Math.floor(Math.random() * arr.length)];
const chance = (p: number) => Math.random() < p;

// Generates a fresh-but-recognizable boom-bap-leaning groove. Random choices
// are weighted toward classic hip-hop conventions: kick on 1, snare on 2/4,
// 8th-note hats by default, and a simple bass figure rooted in C minor pent.
// The result reads as "fun and unexpected" without ever feeling chaotic.
function generateInspirePattern(): Preset {

  // Kick variations — every option starts on beat 1 and includes some
  // syncopation around beats 3-and-of-3 (10) which is the boom-bap
  // signature. Order doesn't matter since steps are an unordered set.
  const KICK_OPTIONS: readonly number[][] = [
    [0, 6, 10],
    [0, 8, 10],
    [0, 6, 10, 13],
    [0, 4, 10],
    [0, 3, 8, 10],
    [0, 6, 8, 10],
    [0, 10, 11],     // double-tap
  ];
  const kick = [...pick(KICK_OPTIONS)];

  // Snare always on the 2 and 4 backbeat. ~25% chance of a single ghost note
  // for a touch of bounce — placed on the and-of-3 (step 11) or the e-of-2 (5).
  const snare = [4, 12];
  if (chance(0.25)) snare.push(chance(0.5) ? 11 : 5);

  // Hi-hat: 8th notes (default boom-bap), 16ths (busier), or a swung "shuffle"
  // where each offbeat 8th is pushed late toward the "a" (step 3 of each beat
  // instead of step 2) — the lazy triplet-ish bounce of classic boom-bap
  // (Pete Rock, Dilla). Not just extra 16ths: the offbeats actually move.
  const HAT_STYLES = ['eighths', 'sixteenths', 'shuffle'] as const;
  const hatStyle = pick(HAT_STYLES);
  const hat: number[] = [];
  if (hatStyle === 'eighths') {
    for (let i = 0; i < 16; i += 2) hat.push(i);
  } else if (hatStyle === 'sixteenths') {
    for (let i = 0; i < 16; i++) hat.push(i);
  } else {
    // Downbeats on 0/4/8/12, offbeats pushed to the "a": 3/7/11/15.
    hat.push(0, 3, 4, 7, 8, 11, 12, 15);
  }

  // Clap: 50% chance. Reinforces snare on 2 and 4.
  const clap = chance(0.5) ? [4, 12] : [];

  // Bass: simple riff in a randomly-chosen minor pentatonic root. Root on 1
  // is mandatory (anchors the groove); 2-3 additional notes pulled from the
  // scale add motion. Octave anchored low so it reads as bass.
  // C2/D2/Eb2/E2/F2/G2/A1/Bb1 — roots heard across the rap-sample canon.
  const ROOTS = [36, 38, 39, 40, 41, 43, 33, 34];
  const root = pick(ROOTS);
  const PENT = [0, 3, 5, 7, 10]; // minor pentatonic
  const noteOf = (deg: number) => root + PENT[deg];
  const tone: number[] = [0];
  const tonePitches: Record<number, number> = { 0: noteOf(0) };
  // 2-3 extra hits, biased toward strong-beat eighths
  const slotPool = [4, 6, 8, 10, 12, 14];
  const numExtra = 2 + (chance(0.5) ? 1 : 0);
  for (let i = 0; i < numExtra; i++) {
    if (slotPool.length === 0) break;
    const idx = Math.floor(Math.random() * slotPool.length);
    const slot = slotPool.splice(idx, 1)[0];
    tone.push(slot);
    // Walk: prefer 5th, octave, b3 — skip the b7 occasionally for color
    const degs = [4, 0, 1, 3]; // 7, root, b3, 5
    tonePitches[slot] = noteOf(pick(degs));
  }
  tone.sort((a, b) => a - b);

  // Percussion colour. Boom-bap leans on cross-stick rim ghosts (Premier-style
  // off-beat clicks) and the odd tom roll into the turnaround. Cowbell is rare
  // here — used sparingly so it reads as a wink, not a Latin groove.
  let rim: number[] = [];
  if (chance(0.4)) {
    rim = pick([[7], [3, 11], [7, 15], [3, 7, 11], [11]] as const).slice();
  }
  let tom: number[] = [];
  if (chance(0.18)) {
    // Descending fill landing into the next downbeat.
    tom = pick([[13, 14, 15], [14, 15], [13, 15], [11, 13, 15]] as const).slice();
  }
  const cowbell = chance(0.08) ? pick([[6, 14], [2, 10], [10]] as const).slice() : [];

  return {
    label: pick(['Inspire', 'Vibe', 'Sketch', 'Spark', 'Loop'] as const),
    bpm: 86 + Math.floor(Math.random() * 18), // 86-103, classic boom-bap range
    patterns: [kick, snare, hat, clap, tone, cowbell, tom, rim],
    tonePitches,
    toneTimbre: 'bass',
  };
}

// TR-808 classics — appended to the unified PRESETS library above.
// Instantly-recognizable patterns from the drum machine's golden age —
// early-'80s electro/R&B, mid-'80s rap, on through the 808-bass era. Laid out
// in the unified layout [kick, snare, hat, clap, tone, cowbell, tom, rim] — the
// 808's open + closed hat are folded into the single hat slot, and the tone
// slot is unused here. 16 steps = one bar of sixteenths; beats land on
// 0/4/8/12, the "&" of a beat is +2, "e" is +1, "a" is +3.
PRESETS.push(
  {
    // Marvin Gaye, "Sexual Healing" (1982). Marvin programmed it himself on a
    // Roland TR-808 — the record that made the 808 cowbell a permanent pop
    // fixture. Laid-back syncopated kick, backbeat snare doubled by claps,
    // busy 16th-note hat, and the unmistakable offbeat cowbell.
    label: 'Sexual Healing',
    bpm: 95,
    patterns: [
      [0, 6, 10],                                              // kick
      [4, 12],                                                 // snare
      [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15], // hat
      [4, 12],                                                 // clap
      [],                                                      // tone
      [2, 6, 10, 14],                                          // cowbell
      [],                                                      // tom
      [],                                                      // rim
    ],
  },
  {
    // Afrika Bambaataa & Soulsonic Force, "Planet Rock" (1982). Arthur Baker
    // / John Robie. The 808's coming-out party: a booming four-on-the-floor
    // kick, handclaps on the backbeat, the "ts-tss" disco hat, a cowbell on 1
    // and 3, and a quick descending tom pickup nodding to the breakdown.
    label: 'Planet Rock',
    bpm: 128,
    patterns: [
      [0, 4, 8, 12],                  // kick
      [],                             // snare
      [0, 2, 4, 6, 8, 10, 12, 14],    // hat
      [4, 12],                        // clap
      [],                             // tone
      [0, 8],                         // cowbell
      [14, 15],                       // tom
      [],                             // rim
    ],
  },
  {
    // Run-DMC, "Sucker M.C.'s (Krush-Groove 1)" (1983). Larry Smith / Russell
    // Simmons. The blueprint for stripped-down hip-hop — a hard kick and a
    // cracking snare, nothing else. The bareness IS the beat.
    label: "Sucker M.C.'s",
    bpm: 103,
    patterns: [
      [0, 8, 10],  // kick
      [4, 12],     // snare
      [],          // hat
      [],          // clap
      [],          // tone
      [],          // cowbell
      [],          // tom
      [],          // rim
    ],
  },
  {
    // N.W.A, "Straight Outta Compton" (1988). Dr. Dre / DJ Yella — early Dre.
    // The deep 808 kick stuttering under a slamming snare-plus-clap backbeat,
    // driven by relentless 8th-note hats.
    label: 'Straight Outta Compton',
    bpm: 104,
    patterns: [
      [0, 3, 8, 11],                // kick
      [4, 12],                      // snare
      [0, 2, 4, 6, 8, 10, 12, 14],  // hat
      [4, 12],                      // clap
      [],                           // tone
      [],                           // cowbell
      [],                           // tom
      [],                           // rim
    ],
  },
  {
    // Kanye West, "Love Lockdown" (2008), from "808s & Heartbreak". Kanye /
    // No I.D. — the album that put the 808 back at the center of pop. A
    // heartbeat kick on beats 1 and 3 with the tribal tom break ("In the Air
    // Tonight" filtered through Kanye) carrying the groove; clap on 2 and 4.
    label: 'Love Lockdown',
    bpm: 120,
    patterns: [
      [0, 8],                 // kick
      [],                     // snare
      [],                     // hat
      [4, 12],                // clap
      [],                     // tone
      [],                     // cowbell
      [2, 4, 6, 10, 12, 14],  // tom
      [],                     // rim
    ],
  },
  {
    // Crunk — Lil Jon & the East Side Boyz, "Get Low" era (c. 2003). The
    // sound of mid-'00s Southern club rap: a stuttering 808 kick, a stacked
    // snare-and-clap backbeat, and a 16th-note hat that rolls into the
    // turnaround.
    label: 'Crunk',
    bpm: 98,
    patterns: [
      [0, 3, 8, 11],                    // kick
      [4, 12],                          // snare
      [0, 2, 4, 6, 8, 10, 12, 14, 15],  // hat
      [4, 12],                          // clap
      [],                               // tone
      [],                               // cowbell
      [],                               // tom
      [],                               // rim
    ],
  },
);

// ==== Late-'90s / 2000s hip-hop, UK grime, and electronic/dance classics ====
// Same unified layout [kick, snare, hat, clap, tone, cowbell, tom, rim].
// Patterns transcribed from canonical drum tabs / by ear from the records;
// melodic riffs are approximated to fit the 16-step grid and our tone voices.
PRESETS.push(
  {
    // Missy Elliott, "Get Ur Freak On" (2001). Timbaland — the bhangra-flip
    // smash: a deep sparse kick, a hard backbeat clap, no hats, and that
    // relentless one-note tumbi twang running across the bar.
    label: 'Get Ur Freak On',
    bpm: 110,
    patterns: [
      [0, 6, 8, 14],                             // kick
      [4, 12],                                   // snare
      [],                                        // hat
      [4, 12],                                   // clap
      [0, 2, 4, 6, 8, 10, 12, 14],               // tone — tumbi twang
      [],                                        // cowbell
      [],                                        // tom
      [2, 6, 10, 14],                            // rim
    ],
    tonePitches: { 0: 67, 2: 67, 4: 67, 6: 67, 8: 67, 10: 67, 12: 67, 14: 67 },
    toneTimbre: 'pluck',
  },
  {
    // Clipse, "Grindin'" (2002). The Neptunes — the lunchroom-table knock.
    // A handful of hard kicks, a cracking snare-plus-clap backbeat, no hats,
    // and a single low synth bleep. Stripped to the bone on purpose.
    label: "Grindin'",
    bpm: 92,
    patterns: [
      [0, 3, 8, 10],   // kick
      [4, 12],         // snare
      [],              // hat
      [4, 12],         // clap
      [0, 8],          // tone — low bleep
      [],              // cowbell
      [],              // tom
      [6, 14],         // rim
    ],
    tonePitches: { 0: 38, 8: 38 },
    toneTimbre: 'synth',
  },
  {
    // Snoop Dogg, "Drop It Like It's Hot" (2004). The Neptunes — minimalism as
    // a flex: a sparse sub kick, backbeat snare and clap, the tongue-click
    // "hi-hat" (rim), and a tiny synth blip. Almost nothing, and it knocked.
    label: "Drop It Like It's Hot",
    bpm: 86,
    patterns: [
      [0, 10],                          // kick
      [4, 12],                          // snare
      [],                               // hat
      [4, 12],                          // clap
      [12],                             // tone — blip
      [],                               // cowbell
      [],                               // tom
      [0, 2, 4, 6, 8, 10, 12, 14],      // rim — tongue clicks
    ],
    tonePitches: { 12: 60 },
    toneTimbre: 'synth',
  },
  {
    // Ginuwine, "Pony" (1996). Timbaland — the talking-frog bass synth. A
    // syncopated kick, hard backbeat, offbeat hats, and the wobbling two-note
    // bass riff that everyone can hum.
    label: 'Pony',
    bpm: 105,
    patterns: [
      [0, 6, 10],          // kick
      [4, 12],             // snare
      [2, 6, 10, 14],      // hat
      [],                  // clap
      [0, 2, 8, 10],       // tone — wobble bass
      [],                  // cowbell
      [],                  // tom
      [3, 11],             // rim
    ],
    tonePitches: { 0: 36, 2: 39, 8: 36, 10: 39 },
    toneTimbre: 'bass',
  },
  {
    // Musical Mob, "Pulse X" (2002). The foundational grime instrumental — a
    // pair of detuned square-bass stabs and a skeletal 140 kick/snare. The
    // blueprint for "eski" minimalism.
    label: 'Pulse X',
    bpm: 140,
    patterns: [
      [0, 8, 11],          // kick
      [4, 12],             // snare
      [],                  // hat
      [],                  // clap
      [0, 3, 8, 11],       // tone — square stabs
      [],                  // cowbell
      [],                  // tom
      [2, 6, 10, 14],      // rim
    ],
    tonePitches: { 0: 36, 3: 36, 8: 43, 11: 36 },
    toneTimbre: 'synth',
  },
  {
    // Dizzee Rascal, "I Luv U" (2003). Boy in da Corner-era grime — a skittering
    // syncopated kick/snare, jittery 16th hats, and a cold minor synth riff.
    label: 'I Luv U',
    bpm: 138,
    patterns: [
      [0, 7, 10],              // kick
      [4, 11, 12],             // snare
      [2, 3, 6, 10, 14, 15],   // hat
      [],                      // clap
      [0, 6, 8, 14],           // tone — cold riff
      [],                      // cowbell
      [],                      // tom
      [3, 11],                 // rim
    ],
    tonePitches: { 0: 40, 6: 43, 8: 45, 14: 43 },
    toneTimbre: 'synth',
  },
  {
    // Daft Punk, "Around the World" (1997). Thomas Bangalter's relentless
    // 16th-note bassline under a clean four-on-the-floor and offbeat hats.
    label: 'Around the World',
    bpm: 121,
    patterns: [
      [0, 4, 8, 12],                                       // kick
      [],                                                  // snare
      [2, 6, 10, 14],                                      // hat
      [4, 12],                                             // clap
      [0,1,2,3,4,5,6,7,8,9,10,11,12,13,14,15],             // tone — bassline
      [],                                                  // cowbell
      [],                                                  // tom
      [],                                                  // rim
    ],
    tonePitches: {
      0: 38, 1: 38, 2: 38, 3: 41, 4: 38, 5: 38, 6: 36, 7: 38,
      8: 38, 9: 38, 10: 38, 11: 41, 12: 43, 13: 41, 14: 38, 15: 36,
    },
    toneTimbre: 'bass',
  },
  {
    // Daft Punk, "One More Time" (2000). Four-on-the-floor, offbeat hats,
    // backbeat clap, and the filtered Romanthony horn-stab chord vamp.
    label: 'One More Time',
    bpm: 123,
    patterns: [
      [0, 4, 8, 12],            // kick
      [],                       // snare
      [2, 6, 10, 14],           // hat
      [4, 12],                  // clap
      [0, 3, 6, 8, 11, 14],     // tone — horn stabs
      [],                       // cowbell
      [],                       // tom
      [],                       // rim
    ],
    tonePitches: { 0: 54, 3: 54, 6: 57, 8: 54, 11: 57, 14: 54 },
    toneTimbre: 'organ',
  },
  {
    // Daft Punk, "Da Funk" (1995). The growling square-lead riff over a
    // syncopated kick, backbeat snare, offbeat hats, and a steady cowbell.
    label: 'Da Funk',
    bpm: 112,
    patterns: [
      [0, 4, 7, 8, 12],                          // kick
      [4, 12],                                   // snare
      [2, 6, 10, 14],                            // hat
      [],                                        // clap
      [0, 2, 4, 6, 8, 10, 12, 14],               // tone — growl riff
      [2, 6, 10, 14],                            // cowbell
      [],                                        // tom
      [],                                        // rim
    ],
    tonePitches: { 0: 45, 2: 45, 4: 43, 6: 45, 8: 41, 10: 41, 12: 40, 14: 38 },
    toneTimbre: 'synth',
  },
  {
    // The Chemical Brothers, "Block Rockin' Beats" (1997). Big-beat — a heavy
    // breakbeat-style kick/snare, offbeat hats, a tom fill into the turnaround,
    // and the snaking acid bassline (a "Coup"/23 Skidoo flip).
    label: 'Block Rockin Beats',
    bpm: 129,
    patterns: [
      [0, 8, 11],                  // kick
      [4, 12],                     // snare
      [2, 6, 10, 14],              // hat
      [],                          // clap
      [0, 2, 6, 8, 10, 14],        // tone — acid bass
      [],                          // cowbell
      [13, 14, 15],                // tom
      [3, 11],                     // rim
    ],
    tonePitches: { 0: 38, 2: 38, 6: 41, 8: 38, 10: 36, 14: 38 },
    toneTimbre: 'bass',
  },
  {
    // Darude, "Sandstorm" (1999). Trance — pumping four-on-the-floor, offbeat
    // hats, backbeat clap, and the unmistakable staccato 16th-note lead riff.
    label: 'Sandstorm',
    bpm: 136,
    patterns: [
      [0, 4, 8, 12],                                       // kick
      [],                                                  // snare
      [2, 6, 10, 14],                                      // hat
      [4, 12],                                             // clap
      [0,1,2,3,4,5,6,7,8,9,10,11,12,13,14,15],             // tone — lead riff
      [],                                                  // cowbell
      [],                                                  // tom
      [],                                                  // rim
    ],
    tonePitches: {
      0: 71, 1: 71, 2: 71, 3: 71, 4: 71, 5: 71, 6: 71, 7: 71,
      8: 69, 9: 69, 10: 69, 11: 69, 12: 67, 13: 67, 14: 66, 15: 66,
    },
    toneTimbre: 'pluck',
  },
  {
    // Alice DeeJay, "Better Off Alone" (1998). Eurotrance — four-on-the-floor,
    // offbeat hats, backbeat clap, and the bouncy minor-key synth hook.
    label: 'Better Off Alone',
    bpm: 137,
    patterns: [
      [0, 4, 8, 12],                  // kick
      [],                             // snare
      [2, 6, 10, 14],                 // hat
      [4, 12],                        // clap
      [0, 2, 4, 6, 8, 10, 12, 14],    // tone — synth hook
      [],                             // cowbell
      [],                             // tom
      [],                             // rim
    ],
    tonePitches: { 0: 71, 2: 74, 4: 71, 6: 69, 8: 67, 10: 69, 12: 71, 14: 74 },
    toneTimbre: 'synth',
  },
);

// Random TR-808 groove, in the unified layout [kick, snare, hat, clap, tone,
// cowbell, tom, rim] (no tonal hits).
function generate808Pattern(): Preset {

  const KICKS: readonly number[][] = [[0, 8], [0, 6, 10], [0, 7, 10], [0, 8, 14], [0, 3, 8, 11], [0, 10]];
  const kick = [...pick(KICKS)];
  const snare = [4, 12];
  if (chance(0.3)) snare.push(chance(0.5) ? 14 : 7);
  const clap = chance(0.45) ? [4, 12] : [];

  // Hat: 8ths or 16ths, with an occasional off-beat "open" accent folded in.
  const sixteenthHats = chance(0.4);
  const hatSet = new Set<number>();
  for (let i = 0; i < 16; i += sixteenthHats ? 1 : 2) hatSet.add(i);
  if (chance(0.65)) hatSet.add(pick([2, 6, 10, 14]));
  const hat = [...hatSet].sort((a, b) => a - b);

  // Cowbell: the 808's signature voice — but not always the same offbeat
  // pattern. "Sexual Healing" / "Planet Rock" gave us the steady offbeat, the
  // on-beat quarter, and the sparse two-hit accent. Probability stays moderate
  // so it shows up often enough to matter but doesn't dominate.
  const COWBELLS: readonly number[][] = [
    [2, 6, 10, 14],   // steady offbeat (Sexual Healing)
    [0, 4, 8, 12],    // four-on-the-floor quarter
    [0, 8],           // 1 and 3 (Planet Rock)
    [6, 14],          // backbeat-ish accent
    [2, 10],          // sparse syncopated
  ];
  const cowbell = chance(0.3) ? pick(COWBELLS).slice() : [];

  // Toms: 2-hit pickups/fills rather than a lone thud — the descending tom
  // turnaround is an 808 staple.
  const tom = chance(0.22)
    ? pick([[14, 15], [13, 15], [6, 7], [10, 11], [12, 14, 15]] as const).slice()
    : [];

  // Rim: cross-stick ghost figures, often a repeating offbeat click.
  const rim = chance(0.28)
    ? pick([[3, 11], [7, 15], [3, 7, 11, 15], [11], [3, 7]] as const).slice()
    : [];

  return {
    label: pick(['808', 'Boom', 'Sub', 'Knock', 'Thump'] as const),
    bpm: pick([120, 128, 130, 132, 140, 150]),
    patterns: [kick, snare, hat, clap, [], cowbell, tom, rim],
  };
}

// Contemporary trap groove. The DNA: a heavy sub kick on the 1 and the "and of
// 3" (steps 0 and 10), a sparse snare/clap that usually lands only on beat 3
// (step 8) — half-time feel — and hi-hats that run straight then erupt into
// triplet-style rolls and clusters. Tempo notated ~130-160 (heard as half that).
// Reference points: Migos / Lex Luger / Metro Boomin / 808 Mafia.
function generateTrapPattern(): Preset {

  // Sub kick — 1 and the "and of 3" are mandatory; add the odd extra syncopation.
  const kick = [0, 10];
  if (chance(0.4)) kick.push(pick([6, 11, 3]));
  if (chance(0.25)) kick.push(pick([8, 14]));

  // Snare: half-time, beat 3 (step 8). Sometimes the classic 2-and-4 instead,
  // occasionally a trap "snare run" of three quick hits into the turnaround.
  let snare: number[];
  const snareRoll = chance(0.18);
  if (chance(0.25)) {
    snare = [4, 12];
  } else {
    snare = [8];
  }
  if (snareRoll) snare = [...new Set([...snare, 13, 14, 15])].sort((a, b) => a - b);
  // Clap usually stacked on the main snare hit.
  const clap = chance(0.55) ? (snare.includes(8) ? [8] : [4, 12]) : [];

  // Hi-hats: straight base (8ths or 16ths) plus 1-2 roll clusters approximating
  // triplet/32nd bursts on a 16-step grid.
  const base16 = chance(0.5);
  const hatSet = new Set<number>();
  for (let i = 0; i < 16; i += base16 ? 1 : 2) hatSet.add(i);
  const ROLLS: readonly number[][] = [
    [10, 11], [13, 14, 15], [6, 7], [2, 3], [9, 10, 11], [14, 15], [3], [7],
  ];
  const numRolls = 1 + (chance(0.5) ? 1 : 0);
  for (let i = 0; i < numRolls; i++) pick(ROLLS).forEach(s => hatSet.add(s));
  const hat = [...hatSet].sort((a, b) => a - b);

  // Cowbell: the Migos triplet-cowbell wink — usually a syncopated triplet-ish
  // sprinkle, sometimes the steady offbeat. Often absent.
  const cowbell = chance(0.22)
    ? pick([[2, 5, 10, 13], [6, 14], [2, 6, 10, 14], [3, 10], [0, 8]] as const).slice()
    : [];

  // Toms / 808-tom fills — short descending turnarounds.
  const tom = chance(0.18)
    ? pick([[14, 15], [13, 15], [11, 13, 15], [6, 7]] as const).slice()
    : [];

  // Rim — sparse cross-stick ghosts riding the offbeats.
  const rim = chance(0.3)
    ? pick([[3, 11], [7, 15], [11], [3, 7, 11, 15], [5, 13]] as const).slice()
    : [];

  return {
    label: pick(['Trap', 'Drip', '808s', 'Plug', 'Murk', 'Bando'] as const),
    bpm: 130 + Math.floor(Math.random() * 31), // 130-160
    patterns: [kick, snare, hat, clap, [], cowbell, tom, rim],
  };
}

// 90s / early-2000s hip-hop — the Timbaland / Neptunes production era. The DNA:
// a hard, cracking snare on the 2 and 4, a syncopated stuttering kick that
// leaves space (not the busy boom-bap shuffle), and sparse, choppy, beatbox-
// style hi-hats rather than a steady 8th-note carpet. The tone slot carries a
// dry, minimal 2-3 note synth/bass motif — the Neptunes' bare keyboard hook.
// Tempo ~92-102. Reference: "Get Ur Freak On", "Pony", "Grindin'", "Drop It
// Like It's Hot", early Kanye.
function generateNeptunesPattern(): Preset {
  const KICKS: readonly number[][] = [
    [0, 7, 10], [0, 6, 11], [0, 3, 10], [0, 7, 14], [0, 10, 11], [0, 6, 7, 13],
  ];
  const kick = [...pick(KICKS)];
  // Snare: rock-solid backbeat, occasional ghost just before the 4.
  const snare = [4, 12];
  if (chance(0.2)) snare.push(11);
  // Clap stacked on the snare most of the time — that big layered crack.
  const clap = chance(0.55) ? [4, 12] : [];
  // Hats: choppy and placed, not a continuous run.
  const HAT_FIGURES: readonly number[][] = [
    [2, 6, 10, 14],
    [0, 2, 6, 8, 10, 14],
    [2, 3, 6, 10, 14, 15],
    [0, 4, 6, 7, 8, 12, 14],
    [2, 6, 10, 12, 13, 14],
    [6, 7, 14, 15],
  ];
  const hat = pick(HAT_FIGURES).slice();
  // Tone: dry, minimal motif — mostly the root with a step or two of motion.
  const ROOTS = [37, 38, 40, 41, 43, 45];
  const root = pick(ROOTS);
  const PENT = [0, 3, 5, 7, 10];
  const toneSlots = pick([[0, 8], [0, 4, 8], [2, 10], [0, 6, 8], [0, 8, 14], [4, 12]] as const).slice();
  const tone: number[] = [];
  const tonePitches: Record<number, number> = {};
  toneSlots.forEach((s, i) => { tone.push(s); tonePitches[s] = root + (i === 0 ? 0 : PENT[pick([0, 0, 1, 2])]); });
  // Percussion colour: snappy rim clicks (mouth-percussion feel), rare cowbell/tom.
  const rim = chance(0.45)
    ? pick([[3, 11], [7, 15], [3, 7, 11, 15], [2, 6, 10, 14], [3, 7, 11], [11]] as const).slice()
    : [];
  const cowbell = chance(0.15) ? pick([[6, 14], [2, 10], [10]] as const).slice() : [];
  const tom = chance(0.12) ? pick([[14, 15], [13, 15], [11, 13, 15]] as const).slice() : [];
  return {
    label: pick(['Neptunes', 'Timbo', 'Virginia', 'Freak', 'Pharrell'] as const),
    bpm: 92 + Math.floor(Math.random() * 11), // 92-102
    patterns: [kick, snare, hat, clap, tone, cowbell, tom, rim],
    tonePitches,
    toneTimbre: pick(['synth', 'bass'] as const),
  };
}

// UK Grime — the "eski-beat" sound, c. 2003-05 (Wiley, Dizzee Rascal, early
// Ruff Sqwad). A different lineage from US hip-hop: a hard 140 BPM, a
// syncopated kick-and-snare interplay that lurches rather than swings, very
// sparse hi-hats, and a cold square-wave synth-stab riff in a minor key — the
// melody IS the hook. Reference: "Eskimo", "Pulse X", "I Luv U", "Functions
// on the Low".
function generateGrimePattern(): Preset {
  const FIGURES: readonly { kick: number[]; snare: number[] }[] = [
    { kick: [0, 3, 6, 11],     snare: [8, 14] },
    { kick: [0, 6, 10, 11],    snare: [4, 12] },
    { kick: [0, 5, 8, 13],     snare: [3, 11] },
    { kick: [0, 3, 8, 10],     snare: [6, 12, 14] },
    { kick: [0, 7, 10],        snare: [4, 12, 13] },
    { kick: [0, 4, 6, 10, 14], snare: [8] },
  ];
  const fig = pick(FIGURES);
  const kick = fig.kick.slice();
  const snare = fig.snare.slice();
  // Hats: sparse offbeat ticks, sometimes a short 16th flurry — never a carpet.
  const hat = pick([
    [2, 6, 10, 14], [2, 10, 14, 15], [6, 14], [2, 6, 10, 12, 13, 14, 15], [], [10, 11, 14, 15],
  ] as const).slice();
  const clap = chance(0.3) ? (snare.includes(8) ? [8] : [4, 12]) : [];
  // Eski stab: a cold square riff. Minor key, low-mid register, syncopated.
  const ROOTS = [33, 35, 36, 38, 40, 41];
  const root = pick(ROOTS);
  const MINOR = [0, 2, 3, 5, 7, 8, 10];
  const RIFFS: readonly { slots: number[]; degs: number[] }[] = [
    { slots: [0, 3, 6, 8, 14],  degs: [0, 0, 3, 2, 0] },
    { slots: [0, 6, 10, 11],    degs: [0, 4, 3, 2] },
    { slots: [0, 2, 8, 10, 12], degs: [0, 5, 0, 3, 5] },
    { slots: [0, 4, 6, 12, 14], degs: [0, 2, 3, 0, 6] },
    { slots: [0, 8, 11, 14],    degs: [0, 3, 5, 4] },
  ];
  const riff = pick(RIFFS);
  const tone: number[] = [];
  const tonePitches: Record<number, number> = {};
  riff.slots.forEach((s, i) => { tone.push(s); tonePitches[s] = root + MINOR[riff.degs[i] % MINOR.length]; });
  const rim = chance(0.4) ? pick([[3, 11], [7, 15], [3, 7, 11, 15], [11], [5, 13]] as const).slice() : [];
  const cowbell = chance(0.1) ? pick([[2, 10], [6, 14]] as const).slice() : [];
  const tom = chance(0.12) ? pick([[14, 15], [13, 14, 15], [10, 11]] as const).slice() : [];
  return {
    label: pick(['Grime', 'Eski', 'Bow E3', 'Sidewinder', '140'] as const),
    bpm: 140,
    patterns: [kick, snare, hat, clap, tone, cowbell, tom, rim],
    tonePitches,
    toneTimbre: pick(['synth', 'bass', 'organ'] as const),
  };
}

// Dance / electronic — picks one of two sub-styles at random:
//   • "house": classic four-on-the-floor — kick on every beat, crisp offbeat
//     open-hats, clap on 2 and 4, and a stabby piano/organ chord vamp. Tempo
//     120-126. Reference: Frankie Knuckles, Daft Punk filter-house, Stardust.
//   • "moody": broken-beat IDM / downtempo — a syncopated off-grid kick,
//     glitchy clustered hats, sparse snare/rim, and a wistful minor-key
//     melodic figure with room to breathe. Tempo 100-115. Reference: early
//     Aphex Twin, Boards of Canada, Burial-adjacent textures.
function generateDancePattern(): Preset {
  if (!chance(0.4)) {
    // ---- classic house ----
    const kick = [0, 4, 8, 12];
    const hat = chance(0.35) ? [2, 6, 10, 14] : [2, 3, 6, 7, 10, 11, 14, 15];
    const clap = [4, 12];
    const snare = chance(0.25) ? [4, 12] : [];
    const ROOTS = [48, 50, 52, 53, 55, 57];
    const root = pick(ROOTS);
    const TRIAD = [0, 3, 7, 10, 12];
    const stabSlots = pick([[2, 6, 10, 14], [0, 6, 8, 14], [2, 5, 10, 13], [6, 14], [2, 6, 10, 12, 14]] as const).slice();
    const tone: number[] = [];
    const tonePitches: Record<number, number> = {};
    stabSlots.forEach((s, i) => { tone.push(s); tonePitches[s] = root + TRIAD[(i + (chance(0.5) ? 1 : 0)) % TRIAD.length]; });
    const cowbell = chance(0.12) ? pick([[2, 6, 10, 14], [0, 8]] as const).slice() : [];
    const rim = chance(0.2) ? pick([[3, 11], [7, 15], [3, 7, 11, 15]] as const).slice() : [];
    const tom = chance(0.1) ? pick([[14, 15], [13, 15]] as const).slice() : [];
    return {
      label: pick(['House', 'Chicago', 'Filter', '4×4', 'Disco'] as const),
      bpm: 120 + Math.floor(Math.random() * 7), // 120-126
      patterns: [kick, snare, hat, clap, tone, cowbell, tom, rim],
      tonePitches,
      toneTimbre: pick(['piano', 'organ'] as const),
    };
  }
  // ---- moody / broken-beat IDM ----
  const KICKS: readonly number[][] = [
    [0, 6, 10], [0, 3, 8, 14], [0, 7, 10, 11], [0, 6, 8, 13], [0, 10], [0, 4, 6, 11],
  ];
  const kick = [...pick(KICKS)];
  const snare = chance(0.5) ? [8] : [4, 12];
  const hat = pick([
    [2, 3, 8, 10, 11, 14],
    [4, 6, 7, 12, 14, 15],
    [0, 2, 3, 6, 10, 11],
    [2, 6, 7, 10, 14, 15],
    [3, 7, 10, 11, 14],
  ] as const).slice();
  const clap = chance(0.3) ? (snare.includes(8) ? [8] : [4, 12]) : [];
  const ROOTS = [50, 52, 53, 55, 57, 59];
  const root = pick(ROOTS);
  const SCALE = [0, 2, 3, 5, 7, 10, 12];
  const FIGS: readonly { slots: number[]; degs: number[] }[] = [
    { slots: [0, 4, 10],     degs: [0, 4, 2] },
    { slots: [2, 6, 8, 14],  degs: [4, 5, 4, 2] },
    { slots: [0, 6, 11],     degs: [2, 4, 0] },
    { slots: [0, 3, 8, 12],  degs: [0, 2, 4, 5] },
    { slots: [4, 8, 10, 14], degs: [4, 6, 5, 4] },
  ];
  const fig = pick(FIGS);
  const tone: number[] = [];
  const tonePitches: Record<number, number> = {};
  fig.slots.forEach((s, i) => { tone.push(s); tonePitches[s] = root + SCALE[fig.degs[i] % SCALE.length]; });
  const rim = chance(0.4) ? pick([[3, 11], [7, 15], [2, 6, 10, 14], [5, 13], [11]] as const).slice() : [];
  const cowbell = chance(0.08) ? pick([[6, 14], [10]] as const).slice() : [];
  const tom = chance(0.15) ? pick([[13, 14, 15], [11, 13, 15], [14, 15]] as const).slice() : [];
  return {
    label: pick(['Moody', 'Drift', 'Glow', 'Haze', 'Hexagon'] as const),
    bpm: 100 + Math.floor(Math.random() * 16), // 100-115
    patterns: [kick, snare, hat, clap, tone, cowbell, tom, rim],
    tonePitches,
    toneTimbre: pick(['bell', 'synth', 'pluck'] as const),
  };
}

// The Inspire pool — source-agnostic. Every preset (the boom-bap-leaning
// named beats, the TR-808 classics, the late-'90s/2000s + grime + dance
// classics, and all six generators) maps onto the unified layout, so a beat
// that's loaded keeps its groove no matter which engine plays it. Each press
// serves an algorithmically-generated groove ~50% of the time — spread across
// boom-bap, TR-808, trap, Neptunes/Timbaland, grime, and dance/electronic —
// otherwise a uniformly-random pick from the unified PRESETS library.
// Returns the chosen pattern plus its analytics origin: either a generator
// type (boom-bap / 808 / trap / neptunes / grime / dance) or, for a curated
// pick, the preset's name.
function pickInspirePattern(): { preset: Preset; generatorType?: string; presetName?: string } {
  if (Math.random() < 0.5) {
    const r = Math.random();
    if (r < 0.18) return { preset: generateInspirePattern(), generatorType: 'boom-bap' };
    if (r < 0.34) return { preset: generate808Pattern(), generatorType: '808' };
    if (r < 0.50) return { preset: generateTrapPattern(), generatorType: 'trap' };
    if (r < 0.66) return { preset: generateNeptunesPattern(), generatorType: 'neptunes' };
    if (r < 0.80) return { preset: generateGrimePattern(), generatorType: 'grime' };
    return { preset: generateDancePattern(), generatorType: 'dance' };
  }
  const preset = PRESETS[Math.floor(Math.random() * PRESETS.length)];
  return { preset, presetName: preset.label };
}

// ==== Saved Beats (localStorage) ====
//
// On Share, we persist the current URL + a short id into localStorage so the
// user can quickly recall recent beats from a strip at the bottom of the UI.
// Cap is 20 entries (oldest dropped).
const SAVED_KEY = 'beatswarm-saved-beats';
const SAVED_MAX = 20;

// One-time onboarding flag for the drag-to-pitch hint. Set after the user
// either dwells on the splash long enough to see the tip toast or actually
// drags a tone cell — whichever happens first.
const TONE_HINT_KEY = 'beatswarm-tone-hint-seen';
function toneHintSeen(): boolean {
  try { return localStorage.getItem(TONE_HINT_KEY) === '1'; } catch { return true; }
}
function markToneHintSeen(): void {
  try { localStorage.setItem(TONE_HINT_KEY, '1'); } catch { /* noop */ }
}

// Whether to skip the intro splash. Returning visitors (flag set on first
// dismissal) and anyone arriving via a share link — a `#…` beat hash or a
// `room=` param — go straight to the app; they came to hear or jam on
// something specific, not to read the pitch.
const SPLASH_SEEN_KEY = 'beatswarm-splash-seen';
function shouldSkipSplash(): boolean {
  try { if (localStorage.getItem(SPLASH_SEEN_KEY) === '1') return true; } catch { /* noop */ }
  if (window.location.hash.replace(/^#/, '').length > 0) return true;
  if (parseRoom()) return true;
  return false;
}
function markSplashSeen(): void {
  try { localStorage.setItem(SPLASH_SEEN_KEY, '1'); } catch { /* noop */ }
}

// Collab display name — persisted so a returning user keeps their name across
// sessions and rooms. No account system; this is purely client-side.
const DISPLAY_NAME_KEY = 'lc_display_name';
const DISPLAY_NAME_MAX = 20;
// Live emoji reactions — the fixed palette shared by the picker and the
// server-side allowlist. Keep in sync with EMOJI_SET in party/index.ts.
const EMOJI_REACTIONS = ['🔥', '❤️', '🍻', '😂'];
const EMOJI_SEND_COOLDOWN_MS = 500;   // ignore own clicks faster than this
const EMOJI_RECV_COOLDOWN_MS = 200;   // drop incoming bursts faster than this per peer
function loadDisplayName(): string | null {
  try {
    const v = localStorage.getItem(DISPLAY_NAME_KEY);
    const trimmed = v?.trim().slice(0, DISPLAY_NAME_MAX);
    return trimmed ? trimmed : null;
  } catch { return null; }
}
function saveDisplayName(name: string): void {
  try { localStorage.setItem(DISPLAY_NAME_KEY, name.trim().slice(0, DISPLAY_NAME_MAX)); } catch { /* noop */ }
}

type SavedBeat = { url: string; hash: string; ts: number; label?: string };

function loadSavedBeats(): SavedBeat[] {
  try {
    const raw = localStorage.getItem(SAVED_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((b): b is SavedBeat =>
      b && typeof b.url === 'string' && typeof b.hash === 'string' && typeof b.ts === 'number'
      // Defend against a tampered store: saved URLs are always built from
      // location.origin, so reject anything that isn't same-origin (blocks
      // javascript: and other hostile schemes from reaching an <a href>).
      && b.url.startsWith(location.origin)
    );
  } catch {
    return [];
  }
}

function persistSavedBeats(beats: SavedBeat[]): void {
  try {
    localStorage.setItem(SAVED_KEY, JSON.stringify(beats));
  } catch {
    /* quota or disabled storage — silently noop */
  }
}

function addSavedBeat(url: string, hash: string, label?: string): SavedBeat[] {
  const existing = loadSavedBeats();
  // Dedupe by hash — if the same beat is shared twice, refresh its timestamp
  // and move it to the front instead of stacking duplicates. Preserve any
  // user-renamed label across the dedupe so a rename isn't blown away by a
  // subsequent share of the same hash.
  const prior = existing.find(b => b.hash === hash);
  const filtered = existing.filter(b => b.hash !== hash);
  const finalLabel = prior?.label ?? label;
  const entry: SavedBeat = { url, hash, ts: Date.now() };
  if (finalLabel) entry.label = finalLabel;
  const next: SavedBeat[] = [entry, ...filtered].slice(0, SAVED_MAX);
  persistSavedBeats(next);
  return next;
}

// Auto-naming: pick a label for a freshly-saved beat. If the current state
// matches a preset's pattern + pitches + bpm + voice + bars exactly, reuse the
// preset's label (with a #N counter for repeats). Otherwise fall back to a
// generic "Beat N" label that increments per save. Volumes, scale, source,
// kick decay and glide are intentionally ignored so a user who tweaks the mix
// on a preset still sees the preset name in their saved list.
function matchedPresetLabel(
  tracks: Track[],
  numSteps: number,
  bpm: number,
  toneTimbre: ToneTimbre,
): string | null {
  const bars = numSteps / DEFAULT_STEPS;
  outer: for (const preset of PRESETS) {
    if ((preset.bars ?? 1) !== bars) continue;
    if (preset.bpm !== bpm) continue;
    if ((preset.toneTimbre ?? DEFAULT_TONE_TIMBRE) !== toneTimbre) continue;
    for (let ti = 0; ti < UNIFIED_TRACK_DEFS.length; ti++) {
      const track = tracks[ti];
      if (!track) continue outer;
      const presetPat = new Set(preset.patterns[ti] ?? []);
      for (let s = 0; s < numSteps; s++) {
        if (!!track.steps[s] !== presetPat.has(s)) continue outer;
      }
      if (UNIFIED_TRACK_DEFS[ti].pitched && track.pitches) {
        for (let s = 0; s < numSteps; s++) {
          if (!track.steps[s]) continue;
          const expected = preset.tonePitches?.[s] ?? DEFAULT_PITCH;
          if (track.pitches[s] !== expected) continue outer;
        }
      }
    }
    return preset.label;
  }
  return null;
}

function uniqueSavedLabel(base: string, existing: SavedBeat[]): string {
  // Returns base, base #2, base #3, … skipping any taken by existing labels.
  const escaped = base.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const re = new RegExp(`^${escaped}( #(\\d+))?$`);
  let max = 0;
  let any = false;
  for (const b of existing) {
    if (!b.label) continue;
    const m = b.label.match(re);
    if (m) {
      any = true;
      max = Math.max(max, m[2] ? Number(m[2]) : 1);
    }
  }
  if (!any) return base;
  return `${base} #${max + 1}`;
}

function removeSavedBeat(hash: string): SavedBeat[] {
  const next = loadSavedBeats().filter(b => b.hash !== hash);
  persistSavedBeats(next);
  return next;
}

function renameSavedBeat(hash: string, label: string): void {
  const beats = loadSavedBeats().map(b => b.hash === hash ? { ...b, label: label.trim() || undefined } : b);
  persistSavedBeats(beats);
}

async function downloadBeatAsWav(b: SavedBeat): Promise<void> {
  const parsed = parseHash(b.hash);
  if (!parsed) throw new Error('Could not parse saved beat hash');
  const { bpm, numSteps, patterns, pitches, volumes, toneTimbre, audioSource, kickDecay, toneOctaveOffset, snareDecay, hatDecay } = parsed;
  const tonePitches = new Array<number>(numSteps).fill(DEFAULT_PITCH);
  if (pitches[TONE_TRACK_IDX]) {
    for (const [s, p] of pitches[TONE_TRACK_IDX]) tonePitches[s] = p;
  }
  // Glide is pinned to DEFAULT_GLIDE in live playback (any gl= in an older
  // hash is ignored there), so the bounce must ignore it too or the WAV
  // won't match what the user hears.
  const buffer = await bounceToWav({ bpm, numSteps, patterns, tonePitches, volumes, toneTimbre, audioSource, kickDecay, glide: DEFAULT_GLIDE, toneOctaveOffset, snareDecay, hatDecay });
  const blob = audioBufferToWavBlob(buffer);
  const safeName = (b.label?.trim() || `beatswarm-${new Date(b.ts).toISOString().slice(0, 10)}`)
    .replace(/[^a-z0-9-_ ]/gi, '_').replace(/\s+/g, '-').slice(0, 60);
  const filename = `${safeName || 'beat'}.wav`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function fmtSavedTs(ts: number): string {
  const d = new Date(ts);
  const m = d.getMonth() + 1;
  const day = d.getDate();
  let h = d.getHours();
  const min = d.getMinutes().toString().padStart(2, '0');
  const ampm = h >= 12 ? 'p' : 'a';
  h = h % 12 || 12;
  return `${m}/${day} ${h}:${min}${ampm}`;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, opts?: { className?: string; text?: string; attrs?: Record<string, string> }): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (opts?.className) e.className = opts.className;
  if (opts?.text != null) e.textContent = opts.text;
  if (opts?.attrs) for (const [k, v] of Object.entries(opts.attrs)) e.setAttribute(k, v);
  return e;
}

// Branded confirmation modal for destructive actions — centered card over a
// dark backdrop (matches the name modal). Cancel dismisses; the destructive
// Confirm button runs the callback. Replaces the old two-stage "tap again"
// toast pattern. Only one confirm modal can be open at a time.
let confirmModalOpen = false;
function openConfirmModal(opts: { title: string; message?: string; confirmLabel: string; onConfirm: () => void }): void {
  if (confirmModalOpen) return;
  confirmModalOpen = true;

  const overlay = el('div', { className: 'modal-overlay', attrs: { role: 'dialog', 'aria-modal': 'true', 'aria-label': opts.title } });
  const card = el('div', { className: 'modal-card confirm-modal' });
  card.appendChild(el('h3', { className: 'modal-title', text: opts.title }));
  if (opts.message) card.appendChild(el('p', { className: 'modal-sub', text: opts.message }));
  const actions = el('div', { className: 'modal-actions' });
  const cancelBtn = el('button', { className: 'btn ghost modal-skip', text: 'Cancel' });
  const confirmBtn = el('button', { className: 'btn modal-danger', text: opts.confirmLabel });
  actions.appendChild(cancelBtn);
  actions.appendChild(confirmBtn);
  card.appendChild(actions);
  overlay.appendChild(card);
  document.body.appendChild(overlay);
  void overlay.offsetWidth;
  overlay.classList.add('visible');
  confirmBtn.focus();

  let closed = false;
  const close = () => {
    if (closed) return;
    closed = true;
    confirmModalOpen = false;
    overlay.classList.remove('visible');
    window.setTimeout(() => overlay.remove(), MODAL_FADE_MS);
    document.removeEventListener('keydown', onKey, true);
  };
  const fire = () => { close(); opts.onConfirm(); };
  // Capture-phase so the modal swallows app-wide shortcuts (SPACE play/stop,
  // ⌘Z undo) while it's open.
  const onKey = (e: KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === 'Escape') { e.preventDefault(); close(); }
    else if (e.key === 'Enter') { e.preventDefault(); fire(); }
  };
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
  card.addEventListener('click', (e) => e.stopPropagation());
  cancelBtn.addEventListener('click', close);
  confirmBtn.addEventListener('click', fire);
  document.addEventListener('keydown', onKey, true);
}

function buildTrack(def: TrackDef, numSteps: number, toneTimbre: ToneTimbre, audioSource: AudioSource, toneSource: ToneSource, pattern?: number[], pitchMap?: Map<number, number>): Track {
  const steps = new Array(numSteps).fill(false);
  const src = pattern ?? def.defaultPattern;
  for (const i of src) if (i < numSteps) steps[i] = true;
  // Pitched (tone) tracks use the independent toneSource; drum slots use the
  // drum engine. The 808-only slots ignore both args.
  const srcForVoice: AudioSource = def.pitched ? toneSource : audioSource;
  const voice = deferredVoice(() => def.voice(srcForVoice, toneTimbre));
  const track: Track = {
    name: def.name,
    steps,
    voice,
    audible: !def.only808 || audioSource === '808',
  };
  if (def.pitched) {
    track.pitches = new Array(numSteps).fill(DEFAULT_PITCH);
    if (pitchMap) for (const [s, p] of pitchMap) track.pitches[s] = p;
  }
  return track;
}

class App {
  root: HTMLElement;
  seq: Sequencer;
  stepCells: HTMLDivElement[][] = [];
  pitchDots: (HTMLDivElement | null)[][] = [];
  rowEls: HTMLDivElement[] = [];
  // Local-only ephemeral row mutes (by track index). Never persisted to the URL
  // hash, saved beats, or collab sync — a monitoring tool, not part of the beat.
  mutedRows = new Set<number>();
  playBtn!: HTMLButtonElement;
  bpmDisplay!: HTMLSpanElement;
  bpmSlider!: HTMLInputElement;
  barsBtn!: HTMLButtonElement;
  barsChipBtn!: HTMLButtonElement;
  mixersOff = window.innerWidth <= 720;
  gridEl!: HTMLDivElement;
  splash!: HTMLDivElement;
  audioReady = false;
  currentBpm: number;
  volumes: Record<string, number>;
  volSliders: HTMLInputElement[] = [];
  savedEl!: HTMLDivElement;
  savedStripEl!: HTMLDivElement;
  toneTimbre: ToneTimbre = DEFAULT_TONE_TIMBRE;
  audioSource: AudioSource = DEFAULT_AUDIO_SOURCE;
  // The unified 8-track layout — always in lockstep with this.seq.tracks. The
  // engine only changes which synthesizer plays each slot, never the layout.
  currentTrackDefs: TrackDef[] = UNIFIED_TRACK_DEFS;
  // 808 kick amplitude-envelope decay (seconds). Only meaningful in 808 mode.
  kick808Decay = DEFAULT_808_KICK_DECAY;
  decaySlider: HTMLInputElement | null = null;
  // Tone-track glide (0..100). Drives portamento on the FM / bass voices. No
  // longer user-tunable — pinned to DEFAULT_GLIDE so Electro Synth/Bass always
  // glide on legato runs. Users who want pizzicato pick an Organic timbre.
  toneGlide = DEFAULT_GLIDE;
  // Snare / hat amplitude-envelope decay (seconds). Keyed by track key 's'/'h'.
  drumDecay: Record<string, number> = { s: SNARE_DECAY.default, h: HAT_DECAY.default };
  drumDecaySliders: Partial<Record<string, HTMLInputElement>> = {};
  // Tone-row octave shift in semitones (multiple of 12), -2..+2 octaves.
  toneOctaveOffset = 0;
  octaveSlider: HTMLInputElement | null = null;
  audioSourceBtns: Map<AudioSource, HTMLButtonElement> = new Map();
  audioSourceSelect: HTMLSelectElement | null = null;
  // Independent tone-row engine. Auto-follows audioSource until the user
  // touches the tone-source selector, after which `toneSourceLocked` is true
  // and the choice is sticky.
  toneSource: ToneSource = DEFAULT_TONE_SOURCE;
  toneSourceLocked = false;
  toneSourceSelect: HTMLSelectElement | null = null;
  // Monotonic ticket for the async engine swaps. A swap that awaited the
  // sample download only applies if no newer swap was requested meanwhile,
  // so the last click wins instead of the slowest await.
  private sourceSwapSeq = 0;
  scale: ScaleId = DEFAULT_SCALE;
  scaleBtns: Map<ScaleId, HTMLButtonElement> = new Map();
  scaleLabelEl: HTMLSpanElement | null = null;
  toneTimbreSelect: HTMLSelectElement | null = null;
  scaleSelect: HTMLSelectElement | null = null;
  loupeEl!: HTMLDivElement;
  // Undo/redo: each entry is a serialized hash snapshot. historyCursor points
  // at the current state. Discrete edits push; slider drags coalesce on
  // release (BPM/volume use 'change' not 'input' for snapshotting).
  history: string[] = [];
  historyCursor = -1;
  isApplyingHistory = false;
  undoBtn: HTMLButtonElement | null = null;
  redoBtn: HTMLButtonElement | null = null;
  // Real-time collab. Connected only when the hash carries a `room=` param.
  // While `collabApplyingRemote` is true, mutation paths skip their broadcast
  // call so an incoming remote delta doesn't echo back to the room.
  collab: Collab;
  collabApplyingRemote = false;
  collabBtn: HTMLButtonElement | null = null;
  private shortLinks = new ShortLinkCache();
  private shortLinkPrimeTimer: number | null = null;
  presenceRowEl: HTMLDivElement | null = null;
  presenceDotsEl: HTMLDivElement | null = null;
  presenceEmojiBarEl: HTMLDivElement | null = null;
  // Live emoji-reaction rate limiting: timestamp of our last sent reaction,
  // and the last time we accepted an incoming reaction from each peer id.
  private lastEmojiSentAt = 0;
  private lastEmojiRecvAt: Map<string, number> = new Map();
  // Rolling counter so stacked float bursts fan out instead of overlapping.
  private emojiFloatSeq = 0;
  // Per-peer "is currently playing" flags, keyed by peer id. Drives the pulse
  // ring on presence dots. Cleared for ids that drop out of the roster.
  peerPlaying: Map<string, boolean> = new Map();
  // Display names for remote peers, keyed by peer id. The local user's name is
  // not stored here — it lives on collab.localName. Cleared when a peer leaves.
  peerNames: Map<string, string> = new Map();
  // The "set your name" modal element while open (null otherwise). Also gates
  // the one-time auto-open for users with no saved name on their first live room.
  private nameModalEl: HTMLDivElement | null = null;
  private nameHintShown = false;
  // Celebratory room-entry tracking. `seenPeerIds` are peers we've already
  // accounted for in the current room (so we don't fanfare everyone who was
  // already there when *we* joined). `pendingEntryCelebration` are fresh
  // joiners whose name we're still waiting on — the burst fires either when
  // their `name` delta arrives or after a short timeout (whichever first).
  // `celebrationRoomId` lets us reset all of this when the room changes.
  private seenPeerIds = new Set<string>();
  private pendingEntryCelebration = new Set<string>();
  private peerEntrySeeded = false;
  private celebrationRoomId: string | null = null;
  private volLoupeEl: HTMLDivElement | null = null;
  // Per-step ownership for collab color-coding. stepOwners[ti][s] holds the
  // color of the peer who last turned that step on, or null if it's the local
  // user's (default-orange) step or empty. Rebuilt fresh on every renderGrid()
  // — wholesale state swaps (preset/bars/undo/full-sync) reset attribution,
  // which is acceptable for v1.
  stepOwners: (string | null)[][] = [];

  // Drag-to-paint state for drum pads. Set on pointerdown over a drum cell; the
  // first cell's prior on/off state decides the paint mode for the whole drag
  // (off→turn-on, on→turn-off). `ti` is locked so the paint never bleeds into
  // another row. Cleared on pointerup/cancel. We don't capture the pointer on
  // the cell so the document-level pointermove can hit-test sibling cells —
  // necessary for touch, where the target keeps implicit pointer capture.
  // `order` records, in sweep order, the cells this drag has flipped (only cells
  // that actually changed state). Backtracking the pointer onto an earlier cell
  // in `order` reverts every cell painted after it, in real time.
  private dragPaint: { ti: number; startY: number; turnOn: boolean; order: Array<{ s: number; cell: HTMLElement }> } | null = null;
  // Maps a rendered drum cell back to its (ti, s, track) so the document-level
  // pointermove handler can paint whichever cell is under the finger/cursor.
  private drumCellInfo = new WeakMap<HTMLElement, { ti: number; s: number; track: Track }>();
  // Maps a rendered tone cell back to its (ti, s, dot) for the document-level
  // pointermove handler that drives the 2D tone-paint gesture.
  private toneCellInfo = new WeakMap<HTMLElement, { ti: number; s: number; dot: HTMLDivElement }>();
  // Active 2D tone gesture (touch only): a horizontal sweep toggles pads, and
  // vertical movement shifts the pitch of every pad touched so far. `order`
  // records, in sweep order, the pads this gesture flipped (plus the pre-gesture
  // pitch of each, so backtracking can restore them). `semis` is the live Y-delta
  // expressed in semitones, applied on top of each pad's `basePitch`.
  private tonePaint: {
    ti: number;
    turnOn: boolean;
    startX: number;
    startY: number;
    startCell: HTMLElement;
    startBasePitch: number;
    horizIntent: boolean;
    semis: number;
    track: Track;
    order: Array<{ s: number; cell: HTMLElement; dot: HTMLDivElement; basePitch: number }>;
    lastPreviewPitch: number;
    changed: boolean;
  } | null = null;

  constructor(root: HTMLElement) {
    this.root = root;
    const DEFAULT_HASH = '#bpm=98&bars=2&k=0,2,4,7,9,11,16,26&s=4,12,20,28&h=0,2,4,6,8,10,12,14,16,18,20,22,24,26,28,30&c=5,6,7&t=0:43,1:43,4:36,6:36,7:36,8:39,9:51,10:51,11:63,12:51,16:41,20:41,24:44,28:41&vol=k:0.75,s:0.75,h:0.75,c:0.75,t:0.75&tt=pluck&sc=minor';
    const fromHash = parseHash() ?? parseHash(DEFAULT_HASH)!;
    this.currentBpm = fromHash.bpm;
    this.volumes = fromHash.volumes;
    this.toneTimbre = fromHash.toneTimbre;
    this.scale = fromHash.scale;
    this.audioSource = fromHash.audioSource;
    this.toneSource = fromHash.toneSource;
    this.toneSourceLocked = fromHash.toneSourceLocked;
    this.kick808Decay = fromHash.kickDecay;
    this.drumDecay = { s: fromHash.snareDecay, h: fromHash.hatDecay };
    this.toneOctaveOffset = fromHash.toneOctaveOffset;
    // Glide is no longer user-tunable; old hashes' gl= value is ignored.
    this.toneGlide = DEFAULT_GLIDE;
    this.currentTrackDefs = UNIFIED_TRACK_DEFS;
    const numSteps = fromHash.numSteps;
    const tracks = this.currentTrackDefs.map((def, i) =>
      buildTrack(def, numSteps, this.toneTimbre, this.audioSource, this.toneSource, fromHash.patterns[i], fromHash.pitches[i])
    );
    this.seq = new Sequencer(tracks, (step) => this.onTick(step), numSteps);
    this.collab = new Collab({
      onPresence: (you, others) => this.renderPresenceRow(you, others),
      onRemoteDelta: (delta, from) => this.applyRemoteDelta(delta, from),
      onSyncRequest: () => ({ type: 'full', hash: this.currentHash() }),
      onSyncState: (delta) => { if (delta.type === 'full') this.applyFullHash(delta.hash); },
      onEmoji: (peerId, emoji) => this.handleIncomingEmoji(peerId, emoji),
    });
    this.collab.localName = loadDisplayName();
    this.render();
    this.setupDragPaint();
    this.setupTonePaint();
    this.applyVolumes();
    this.applyKickDecay();
    this.applyGlide();
    this.applyDrumDecay();
    this.applyToneOctave();
    // Transport BPM is set inside handleSplash() after the AudioContext is
    // allowed to start, so we don't ping the suspended context at boot.
    // Seed undo history with the initial state so the first edit is undoable.
    this.recordHistory(this.currentHash());
    window.addEventListener('hashchange', () => {
      // Auto-connect / disconnect when the room param flips.
      const newRoom = parseRoom();
      if (newRoom && newRoom !== this.collab.roomId) {
        this.collab.connect(newRoom);
        setCollabActive();
        track('collab_joined', { room_id: newRoom });
        this.refreshCollabBtn();
        this.renderPresenceRow(this.collab.me, this.collab.others);
      }
      const h = parseHash();
      if (!h) return;
      this.loadFromHash(h);
      // Auto-play when the user deliberately navigates to a saved beat.
      if (this.audioReady && !this.seq.isPlaying()) {
        this.seq.start();
        this.setPlayUI(true);
      }
    });
    // If the page was opened with a room link, connect immediately so the
    // joiner sees the host's presence and gets a state sync.
    const initialRoom = parseRoom();
    if (initialRoom) {
      this.collab.connect(initialRoom);
      setCollabActive();
      track('collab_joined', { room_id: initialRoom });
      this.refreshCollabBtn();
      this.renderPresenceRow(this.collab.me, this.collab.others);
    }
    this.scheduleShortLinkPrime();
  }

  // Serialize the full current state (beat + mix + voice/scale + source + 808
  // kick decay + glide) to a URL hash. The single funnel so callers never
  // forget an arg.
  currentHash(): string {
    return buildHash(this.currentBpm, this.seq.numSteps, this.seq.tracks, this.volumes, this.toneTimbre, this.scale, this.audioSource, this.kick808Decay, this.toneGlide, this.toneOctaveOffset, this.drumDecay.s, this.drumDecay.h, this.toneSource, this.toneSourceLocked);
  }

  // Returns the current beat hash with `room=<id>` appended when a room is
  // active. `room` is intentionally outside buildHash() so undo history stays
  // free of the room id (changing rooms shouldn't fill the undo stack).
  withRoom(hash: string): string {
    if (!this.collab.roomId) return hash;
    const sep = hash.endsWith('=') || hash.endsWith('&') ? '' : '&';
    return hash + sep + 'room=' + encodeURIComponent(this.collab.roomId);
  }

  render(): void {
    this.root.innerHTML = '';

    const brand = el('div', { className: 'brand' });
    const h1 = el('h1');
    const homeLink = el('a', { attrs: { href: window.location.origin, 'aria-label': 'Beat Swarm — reload' } });
    homeLink.innerHTML = 'BEAT <span class="accent">SWARM</span>';
    h1.appendChild(homeLink);
    brand.appendChild(h1);
    brand.appendChild(el('span', { className: 'tag', text: 'v0.4' }));
    this.root.appendChild(brand);

    // Transport, laid out in four visually-separated zones (no labels — just
    // gaps + dividers, per the UX brief):
    //   1. PLAYBACK  — Play + BPM (one unit, like a tape transport)
    //   2. SOCIAL    — Share (quick, Web Share API) + Collab (intentional, live jam)
    //   3. UTILITY   — Undo / Redo / Save
    //   4. DANGER    — Trash, held off on its own so it's hard to fat-finger
    const transport = el('div', { className: 'transport' });
    const divider = () => el('div', { className: 'transport-divider', attrs: { 'aria-hidden': 'true' } });

    // -- Zone 1: PLAYBACK --
    const playbackZone = el('div', { className: 'transport-zone transport-playback' });
    this.playBtn = el('button', { className: 'btn icon-btn primary play-btn' });
    this.playBtn.onclick = () => this.togglePlay();
    playbackZone.appendChild(this.playBtn);
    this.setPlayUI(false);
    const bpmWrap = el('div', { className: 'bpm' });
    bpmWrap.appendChild(el('label', { text: 'BPM' }));
    this.bpmSlider = el('input', { attrs: { type: 'range', min: '60', max: '180', step: '1', value: String(this.currentBpm) } });
    this.bpmSlider.oninput = () => this.onBpmChange(false);
    this.bpmSlider.onchange = () => this.onBpmChange(true);
    bpmWrap.appendChild(this.bpmSlider);
    this.bpmDisplay = el('span', { className: 'value', text: String(this.currentBpm) });
    bpmWrap.appendChild(this.bpmDisplay);
    playbackZone.appendChild(bpmWrap);
    transport.appendChild(playbackZone);

    transport.appendChild(divider());

    // -- Zone 2: SOCIAL --
    const socialZone = el('div', { className: 'transport-zone transport-social' });
    const shareBtn = el('button', { className: 'btn icon-btn share', attrs: { 'data-tip': 'Share this beat', 'aria-label': 'Share this beat' } });
    shareBtn.innerHTML = ICON_SHARE;
    shareBtn.onclick = () => this.shareBeat(shareBtn);
    shareBtn.addEventListener('pointerenter', () => void this.shortLinks.prime(this.currentHash()));
    shareBtn.addEventListener('focus', () => void this.shortLinks.prime(this.currentHash()));
    socialZone.appendChild(shareBtn);
    this.collabBtn = el('button', { className: 'btn icon-btn collab', attrs: { 'data-tip': 'Start a live jam', 'aria-label': 'Start a live collaboration session' } });
    this.collabBtn.innerHTML = ICON_COLLAB;
    this.collabBtn.onclick = () => this.toggleCollab();
    socialZone.appendChild(this.collabBtn);
    transport.appendChild(socialZone);

    transport.appendChild(divider());

    // -- Zone 3: UTILITY --
    const utilityZone = el('div', { className: 'transport-zone transport-utility' });
    this.undoBtn = el('button', { className: 'btn icon-btn ghost', attrs: { 'data-tip': 'Undo (⌘Z)', 'aria-label': 'Undo' } });
    this.undoBtn.innerHTML = ICON_UNDO;
    this.undoBtn.onclick = () => this.undo();
    utilityZone.appendChild(this.undoBtn);
    this.redoBtn = el('button', { className: 'btn icon-btn ghost', attrs: { 'data-tip': 'Redo (⌘⇧Z)', 'aria-label': 'Redo' } });
    this.redoBtn.innerHTML = ICON_REDO;
    this.redoBtn.onclick = () => this.redo();
    utilityZone.appendChild(this.redoBtn);
    const saveBtn = el('button', { className: 'btn icon-btn save', attrs: { 'data-tip': 'Save beat', 'aria-label': 'Save beat' } });
    saveBtn.innerHTML = ICON_SAVE;
    saveBtn.onclick = () => this.saveBeat();
    utilityZone.appendChild(saveBtn);
    transport.appendChild(utilityZone);

    transport.appendChild(divider());

    // -- Zone 4: DANGER --
    const dangerZone = el('div', { className: 'transport-zone transport-danger' });
    const clearBtn = el('button', { className: 'btn icon-btn ghost danger', attrs: { 'data-tip': 'Clear all steps', 'aria-label': 'Clear all steps' } });
    clearBtn.innerHTML = ICON_CLEAR;
    clearBtn.onclick = () => {
      this.clearAll();
      clearBtn.blur();
    };
    dangerZone.appendChild(clearBtn);
    transport.appendChild(dangerZone);

    this.refreshUndoRedoUI();
    this.root.appendChild(transport);

    // Collab presence row — its own full-width row above the sequencer. Always
    // shown when a room is active (any viewport), hidden otherwise. Lives
    // outside the control row so joins/leaves never shift the buttons.
    this.presenceRowEl = el('div', { className: 'presence-row' });
    this.root.appendChild(this.presenceRowEl);

    // Grid + bar-cycle button
    const gridWrap = el('div', { className: 'grid-wrap' });
    this.gridEl = el('div', { className: 'grid' + (this.mixersOff ? ' mixers-off' : '') });
    this.gridEl.style.setProperty('--step-count', String(this.seq.numSteps));
    this.renderGrid();
    gridWrap.appendChild(this.gridEl);
    this.renderPresenceRow(this.collab.me, this.collab.others);
    this.refreshCollabBtn();

    this.barsBtn = el('button', { className: 'bars-btn' });
    this.barsBtn.setAttribute('data-tip', 'Cycle bars: 1 → 2 → 4');
    this.barsBtn.onclick = () => this.handleBarsClick();
    this.renderBarsLabel();
    gridWrap.appendChild(this.barsBtn);

    this.root.appendChild(gridWrap);

    // Saved Beats strip — always visible so page height stays stable (prevents
    // mobile Safari toolbar collapse on first save). Placeholder shown when empty.
    this.savedEl = el('div', { className: 'saved' });
    const savedHead = el('div', { className: 'saved-head' });
    savedHead.appendChild(el('span', { className: 'saved-label', text: 'SAVED BEATS' }));
    const savedActions = el('div', { className: 'saved-actions' });
    const exportBtn = el('button', { className: 'saved-clear', text: 'EXPORT', attrs: { 'data-tip': 'Export Beats (JSON)' } });
    exportBtn.onclick = () => this.exportBeatJson();
    const importInput = el('input', { attrs: { type: 'file', accept: '.json,application/json' } });
    importInput.style.display = 'none';
    importInput.addEventListener('change', () => this.importBeatJson(importInput));
    const importBtn = el('button', { className: 'saved-clear saved-import', text: 'IMPORT', attrs: { 'data-tip': 'Import Beats (JSON)' } });
    importBtn.onclick = () => importInput.click();
    const savedClearBtn = el('button', { className: 'saved-clear', text: 'CLEAR', attrs: { 'data-tip': 'Delete Beats' } });
    savedClearBtn.onclick = () => openConfirmModal({
      title: 'Delete all saved beats?',
      message: 'Every saved beat will be permanently removed.',
      confirmLabel: 'Delete all',
      onConfirm: () => {
        persistSavedBeats([]);
        this.renderSavedBeats();
      },
    });
    savedActions.appendChild(exportBtn);
    savedActions.appendChild(importBtn);
    savedActions.appendChild(importInput);
    savedActions.appendChild(savedClearBtn);
    savedHead.appendChild(savedActions);
    this.savedEl.appendChild(savedHead);
    const savedStripOuter = el('div', { className: 'saved-strip' });
    this.savedStripEl = el('div', { className: 'saved-scroller' });
    savedStripOuter.appendChild(this.savedStripEl);
    this.savedEl.appendChild(savedStripOuter);
    const savedFooter = el('div', { className: 'saved-footer', text: 'Saved beats are in browser memory and not permanent. Export to save a copy.' });
    this.savedEl.appendChild(savedFooter);
    this.root.appendChild(this.savedEl);
    this.renderSavedBeats();

    const hint = el('div', { className: 'hint' });
    hint.innerHTML = '<kbd>SPACE</kbd> play/stop · <kbd>⌘Z</kbd> undo · <kbd>⌘⇧Z</kbd> redo';
    this.root.appendChild(hint);

    this.splash = el('div', { className: 'splash' });
    const inner = el('div', { className: 'inner' });
    const sh = el('h2');
    sh.innerHTML = 'BEAT <span class="accent">SWARM</span>';
    inner.appendChild(sh);
    const tagline = el('p', { className: 'tagline' });
    tagline.textContent = 'collaborative beat playground';
    inner.appendChild(tagline);
    inner.appendChild(el('p', { className: 'splash-cta', text: 'TAP TO START' }));

    // "How it works" — three icon + one-liner steps, always visible on the
    // splash. Clicking anywhere (incl. here) still dismisses → starts the app.
    const steps = el('div', { className: 'splash-steps' });
    const stepData: Array<{ icon: string; title: string }> = [
      { icon: ICON_GRID,    title: 'Create a beat.' },
      { icon: ICON_COLLAB,  title: 'Share a link.' },
      { icon: ICON_SLIDERS, title: 'Collaborate in real-time.' },
    ];
    for (const s of stepData) {
      const step = el('div', { className: 'splash-step' });
      const ic = el('div', { className: 'splash-step-icon' });
      ic.innerHTML = s.icon;
      step.appendChild(ic);
      const body = el('div', { className: 'splash-step-body' });
      body.appendChild(el('span', { className: 'splash-step-title', text: s.title }));
      step.appendChild(body);
      steps.appendChild(step);
    }
    inner.appendChild(steps);

    this.splash.appendChild(inner);
    this.splash.setAttribute('role', 'button');
    this.splash.setAttribute('aria-label', 'Tap to start Beat Swarm');
    this.splash.tabIndex = 0;
    this.splash.onclick = () => this.handleSplash();
    this.splash.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ' || e.code === 'Space') {
        e.preventDefault();
        void this.handleSplash();
      }
    });
    if (shouldSkipSplash()) {
      // Returning visitor or share-link arrival — don't show the splash.
      // The AudioContext can only resume inside a user gesture, so arm a
      // one-shot listener that boots audio on the first interaction.
      markSplashSeen();
      const boot = () => { void this.bootAudio(); };
      window.addEventListener('pointerdown', boot, { once: true });
      window.addEventListener('keydown', boot, { once: true });
    } else {
      document.body.appendChild(this.splash);
      this.splash.focus();
    }

    window.addEventListener('keydown', (e) => {
      if (this.splash.parentElement) return;
      // Ignore shortcuts when typing in any text input (e.g. rename field).
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.code === 'Space') {
        e.preventDefault();
        this.togglePlay();
        return;
      }
      const mod = e.metaKey || e.ctrlKey;
      if (mod && (e.key === 'z' || e.key === 'Z')) {
        e.preventDefault();
        if (e.shiftKey) this.redo();
        else this.undo();
        return;
      }
      if (mod && (e.key === 'y' || e.key === 'Y')) {
        e.preventDefault();
        this.redo();
      }
    });
  }

  // Build (or rebuild) the grid rows. Used on bar-doubling, preset / undo /
  // sync loads, and engine swaps (the `src-808` class shows/hides the
  // supplemental TR-808 rows + the kick decay slider).
  renderGrid(): void {
    this.gridEl.innerHTML = '';
    this.gridEl.style.setProperty('--step-count', String(this.seq.numSteps));
    this.gridEl.classList.toggle('src-808', this.audioSource === '808');
    this.gridEl.classList.toggle('src-organic', this.audioSource === 'organic');
    this.stepCells = [];
    this.pitchDots = [];
    this.rowEls = [];
    this.volSliders = [];
    this.decaySlider = null;
    this.drumDecaySliders = {};
    this.octaveSlider = null;
    // Wholesale re-render → fresh ownership map. Remote step tints survive
    // between renders (a remote toggle never re-renders), but a full grid
    // rebuild (preset / bars / undo / sync) resets attribution.
    this.stepOwners = [];
    // Toolbar sits at the top of the grid panel, outside the rows-scroll
    // container, so it fits the viewport even when the rows row-scroll
    // horizontally at 4 bars on a narrow screen.
    // Drums scroll separately from tone so the Voice/Scale toolbar can sit
    // between them, locked to panel width, never scrolling off-screen.
    this.renderSourceBar(this.gridEl);
    const drumsScroll = el('div', { className: 'rows-scroll rows-scroll-drums' });
    drumsScroll.style.setProperty('--step-count', String(this.seq.numSteps));
    this.gridEl.appendChild(drumsScroll);

    const toneScroll = el('div', { className: 'rows-scroll rows-scroll-tone' });
    toneScroll.style.setProperty('--step-count', String(this.seq.numSteps));

    // Sync horizontal scroll between the two containers.
    let syncingScroll = false;
    drumsScroll.addEventListener('scroll', () => {
      if (syncingScroll) return; syncingScroll = true;
      toneScroll.scrollLeft = drumsScroll.scrollLeft;
      syncingScroll = false;
    });
    toneScroll.addEventListener('scroll', () => {
      if (syncingScroll) return; syncingScroll = true;
      drumsScroll.scrollLeft = toneScroll.scrollLeft;
      syncingScroll = false;
    });

    this.seq.tracks.forEach((track, ti) => {
      const def = this.currentTrackDefs[ti];
      const key = def.key;
      if (def.pitched) {
        // Toolbar lives directly in gridEl — outside both scroll containers —
        // so it stays viewport-locked while bars scroll underneath it.
        this.renderToneToolbar(this.gridEl);
        this.gridEl.appendChild(toneScroll);
      }
      const isKick = def.key === 'k';
      const isDrumDecay = def.key === 's' || def.key === 'h';
      const isToneRow = !!def.pitched;
      // Kick → 808 decay slider; snare/hat → decay slider; tone → octave slider.
      const dual = isKick || isDrumDecay || isToneRow;
      const rowClasses = ['row'];
      if (def.pitched) rowClasses.push('tone-row');
      if (isKick) rowClasses.push('kick-row');
      if (isDrumDecay) rowClasses.push('has-drum-decay');
      if (def.only808) rowClasses.push('only808-row');
      if (dual) rowClasses.push('dual-vol');
      const isMuted = this.mutedRows.has(ti);
      track.muted = isMuted;
      if (isMuted) rowClasses.push('muted');
      const row = el('div', { className: rowClasses.join(' ') });
      this.rowEls.push(row);

      // Instrument name label — tap to mute the row, hold to clear it.
      const label = el('div', { className: 'label', attrs: { 'data-tip': 'Tap to mute\nHold to clear' } });
      const swatch = el('span', { className: 'swatch' });
      swatch.style.background = def.color;
      label.appendChild(swatch);
      // Full name shown on tablet+; abbreviated short label shown on narrow
      // phones to free horizontal space for step cells.
      const labelText = el('span', { className: 'label-text' });
      const nameFull = el('span', { className: 'label-full', text: track.name });
      const nameShort = el('span', { className: 'label-short', text: def.short ?? track.name.charAt(0) });
      labelText.appendChild(nameFull);
      labelText.appendChild(nameShort);
      label.appendChild(labelText);
      this.attachLabelHandlers(label, ti);
      row.appendChild(label);

      // Vertical volume slider — thin, lives between the label and the steps.
      // Uses the underlying <input type=range> rotated via CSS. The kick and
      // tone rows carry a second slider beside it (decay / glide).
      const volWrap = el('div', { className: dual ? 'vol vol-dual' : 'vol' });
      const volSlider = el('input', {
        attrs: {
          type: 'range',
          min: String(VOL_MIN),
          max: String(VOL_MAX),
          step: String(VOL_STEP),
          value: String(this.volumes[key] ?? DEFAULT_VOLUMES[key]),
          'aria-label': `${track.name} volume`,
          title: `${track.name} volume`,
        },
      });
      volSlider.style.setProperty('--track-color', def.color);
      volSlider.addEventListener('input', () => {
        const v = Number(volSlider.value);
        this.volumes[key] = v;
        track.voice.setVolume(v);
        this.pushHashLive();
        this.showSliderLoupe(volSlider, 'volume', Math.round((v / VOL_MAX) * 100));
      });
      volSlider.addEventListener('pointerup', () => this.hideVolLoupe());
      volSlider.addEventListener('change', () => {
        const v = Number(volSlider.value);
        this.broadcast({ type: 'volume', key, v });
        this.pushHash();
      });
      if (dual) {
        const slot = el('div', { className: 'vol-slot' });
        slot.appendChild(volSlider);
        volWrap.appendChild(slot);
      } else {
        volWrap.appendChild(volSlider);
      }
      this.volSliders.push(volSlider);

      // Kick row → 808 kick decay slider (only interactive in 808 mode, hidden
      // otherwise via the .src-808 class). The tone row used to carry a glide
      // slider here; glide is now always-on at the default value for Electro
      // Synth/Bass and no longer user-tunable.
      if (isKick) {
        const decaySlot = el('div', { className: 'vol-slot decay-slot' });
        const decaySlider = el('input', {
          attrs: {
            type: 'range', min: String(MIN_808_KICK_DECAY), max: String(MAX_808_KICK_DECAY), step: '0.01',
            value: String(this.kick808Decay),
            'aria-label': '808 kick decay', title: '808 kick decay',
          },
        });
        decaySlider.style.setProperty('--track-color', def.color);
        const decayPct = () => Math.round((this.kick808Decay - MIN_808_KICK_DECAY) / (MAX_808_KICK_DECAY - MIN_808_KICK_DECAY) * 100);
        decaySlider.addEventListener('input', () => {
          this.kick808Decay = clampKickDecay(Number(decaySlider.value));
          this.applyKickDecay();
          this.pushHashLive();
          this.showSliderLoupe(decaySlider, 'decay', decayPct());
        });
        decaySlider.addEventListener('pointerup', () => this.hideVolLoupe());
        decaySlider.addEventListener('change', () => {
          this.kick808Decay = clampKickDecay(Number(decaySlider.value));
          this.applyKickDecay();
          this.broadcast({ type: 'decay', d: this.kick808Decay });
          this.pushHash();
        });
        decaySlot.appendChild(decaySlider);
        volWrap.appendChild(decaySlot);
        this.decaySlider = decaySlider;
      }

      // Snare / hat row → amplitude-envelope decay slider. Hidden in organic
      // mode (sample voices have no envelope) via the .src-organic class.
      if (isDrumDecay) {
        const cfg = def.key === 's' ? SNARE_DECAY : HAT_DECAY;
        const clampDec = (d: number) => Math.max(cfg.min, Math.min(cfg.max, d));
        const slot = el('div', { className: 'vol-slot drum-decay-slot' });
        const slider = el('input', {
          attrs: {
            type: 'range', min: String(cfg.min), max: String(cfg.max), step: '0.005',
            value: String(this.drumDecay[def.key]),
            'aria-label': `${track.name} decay`, title: `${track.name} decay`,
          },
        });
        slider.style.setProperty('--track-color', def.color);
        const pct = () => Math.round((this.drumDecay[def.key] - cfg.min) / (cfg.max - cfg.min) * 100);
        slider.addEventListener('input', () => {
          this.drumDecay[def.key] = clampDec(Number(slider.value));
          this.applyDrumDecay();
          this.pushHashLive();
          this.showSliderLoupe(slider, 'decay', pct());
        });
        slider.addEventListener('pointerup', () => this.hideVolLoupe());
        slider.addEventListener('change', () => {
          this.drumDecay[def.key] = clampDec(Number(slider.value));
          this.applyDrumDecay();
          this.broadcast({ type: 'drumdecay', key: def.key, d: this.drumDecay[def.key] });
          this.pushHash();
        });
        slot.appendChild(slider);
        volWrap.appendChild(slot);
        this.drumDecaySliders[def.key] = slider;
      }

      // Tone row → octave shifter (sits in the slot the glide slider used to
      // occupy). Whole-octave steps, -2..+2 octaves; shifts the entire row.
      if (isToneRow) {
        const slot = el('div', { className: 'vol-slot octave-slot' });
        const slider = el('input', {
          attrs: {
            type: 'range', min: String(OCTAVE_MIN_SEMIS), max: String(OCTAVE_MAX_SEMIS), step: '12',
            value: String(this.toneOctaveOffset),
            'aria-label': 'Tone row octave', title: 'Octave shift for the tone row',
          },
        });
        slider.style.setProperty('--track-color', def.color);
        const octLabel = () => { const o = this.toneOctaveOffset / 12; return `${o >= 0 ? '+' : ''}${o}`; };
        slider.addEventListener('input', () => {
          this.toneOctaveOffset = clampOctaveSemis(Number(slider.value));
          this.applyToneOctave();
          this.pushHashLive();
          this.showSliderLoupe(slider, 'octave', octLabel());
        });
        slider.addEventListener('pointerup', () => this.hideVolLoupe());
        slider.addEventListener('change', () => {
          this.toneOctaveOffset = clampOctaveSemis(Number(slider.value));
          this.applyToneOctave();
          this.broadcast({ type: 'octave', semis: this.toneOctaveOffset });
          this.pushHash();
        });
        slot.appendChild(slider);
        volWrap.appendChild(slot);
        this.octaveSlider = slider;
      }
      row.appendChild(volWrap);

      const steps = el('div', { className: 'steps' });
      const cells: HTMLDivElement[] = [];
      const dots: (HTMLDivElement | null)[] = [];
      const owners: (string | null)[] = [];
      for (let s = 0; s < this.seq.numSteps; s++) {
        const cell = el('div', { className: 'step' + (s % 4 === 0 ? ' downbeat' : '') + (s % 16 === 0 && s > 0 ? ' barline' : '') });
        cell.style.setProperty('--track-color', def.color);
        if (track.steps[s]) cell.classList.add('on');
        owners.push(null);

        if (def.pitched) {
          const dot = el('div', { className: 'pitch-dot' });
          this.updatePitchDot(dot, track.pitches?.[s] ?? DEFAULT_PITCH);
          cell.appendChild(dot);
          dots.push(dot);
          this.attachToneCellHandlers(cell, dot, ti, s);
        } else {
          dots.push(null);
          this.attachDrumCellHandlers(cell, ti, s, track);
        }
        steps.appendChild(cell);
        cells.push(cell);
      }
      this.stepCells.push(cells);
      this.pitchDots.push(dots);
      this.stepOwners.push(owners);
      row.appendChild(steps);
      (def.pitched ? toneScroll : drumsScroll).appendChild(row);
    });
  }

  // SOURCE bar — sits above the drum pads at the top of the grid panel.
  // Affects the entire engine (drums + tone), so it belongs above everything.
  renderSourceBar(parent: HTMLElement): void {
    this.audioSourceBtns.clear();
    const bar = el('div', { className: 'source-bar' });
    bar.appendChild(el('span', { className: 'tone-toolbar-label', text: 'SOURCE' }));
    const srcPills = el('div', { className: 'tt-pills' });
    for (const s of AUDIO_SOURCES) {
      const btn = el('button', {
        className: 'tone-voice-btn' + (s.id === this.audioSource ? ' active' : ''),
        text: s.label,
        attrs: { 'data-tip': s.tip, 'aria-label': `${s.label} engine` },
      });
      btn.addEventListener('click', () => { void this.swapAudioSource(s.id); });
      this.audioSourceBtns.set(s.id, btn);
      srcPills.appendChild(btn);
    }
    bar.appendChild(srcPills);
    const srcSelect = el('select', { className: 'tt-select', attrs: { 'aria-label': 'Audio source' } });
    for (const s of AUDIO_SOURCES) {
      const opt = el('option', { text: s.label, attrs: { value: s.id } });
      if (s.id === this.audioSource) opt.selected = true;
      srcSelect.appendChild(opt);
    }
    srcSelect.addEventListener('change', () => { void this.swapAudioSource(srcSelect.value as AudioSource); });
    bar.appendChild(srcSelect);
    this.audioSourceSelect = srcSelect;

    // (The 808 kick decay moved onto a vertical slider beside the KICK volume
    // slider — see renderGrid — so it broadcasts in a live jam like the mix.)

    // INSPIRE — anchored to the top-right of the grid panel, directly opposite
    // the SOURCE picker. One press pulls a random beat from the Inspire pool
    // (curated named beats + algorithmically generated grooves).
    const inspireBtn = el('button', { className: 'inspire-btn source-inspire', attrs: { 'data-tip': 'Surprise me — load a random beat', 'aria-label': 'Inspire — load a random beat' } });
    inspireBtn.innerHTML = `<span class="inspire-icon">${ICON_SPARK}</span><span class="inspire-label">INSPIRE</span>`;
    inspireBtn.addEventListener('click', () => this.loadInspire(inspireBtn));
    bar.appendChild(inspireBtn);

    // Mixer toggle — collapses every per-track volume/decay slider in one tap so
    // the pad grid gets the full row width (most useful on phones, where the
    // sliders crowd the cells). Sits at the right end of the SOURCE bar.
    const mixBtn = el('button', {
      className: 'mix-toggle-btn' + (this.mixersOff ? ' active' : ''),
      attrs: {
        'data-tip': this.mixersOff ? 'Show track sliders' : 'Hide track sliders',
        'aria-label': 'Toggle per-track sliders',
        'aria-pressed': String(this.mixersOff),
      },
    });
    mixBtn.innerHTML = ICON_SLIDERS;
    mixBtn.addEventListener('click', () => {
      this.mixersOff = !this.mixersOff;
      this.gridEl.classList.toggle('mixers-off', this.mixersOff);
      mixBtn.classList.toggle('active', this.mixersOff);
      mixBtn.setAttribute('aria-pressed', String(this.mixersOff));
      mixBtn.setAttribute('data-tip', this.mixersOff ? 'Show track sliders' : 'Hide track sliders');
    });
    bar.appendChild(mixBtn);

    // Compact "+N" bars chip — the mobile replacement for the full-height
    // vertical bars bar that used to run down the right edge of the grid.
    // Same click behaviour; the desktop vertical bar stays (hidden on mobile
    // via CSS), this chip is hidden on desktop.
    const initBars = this.seq.numSteps / DEFAULT_STEPS;
    const barsChip = el('button', { className: 'bars-chip' });
    barsChip.innerHTML = barsChipMarkup(initBars);
    barsChip.setAttribute('aria-label', `${initBars} bars — tap to cycle 1, 2, 4`);
    barsChip.setAttribute('data-tip', 'Cycle bars: 1 → 2 → 4');
    barsChip.addEventListener('click', () => this.handleBarsClick());
    bar.appendChild(barsChip);
    this.barsChipBtn = barsChip;

    parent.appendChild(bar);
  }

  // Source + voice + scale selector strip — injected between CLAP and TONE
  // rows so it's visually adjacent to the row it controls. SOURCE picks the
  // tone-track engine (independent of the drum engine); VOICE pills swap
  // timbre; SCALE clamps pitches to in-scale notes. Source + Scale are always
  // dropdowns; Voice keeps its pill row (collapsing to a dropdown on narrow
  // viewports via CSS).
  renderToneToolbar(parent: HTMLElement): void {
    this.scaleBtns.clear();
    const toolbar = el('div', { className: 'tone-toolbar' });

    // -- SOURCE group --
    const srcGroup = el('div', { className: 'tt-group tt-group-tonesource' });
    srcGroup.appendChild(el('span', { className: 'tone-toolbar-label', text: 'SOURCE' }));
    const srcSelect = el('select', { className: 'tt-select tt-select-always', attrs: { 'aria-label': 'Tone source' } });
    for (const s of TONE_SOURCES) {
      const opt = el('option', { text: s.label, attrs: { value: s.id } });
      if (s.id === this.toneSource) opt.selected = true;
      srcSelect.appendChild(opt);
    }
    srcSelect.addEventListener('change', () => { void this.swapToneSource(srcSelect.value as ToneSource); });
    srcGroup.appendChild(srcSelect);
    this.toneSourceSelect = srcSelect;
    // -- VOICE group (first) --
    // Organic tone mode hides the Synth voice — its sampler is just piano-through-a-
    // filter, which reads as a slightly duller piano and doesn't fit the
    // organic aesthetic.
    const availableTimbres = TONE_TIMBRES.filter(t =>
      isTimbreAvailable(t.id) &&
      !(this.toneSource === 'organic' && (t.id === 'synth' || t.id === 'voice'))
    );
    const voiceGroup = el('div', { className: 'tt-group tt-group-voice' });
    voiceGroup.appendChild(el('span', { className: 'tone-toolbar-label', text: 'TONE' }));
    const voiceSelect = el('select', { className: 'tt-select tt-select-always', attrs: { 'aria-label': 'Voice' } });
    for (const t of availableTimbres) {
      const opt = el('option', { text: t.label, attrs: { value: t.id } });
      if (t.id === this.toneTimbre) opt.selected = true;
      voiceSelect.appendChild(opt);
    }
    voiceSelect.addEventListener('change', () => this.swapToneTimbre(voiceSelect.value as ToneTimbre));
    voiceGroup.appendChild(voiceSelect);
    this.toneTimbreSelect = voiceSelect;
    toolbar.appendChild(voiceGroup);

    // -- SCALE group --
    const scaleGroup = el('div', { className: 'tt-group tt-group-scale' });
    const scaleLabelWrap = el('span', { className: 'tone-toolbar-label tt-label-scale', text: 'SCALE' });
    scaleGroup.appendChild(scaleLabelWrap);
    const scaleSelect = el('select', { className: 'tt-select tt-select-always', attrs: { 'aria-label': 'Scale' } });
    for (const s of SCALES) {
      const opt = el('option', { text: s.label, attrs: { value: s.id } });
      if (s.id === this.scale) opt.selected = true;
      scaleSelect.appendChild(opt);
    }
    scaleSelect.addEventListener('change', () => this.setScale(scaleSelect.value as ScaleId));
    scaleGroup.appendChild(scaleSelect);
    this.scaleSelect = scaleSelect;

    // SOURCE + SCALE cluster right-justified
    const rightGroup = el('div', { className: 'tt-right' });
    rightGroup.appendChild(srcGroup);
    rightGroup.appendChild(scaleGroup);
    toolbar.appendChild(rightGroup);

    parent.appendChild(toolbar);
  }

  setScale(scale: ScaleId): void {
    if (scale === this.scale) return;
    this.scale = scale;
    // Snap any existing tone-row pitches to the new scale.
    const toneIdx = this.currentTrackDefs.findIndex(d => d.pitched);
    if (toneIdx >= 0) {
      const track = this.seq.tracks[toneIdx];
      if (track.pitches) {
        for (let s = 0; s < track.pitches.length; s++) {
          const snapped = snapToScale(track.pitches[s], this.scale);
          track.pitches[s] = snapped;
          const dot = this.pitchDots[toneIdx]?.[s];
          if (dot) this.updatePitchDot(dot, snapped);
        }
      }
    }
    if (this.scaleSelect) this.scaleSelect.value = scale;
    this.broadcast({ type: 'scale', scale });
    this.pushHash();
  }

  swapToneTimbre(timbre: ToneTimbre): void {
    if (timbre === this.toneTimbre) return;
    this.toneTimbre = timbre;
    const toneIdx = TONE_TRACK_IDX;
    const track = this.seq.tracks[toneIdx];
    const key = this.currentTrackDefs[toneIdx].key;
    const oldVoice = track.voice;
    oldVoice.release?.();
    const newVoice = makeTone(timbre, this.toneSource);
    newVoice.setVolume(this.volumes[key] ?? DEFAULT_VOLUMES[key]);
    track.voice = newVoice;
    this.applyGlide();
    // Defer disposal of the old voice past the longest tail in this app
    // (~1.6s for the bell release) so any in-flight scheduled note finishes
    // before its audio nodes are torn down.
    setTimeout(() => oldVoice.dispose?.(), VOICE_DISPOSE_DELAY_MS);
    if (this.toneTimbreSelect) this.toneTimbreSelect.value = timbre;
    this.broadcast({ type: 'voice', timbre });
    this.pushHash();
  }

  // Hot-swap the audio engine. Organic (sample) mode lazily fetches the
  // Tone.js drum + Salamander piano buffers; the picker shows a loading state
  // until they're ready so the user never lands in a silent engine. The layout
  // is the same across every engine — switching just swaps which synthesizer
  // plays each slot (the 808 supplemental rows show/hide via .src-808), so the
  // groove (pattern + pitches) carries straight across.
  async swapAudioSource(source: AudioSource): Promise<void> {
    if (source === this.audioSource) return;
    const ticket = ++this.sourceSwapSeq;
    track('source_toggled', { source_name: source, active: true });
    // Auto-follow the tone source unless the user has manually locked it.
    const nextToneSource: ToneSource = this.toneSourceLocked
      ? this.toneSource
      : deriveToneSource(source);
    const needSamples = source === 'organic' || nextToneSource === 'organic';
    if (needSamples) {
      const btn = this.audioSourceBtns.get('organic');
      btn?.classList.add('loading');
      try {
        const loadingToast = window.setTimeout(() => this.showToast('Loading samples…'), 250);
        await ensureSamplesLoaded();
        window.clearTimeout(loadingToast);
      } catch (err) {
        console.error('Sample load failed', err);
        this.showToast('Could not load samples', true);
        btn?.classList.remove('loading');
        return;
      }
      btn?.classList.remove('loading');
      // A newer swap landed while the samples were downloading — it already
      // applied its own state, so this stale request must not overwrite it.
      if (ticket !== this.sourceSwapSeq) return;
    }
    this.audioSource = source;
    this.toneSource = nextToneSource;
    this.applySourceAudibility();
    // Synth timbre isn't available in Organic mode — auto-fall back to piano so
    // the tone row keeps playing instead of going silent.
    if (this.toneSource === 'organic' && (this.toneTimbre === 'synth' || this.toneTimbre === 'voice')) {
      this.toneTimbre = 'piano';
    }
    this.seq.tracks.forEach((track, ti) => {
      const def = this.currentTrackDefs[ti];
      if (def.only808) return; // cowbell / tom / rim are always the TR-808 voices
      const oldVoice = track.voice;
      oldVoice.release?.();
      const srcForVoice: AudioSource = def.pitched ? this.toneSource : source;
      const newVoice = def.voice(srcForVoice, this.toneTimbre);
      newVoice.setVolume(this.volumes[def.key] ?? DEFAULT_VOLUMES[def.key]);
      track.voice = newVoice;
      setTimeout(() => oldVoice.dispose?.(), VOICE_DISPOSE_DELAY_MS);
    });
    this.applyKickDecay();
    this.applyGlide();
    this.applyDrumDecay();
    this.applyToneOctave();
    // Re-render the grid so the VOICE toolbar's pill set tracks the new
    // source (Organic hides the Synth pill) and the toolbar's active pill
    // reflects an auto-fallback timbre. renderGrid also re-applies the
    // src-808 class which shows / hides the supplemental TR-808 rows.
    this.renderGrid();
    for (const [id, btn] of this.audioSourceBtns) btn.classList.toggle('active', id === source);
    if (this.audioSourceSelect) this.audioSourceSelect.value = source;
    if (this.toneSourceSelect) this.toneSourceSelect.value = this.toneSource;
    // A source change has whole-grid blast radius in a live room (extra rows
    // appear / disappear and every voice changes), so broadcast the full state.
    this.broadcastFull();
    this.pushHash();
  }

  // Independent tone-row engine swap. Marks the choice as user-locked so
  // future audio-source changes no longer auto-follow. `silent` suppresses
  // the outbound collab broadcast (used when applying an inbound peer delta
  // — swapToneSource is async, so the collabApplyingRemote echo guard can't
  // straddle the await reliably; we just pass silent=true instead).
  async swapToneSource(source: ToneSource, silent = false): Promise<void> {
    // First touch always locks, even if the user re-picks the current value.
    this.toneSourceLocked = true;
    if (source === this.toneSource) {
      this.pushHash();
      return;
    }
    const ticket = ++this.sourceSwapSeq;
    if (!silent) track('tone_source_toggled', { source_name: source, active: true });
    if (source === 'organic') {
      try {
        const loadingToast = window.setTimeout(() => this.showToast('Loading samples…'), 250);
        await ensureSamplesLoaded();
        window.clearTimeout(loadingToast);
      } catch (err) {
        console.error('Sample load failed', err);
        this.showToast('Could not load samples', true);
        return;
      }
      if (ticket !== this.sourceSwapSeq) return; // superseded while loading
    }
    this.toneSource = source;
    if (source === 'organic' && (this.toneTimbre === 'synth' || this.toneTimbre === 'voice')) {
      this.toneTimbre = 'piano';
    }
    const toneIdx = TONE_TRACK_IDX;
    const tr = this.seq.tracks[toneIdx];
    const def = this.currentTrackDefs[toneIdx];
    if (tr && def) {
      const oldVoice = tr.voice;
      oldVoice.release?.();
      const newVoice = def.voice(this.toneSource, this.toneTimbre);
      newVoice.setVolume(this.volumes[def.key] ?? DEFAULT_VOLUMES[def.key]);
      tr.voice = newVoice;
      setTimeout(() => oldVoice.dispose?.(), VOICE_DISPOSE_DELAY_MS);
    }
    this.applyGlide();
    this.applyToneOctave();
    // Re-render so the VOICE pill set reflects the new tone source (Organic
    // hides the Synth pill) and the tone-source select reflects the choice.
    this.renderGrid();
    if (this.toneSourceSelect) this.toneSourceSelect.value = this.toneSource;
    if (!silent) this.broadcast({ type: 'tonesource', source });
    this.pushHash();
  }

  // Supplemental cowbell/tom/rim data always stays in the unified pattern,
  // while its audibility is derived solely from the current drum source.
  // This state is intentionally absent from hashes, saved beats, and collab.
  private applySourceAudibility(): void {
    this.seq.tracks.forEach((track, ti) => {
      const audible = !this.currentTrackDefs[ti]?.only808 || this.audioSource === '808';
      if (track.audible !== false && !audible) track.voice.release?.();
      track.audible = audible;
    });
  }

  // Push the current 808 kick decay to the live kick voice (no-op outside 808).
  applyKickDecay(): void {
    if (this.audioSource !== '808') return;
    this.seq.tracks[0]?.voice.setDecay?.(this.kick808Decay);
  }

  // Push the current tone glide to the live tone voice (no-op for non-gliding
  // timbres / engines).
  applyGlide(): void {
    this.seq.tracks[TONE_TRACK_IDX]?.voice.setGlide?.(glideToSeconds(this.toneGlide));
  }

  // Push the snare / hat decay to their live voices (no-op for organic samples).
  applyDrumDecay(): void {
    this.seq.tracks[1]?.voice.setDecay?.(this.drumDecay.s);
    this.seq.tracks[2]?.voice.setDecay?.(this.drumDecay.h);
  }

  // Pin the tone-row octave shift onto the tone track so the sequencer adds it
  // to every pitch it triggers.
  applyToneOctave(): void {
    const t = this.seq.tracks[TONE_TRACK_IDX];
    if (t) t.pitchOffset = this.toneOctaveOffset;
  }

  // ==== Loupe ====
  // A floating chip that appears above a tone cell during a vertical pitch
  // drag. Two lines: the metric name on top ("pitch"), the live value below
  // (e.g. "F#3"). On mobile the user's finger covers the cell, so the loupe
  // sits above it.
  // Renders the loupe's two-line content: a small uppercase metric label on
  // top, the value (larger, bold) below.
  private fillLoupe(node: HTMLElement, label: string, value: string): void {
    node.innerHTML = '';
    node.appendChild(el('span', { className: 'loupe-label', text: label }));
    node.appendChild(el('span', { className: 'loupe-value', text: value }));
  }

  ensureLoupe(): void {
    if (this.loupeEl) return;
    this.loupeEl = el('div', { className: 'loupe' });
    document.body.appendChild(this.loupeEl);
  }

  showLoupe(midi: number, cell: HTMLElement): void {
    this.ensureLoupe();
    const rect = cell.getBoundingClientRect();
    this.fillLoupe(this.loupeEl, 'pitch', midiName(midi));
    this.loupeEl.style.left = `${rect.left + rect.width / 2}px`;
    this.loupeEl.style.top = `${rect.top - 14}px`;
    this.loupeEl.classList.add('visible');
  }

  updateLoupe(midi: number): void {
    if (!this.loupeEl) return;
    this.fillLoupe(this.loupeEl, 'pitch', midiName(midi));
  }

  hideLoupe(): void {
    if (!this.loupeEl) return;
    this.loupeEl.classList.remove('visible');
  }

  // Slider loupe — pops *above* a rotated mixer/decay/glide slider (so a touch
  // finger on the thumb doesn't cover it). Two lines: the metric name on top
  // ("volume" / "decay" / "glide"), the 0-100 value below. Shared element
  // across volume, kick decay and tone glide.
  showSliderLoupe(slider: HTMLInputElement, label: string, value: number | string): void {
    if (!this.volLoupeEl) {
      this.volLoupeEl = el('div', { className: 'loupe vol-loupe' });
      document.body.appendChild(this.volLoupeEl);
    }
    const host = (slider.closest('.vol-slot') as HTMLElement | null)
      ?? (slider.closest('.vol') as HTMLElement | null)
      ?? slider;
    const rect = host.getBoundingClientRect();
    this.fillLoupe(this.volLoupeEl, label, String(value));
    this.volLoupeEl.style.left = `${rect.left + rect.width / 2}px`;
    this.volLoupeEl.style.top = `${rect.top - 14}px`;
    this.volLoupeEl.classList.add('visible');
  }

  hideVolLoupe(): void {
    if (!this.volLoupeEl) return;
    this.volLoupeEl.classList.remove('visible');
  }

  // Preview of a tone pitch, played while the user drags a tone cell. Cuts any
  // preview already sounding (voice.release) before the new onset so quick
  // drags don't pile up overlapping notes. With hold=false it's a brief pluck
  // (~180ms release); with hold=true the note sustains until stopTonePreview()
  // is called — used so the tone holds for as long as the user stays pressed.
  previewTonePitch(voice: Voice, pitch: number, hold = false): void {
    if (!this.audioReady) return;
    voice.release?.();
    const now = Tone.now();
    if (hold) voice.trigger(now, 0.65, pitch);
    else voice.trigger(now, 0.65, pitch, { releaseAt: now + 0.18 });
  }

  // Stop a sustained pitch preview started by previewTonePitch(..., true).
  stopTonePreview(voice: Voice): void {
    voice.release?.();
  }

  // Drum-pad interaction: tap toggles a cell; a horizontal drag paints a run of
  // cells. The first cell touched fixes the mode — if it was off the drag turns
  // cells on, if it was on the drag turns them off. Painting is locked to the
  // starting row (no bleed). Works for mouse and touch via pointer events.
  attachDrumCellHandlers(cell: HTMLDivElement, ti: number, s: number, track: Track): void {
    this.drumCellInfo.set(cell, { ti, s, track });

    // Arm a paint sweep. The starting cell is NOT painted yet — painting is
    // deferred until the finger actually moves (dragOnto on first pointermove).
    // Long press + release with no drag → nothing toggled.
    const beginPaint = (y: number): void => {
      const turnOn = !this.seq.tracks[ti].steps[s];
      this.dragPaint = { ti, startY: y, turnOn, order: [] };
    };

    cell.addEventListener('pointerdown', (e: PointerEvent) => {
      if (e.button > 0) return; // ignore right/middle click

      if (e.pointerType !== 'touch') {
        e.preventDefault();
        beginPaint(e.clientY);
        this.dragOnto(cell); // paint starting cell immediately (no long-press concern on mouse)
        return;
      }

      // Touch: cells are `touch-action: manipulation`, so the browser natively
      // handles vertical page scroll AND horizontal .rows-scroll — we don't
      // touch either. We only watch for a 200ms stationary long-press: if the
      // finger hasn't moved past MOVE_TOL by then we arm drag-select and hand
      // off to the setupDragPaint document handlers. From that instant a
      // {passive:false} touchmove guard (registered up front, gated on `armed`)
      // starts preventDefault()ing, which imperatively cancels browser scrolling
      // for the rest of the gesture — that's what makes the elementFromPoint
      // sweep (incl. the back-and-forth deselect motion) survive on iOS. If the
      // finger moves first, we bow out and the browser owns the gesture.
      const LONGPRESS_MS = 200;
      const MOVE_TOL = 10; // px — must stay at/below iOS's own pan-start threshold
      const startX = e.clientX;
      const startY = e.clientY;
      let armed = false;  // drag-select handed to setupDragPaint
      let bailed = false; // finger moved first — browser owns the gesture

      // Register the non-passive touchmove listener NOW, not after the timer:
      // iOS decides scroll-vs-not partly from the listeners present at touchstart,
      // so a late addEventListener can be ignored. It's a no-op until `armed`.
      const onTouchMove = (te: TouchEvent): void => {
        if (armed && te.cancelable) te.preventDefault();
      };

      const timer = window.setTimeout(() => {
        if (bailed) return;
        armed = true;
        beginPaint(startY);
      }, LONGPRESS_MS);

      const cleanup = (): void => {
        cell.removeEventListener('pointermove', onMove);
        cell.removeEventListener('pointerup', onUp);
        cell.removeEventListener('pointercancel', onCancel);
        cell.removeEventListener('touchmove', onTouchMove);
        clearTimeout(timer);
      };

      const onMove = (ev: PointerEvent): void => {
        if (armed || bailed) return; // setupDragPaint drives painting now / browser owns it
        const dx = Math.abs(ev.clientX - startX);
        const dy = Math.abs(ev.clientY - startY);
        if (Math.hypot(dx, dy) > MOVE_TOL) {
          // Finger moved before the long-press fired → let the browser scroll
          // (it picks the axis: vertical = page, horizontal = .rows-scroll).
          bailed = true;
          clearTimeout(timer);
          cell.removeEventListener('touchmove', onTouchMove);
        }
      };
      const onUp = (): void => {
        cleanup();
        if (armed) return; // paint commit handled by setupDragPaint's end()
        if (!bailed) {
          // Quick tap → toggle this cell.
          const turnOn = !this.seq.tracks[ti].steps[s];
          if (this.applyDrumStep(cell, turnOn, true)) this.pushHash();
        }
      };
      const onCancel = (): void => { cleanup(); };
      cell.addEventListener('pointermove', onMove);
      cell.addEventListener('pointerup', onUp);
      cell.addEventListener('pointercancel', onCancel);
      cell.addEventListener('touchmove', onTouchMove, { passive: false });
    });

    // Mouse: sibling cells fire pointerenter as the cursor sweeps across.
    cell.addEventListener('pointerenter', () => this.dragOnto(cell));
  }

  // Set a single drum cell to `on`. No-op if already in that state. Updates the
  // grid, clears collab ownership, optionally previews the sound, and broadcasts
  // the step delta. Returns whether the cell's state actually changed.
  private applyDrumStep(cell: HTMLElement, on: boolean, preview: boolean): boolean {
    const info = this.drumCellInfo.get(cell);
    if (!info) return false;
    const { ti, s, track } = info;
    if (this.seq.tracks[ti].steps[s] === on) return false;
    this.seq.tracks[ti].steps[s] = on;
    cell.classList.toggle('on', on);
    this.setStepOwner(ti, s, null); // it's mine now
    if (on && preview && this.audioReady) track.voice.trigger(Tone.now());
    this.broadcast({ type: 'step', ti, s, on });
    return true;
  }

  // Handle the drag pointer arriving over `cell` (same row only):
  //   - frontier cell (last painted): nothing to do
  //   - a cell already painted earlier in the sweep: backtrack — revert every
  //     cell painted after it, restoring their pre-drag state
  //   - a fresh cell: paint it and extend the sweep
  private dragOnto(cell: HTMLElement): void {
    const dp = this.dragPaint;
    if (!dp) return;
    const info = this.drumCellInfo.get(cell);
    if (!info || info.ti !== dp.ti) return; // no row-bleed
    const idx = dp.order.findIndex((e) => e.s === info.s);
    if (dp.order.length > 0 && idx === dp.order.length - 1) return; // already the frontier
    if (idx === -1) {
      if (this.applyDrumStep(cell, dp.turnOn, true)) dp.order.push({ s: info.s, cell });
      return;
    }
    for (let i = dp.order.length - 1; i > idx; i--) {
      this.applyDrumStep(dp.order[i].cell, !dp.turnOn, false);
    }
    dp.order.length = idx + 1;
  }

  // Document-level pointer handlers that drive drag-painting. On touch the
  // starting cell keeps implicit pointer capture, so sibling cells never get
  // pointerenter — we hit-test with elementFromPoint instead. pointerup/cancel
  // ends the drag and commits one undo entry + hash push for the whole sweep.
  private setupDragPaint(): void {
    document.addEventListener('pointermove', (e: PointerEvent) => {
      if (!this.dragPaint) return;
      // Keep the browser from claiming this pointer for a vertical pan-y
      // scroll mid-sweep (which would fire pointercancel and kill the
      // selection). Cancellable because pointermove isn't passive by default.
      if (e.cancelable) e.preventDefault();
      // Use dp.startY instead of e.clientY so vertical finger slop never
      // drifts the hit-test into a different row and stalls the selection.
      const target = document.elementFromPoint(e.clientX, this.dragPaint.startY);
      const stepEl = target?.closest('.step');
      if (stepEl instanceof HTMLElement) this.dragOnto(stepEl);
    }, { passive: false });
    const end = (): void => {
      if (!this.dragPaint) return;
      const changed = this.dragPaint.order.length > 0;
      this.dragPaint = null;
      if (changed) this.pushHash();
    };
    document.addEventListener('pointerup', end);
    document.addEventListener('pointercancel', end);
  }

  // Tonal cell interaction:
  //   Mouse/pen:
  //     - mousedown → mousemove vertically: shift pitch (only when cell is on)
  //     - if no significant drag at mouseup: toggle on/off (inheriting prev pitch)
  //     - wheel: shift pitch by 1 semitone (only when on)
  //   Touch: a quick touch-drag scrolls the pattern; a 200ms long-press arms a
  //   2D paint gesture — horizontal sweep toggles pads, vertical movement shifts
  //   the pitch of every pad touched so far (see `tonePaint`).
  attachToneCellHandlers(cell: HTMLDivElement, dot: HTMLDivElement, ti: number, s: number): void {
    const track = this.seq.tracks[ti];
    this.toneCellInfo.set(cell, { ti, s, dot });
    const PX_PER_SEMITONE = TONE_PX_PER_SEMITONE;

    const onPointerDownMouse = (e: PointerEvent, skipLongPress = false) => {
      if (e.button > 0) return; // ignore right/middle click
      e.preventDefault();
      try { cell.setPointerCapture(e.pointerId); } catch { /* synthetic events / unsupported */ }
      const startX = e.clientX;
      const startY = e.clientY;
      const wasOn = track.steps[s];
      const startPitch = track.pitches?.[s] ?? DEFAULT_PITCH;
      const LONGPRESS_MS = 200;
      const ARM_MOVE_TOL = 6; // px — movement past this cancels the long-press
      let dragging = skipLongPress && wasOn; // committed to single-pad pitch drag
      let armed = false;    // committed to the 2D multi-pad sweep (long-press)
      let lastPreviewPitch = startPitch;
      let previewing = false;
      // Show the loupe immediately on press of an active cell so the user
      // sees the current pitch before they start dragging.
      if (wasOn) this.showLoupe(startPitch, cell);
      // Start a sustained preview at the current pitch the moment the user
      // presses an active cell — it holds until pointerup/cancel.
      if (wasOn && this.audioReady) {
        this.previewTonePitch(track.voice, startPitch + (track.pitchOffset ?? 0), true);
        previewing = true;
      }

      // Hold still ~200ms (mouse/pen) → arm the same horizontal drag-select +
      // vertical pitch-shift gesture that touch long-press gives. The document
      // handlers from setupTonePaint take over once armed.
      const timer = skipLongPress ? 0 : window.setTimeout(() => {
        if (dragging) return;
        armed = true;
        if (previewing) { this.stopTonePreview(track.voice); previewing = false; }
        this.hideLoupe();
        cell.removeEventListener('pointermove', onMove);
        cell.removeEventListener('pointerup', onUp);
        cell.removeEventListener('pointercancel', onUp);
        try { cell.releasePointerCapture(e.pointerId); } catch { /* not captured */ }
        this.beginTonePaint(cell, ti, s, startX, startY);
      }, LONGPRESS_MS);

      const onMove = (ev: PointerEvent) => {
        if (armed) return; // setupTonePaint's document handlers drive it now
        const dy = ev.clientY - startY;
        if (!dragging) {
          // Any real movement before the long-press fires → this is a
          // single-pad pitch drag (or a plain click, on an inactive cell).
          if (Math.hypot(ev.clientX - startX, dy) <= ARM_MOVE_TOL) return;
          clearTimeout(timer);
          if (!wasOn) return; // inactive cell: no pitch drag — wait for click toggle
          dragging = true;
          if (!toneHintSeen()) markToneHintSeen();
        }
        if (!dragging) return;
        // up = higher pitch
        const semis = -Math.round(dy / PX_PER_SEMITONE);
        const raw = Math.max(MIN_PITCH, Math.min(MAX_PITCH, startPitch + semis));
        const next = snapToScale(raw, this.scale);
        if (track.pitches) track.pitches[s] = next;
        this.updatePitchDot(dot, next);
        this.updateLoupe(next);
        // Audible preview of the pitch under the loupe — a brief pluck so the
        // user hears what they're dialing in. Only when it actually changes.
        if (next !== lastPreviewPitch) {
          lastPreviewPitch = next;
          this.previewTonePitch(track.voice, next + (track.pitchOffset ?? 0), true);
          previewing = true;
        }
      };

      const onUp = (ev: PointerEvent) => {
        clearTimeout(timer);
        if (armed) return; // gesture handed off — setupTonePaint's end() commits
        try { cell.releasePointerCapture(e.pointerId); } catch { /* not captured */ }
        cell.removeEventListener('pointermove', onMove);
        cell.removeEventListener('pointerup', onUp);
        cell.removeEventListener('pointercancel', onUp);
        if (previewing) { this.stopTonePreview(track.voice); previewing = false; }
        this.hideLoupe();
        if (!dragging) {
          // click → toggle
          const on = this.seq.toggle(ti, s);
          if (on && track.pitches) {
            // inherit pitch of the previous active step (any track wraparound)
            track.pitches[s] = snapToScale(this.findPrevPitch(ti, s), this.scale);
            this.updatePitchDot(dot, track.pitches[s]);
          }
          cell.classList.toggle('on', on);
          this.setStepOwner(ti, s, null); // it's mine now
          if (on && this.audioReady) {
            const now = Tone.now();
            track.voice.trigger(now, 1, (track.pitches?.[s] ?? DEFAULT_PITCH) + (track.pitchOffset ?? 0), { releaseAt: now + 0.15 });
          }
          this.broadcast({ type: 'step', ti, s, on, pitch: track.pitches?.[s] });
          this.pushHash();
          // suppress click event
          ev.preventDefault();
          ev.stopPropagation();
        } else {
          // Pitch drag commit — broadcast the final pitch.
          if (track.pitches) this.broadcast({ type: 'pitch', ti, s, pitch: track.pitches[s] });
          this.pushHash();
        }
      };

      cell.addEventListener('pointermove', onMove);
      cell.addEventListener('pointerup', onUp);
      cell.addEventListener('pointercancel', onUp);
    };

    // Touch: tone rows trade away vertical page scroll for a responsive
    // single-pad pitch drag. The first significant finger movement decides:
    //   - primarily vertical (>8px, dy > dx) → hand off to the mouse/pen
    //     pitch-drag path immediately (single-pad ns-resize behavior)
    //   - horizontal / ~50-50 → fall through to the existing 200ms long-press
    //     gate: a quick drag scrolls the pattern, a held press arms the 2D
    //     multi-select sweep (document handlers take over)
    //   - no movement before 200ms while still holding → arm multi-select too
    // Finger lifts before any of that = tap toggle.
    const onPointerDownTouch = (e: PointerEvent) => {
      e.preventDefault();
      const LONGPRESS_MS = 200;
      const MOVE_TOL = 10; // px — horizontal scroll trip distance
      const DIR_TOL = 8; // px — first movement past this decides direction
      const rowsScroll = cell.closest('.rows-scroll') as HTMLElement | null;
      const startX = e.clientX;
      const startYPx = e.clientY;
      let lastX = startX;
      let lastY = e.clientY;
      let scrolling = false;
      let armed = false;
      let delegated = false; // handed off to the single-pad pitch-drag path

      const timer = window.setTimeout(() => {
        if (scrolling || delegated) return;
        // If the finger has already moved primarily vertically, stay in single-pad
        // pitch drag rather than arming multi-select (which would toggle the cell off).
        const dx = Math.abs(lastX - startX);
        const dy = Math.abs(lastY - startYPx);
        if (track.steps[s] && dy > 4 && dy > dx) {
          delegated = true;
          cell.removeEventListener('pointermove', onMove);
          cell.removeEventListener('pointerup', onUp);
          cell.removeEventListener('pointercancel', onUp);
          onPointerDownMouse(e, true);
          return;
        }
        armed = true;
        this.beginTonePaint(cell, ti, s, lastX, lastY);
      }, LONGPRESS_MS);

      const onMove = (ev: PointerEvent): void => {
        lastY = ev.clientY;
        if (armed || delegated) return; // document pointermove drives the gesture now
        const dx = Math.abs(ev.clientX - startX);
        const dy = Math.abs(ev.clientY - startYPx);
        if (!scrolling && Math.hypot(dx, dy) > DIR_TOL && dy > dx) {
          // Vertical-first → behave exactly like a mouse/pen press: single-pad
          // pitch drag (or, on an inactive pad, a deferred toggle on release).
          delegated = true;
          clearTimeout(timer);
          cell.removeEventListener('pointermove', onMove);
          cell.removeEventListener('pointerup', onUp);
          cell.removeEventListener('pointercancel', onUp);
          onPointerDownMouse(e, true); // already moved — skip the long-press arm
          return;
        }
        if (!scrolling && Math.hypot(dx, dy) > MOVE_TOL) {
          scrolling = true;
          clearTimeout(timer);
        }
        if (scrolling && rowsScroll) rowsScroll.scrollLeft -= ev.clientX - lastX;
        lastX = ev.clientX;
      };
      const onUp = (): void => {
        cell.removeEventListener('pointermove', onMove);
        cell.removeEventListener('pointerup', onUp);
        cell.removeEventListener('pointercancel', onUp);
        clearTimeout(timer);
        if (armed || delegated) return; // commit handled elsewhere
        if (!scrolling) {
          // Quick tap → toggle this cell (inheriting the previous active pitch).
          const on = this.seq.toggle(ti, s);
          if (on && track.pitches) {
            track.pitches[s] = snapToScale(this.findPrevPitch(ti, s), this.scale);
            this.updatePitchDot(dot, track.pitches[s]);
          }
          cell.classList.toggle('on', on);
          this.setStepOwner(ti, s, null);
          if (on && this.audioReady) {
            const now = Tone.now();
            track.voice.trigger(now, 1, (track.pitches?.[s] ?? DEFAULT_PITCH) + (track.pitchOffset ?? 0), { releaseAt: now + 0.15 });
          }
          this.broadcast({ type: 'step', ti, s, on, pitch: track.pitches?.[s] });
          this.pushHash();
        }
      };
      cell.addEventListener('pointermove', onMove);
      cell.addEventListener('pointerup', onUp);
      cell.addEventListener('pointercancel', onUp);
    };

    cell.addEventListener('pointerdown', (e: PointerEvent) => {
      if (e.pointerType === 'touch') onPointerDownTouch(e);
      else onPointerDownMouse(e);
    });

    cell.addEventListener('wheel', (e) => {
      if (!track.steps[s] || !track.pitches) return;
      e.preventDefault();
      const delta = e.deltaY > 0 ? -1 : 1;
      const raw = Math.max(MIN_PITCH, Math.min(MAX_PITCH, track.pitches[s] + delta));
      const next = snapToScale(raw, this.scale);
      track.pitches[s] = next;
      this.updatePitchDot(dot, next);
      this.broadcast({ type: 'pitch', ti, s, pitch: next });
      this.pushHash();
    }, { passive: false });
  }

  // Begin a 2D tone-paint gesture anchored on `cell` (touch long-press). The
  // anchor pad always toggles; subsequent pads under the sweeping finger toggle
  // to the same target state. Vertical finger travel from `startY` shifts the
  // pitch of every pad in the gesture (relative to each pad's pre-gesture pitch).
  private beginTonePaint(startCell: HTMLElement, ti: number, s: number, startX: number, startY: number): void {
    const track = this.seq.tracks[ti];
    const turnOn = !track.steps[s];
    const startBasePitch = track.pitches?.[s] ?? DEFAULT_PITCH;
    this.tonePaint = { ti, turnOn, startX, startY, startCell, startBasePitch, horizIntent: false, semis: 0, track, order: [], lastPreviewPitch: -1, changed: false };
    if (!toneHintSeen()) markToneHintSeen();
    // Starting cell painted on first pointermove (deferred), not here.
    // Long press + release with no drag → nothing toggled.
  }

  // Handle the gesture's finger arriving over `cell`:
  //   - frontier pad (last touched): nothing to do
  //   - a pad already in the gesture: backtrack — revert every pad touched after
  //     it (toggle back, restore its pre-gesture pitch)
  //   - a fresh pad on the same row: toggle it to the target state, record its
  //     pre-gesture pitch as `basePitch`, and apply the current pitch shift
  private toneOnto(cell: HTMLElement): void {
    const tp = this.tonePaint;
    if (!tp) return;
    const info = this.toneCellInfo.get(cell);
    if (!info || info.ti !== tp.ti) return; // no row-bleed
    const idx = tp.order.findIndex((e) => e.s === info.s);
    if (tp.order.length > 0 && idx === tp.order.length - 1) return; // already the frontier
    const track = tp.track;
    if (idx === -1) {
      if (track.steps[info.s] === tp.turnOn) return; // no state change → don't enroll
      const basePitch = tp.turnOn
        ? (track.pitches ? snapToScale(this.findPrevPitch(tp.ti, info.s), this.scale) : DEFAULT_PITCH)
        : (track.pitches?.[info.s] ?? DEFAULT_PITCH);
      track.steps[info.s] = tp.turnOn;
      cell.classList.toggle('on', tp.turnOn);
      this.setStepOwner(tp.ti, info.s, null); // it's mine now
      const entry = { s: info.s, cell, dot: info.dot, basePitch };
      tp.order.push(entry);
      this.applyTonePaintEntry(entry);
      tp.changed = true;
      this.broadcast({ type: 'step', ti: tp.ti, s: info.s, on: tp.turnOn, pitch: track.pitches?.[info.s] });
      if (tp.turnOn) {
        const p = (track.pitches?.[info.s] ?? DEFAULT_PITCH) + (track.pitchOffset ?? 0);
        this.previewTonePitch(track.voice, p, false);
        tp.lastPreviewPitch = p;
        this.showLoupe(track.pitches?.[info.s] ?? DEFAULT_PITCH, cell);
      }
      return;
    }
    for (let i = tp.order.length - 1; i > idx; i--) {
      const e = tp.order[i];
      track.steps[e.s] = !tp.turnOn;
      e.cell.classList.toggle('on', !tp.turnOn);
      this.setStepOwner(tp.ti, e.s, null);
      if (track.pitches) track.pitches[e.s] = e.basePitch;
      this.updatePitchDot(e.dot, e.basePitch);
      this.broadcast({ type: 'step', ti: tp.ti, s: e.s, on: !tp.turnOn, pitch: track.pitches?.[e.s] });
    }
    tp.order.length = idx + 1;
  }

  // Apply the gesture's current semitone shift to one enrolled pad.
  private applyTonePaintEntry(entry: { s: number; dot: HTMLDivElement; basePitch: number }): void {
    const tp = this.tonePaint;
    if (!tp) return;
    const raw = Math.max(MIN_PITCH, Math.min(MAX_PITCH, entry.basePitch + tp.semis));
    const next = snapToScale(raw, this.scale);
    if (tp.track.pitches) tp.track.pitches[entry.s] = next;
    this.updatePitchDot(entry.dot, next);
  }

  // Re-apply the gesture's pitch shift to every pad touched so far. Called on
  // each vertical move so the whole run tracks the finger live.
  private applyTonePaintPitch(): void {
    const tp = this.tonePaint;
    if (!tp) return;
    for (const e of tp.order) this.applyTonePaintEntry(e);
  }

  // Document-level handlers that drive the 2D tone-paint gesture. On touch the
  // anchor cell keeps implicit pointer capture, so we hit-test with
  // elementFromPoint for the horizontal sweep; the same move carries the live
  // vertical pitch shift. pointerup/cancel commits one hash push for the gesture.
  private setupTonePaint(): void {
    document.addEventListener('pointermove', (e: PointerEvent) => {
      const tp = this.tonePaint;
      if (!tp) return;
      // Gate multi-select enrollment behind confirmed horizontal intent so that
      // a vertical-first armed gesture adjusts pitch without toggling the cell off.
      const horizDx = Math.abs(e.clientX - tp.startX);
      if (!tp.horizIntent && horizDx > 12) tp.horizIntent = true;
      if (tp.horizIntent || tp.order.length > 0) {
        const target = document.elementFromPoint(e.clientX, e.clientY);
        const stepEl = target?.closest('.step');
        if (stepEl instanceof HTMLElement) this.toneOnto(stepEl);
      }
      const newSemis = -Math.round((e.clientY - tp.startY) / TONE_PX_PER_SEMITONE);
      if (newSemis !== tp.semis) {
        tp.semis = newSemis;
        if (tp.order.length > 0) {
          this.applyTonePaintPitch();
          const f = tp.order[tp.order.length - 1];
          if (f && this.tonePaint) {
            const pitch = tp.track.pitches?.[f.s] ?? DEFAULT_PITCH;
            this.showLoupe(pitch, f.cell);
            const p = pitch + (tp.track.pitchOffset ?? 0);
            if (p !== tp.lastPreviewPitch) {
              tp.lastPreviewPitch = p;
              this.previewTonePitch(tp.track.voice, p, false);
            }
          }
        } else {
          // Vertical-only armed gesture: adjust starting cell's pitch directly without toggling.
          const info = this.toneCellInfo.get(tp.startCell);
          if (info && tp.track.steps[info.s] && tp.track.pitches) {
            const raw = Math.max(MIN_PITCH, Math.min(MAX_PITCH, tp.startBasePitch + newSemis));
            const next = snapToScale(raw, this.scale);
            tp.track.pitches[info.s] = next;
            this.updatePitchDot(info.dot, next);
            this.showLoupe(next, tp.startCell);
            const p = next + (tp.track.pitchOffset ?? 0);
            if (p !== tp.lastPreviewPitch) {
              tp.lastPreviewPitch = p;
              this.previewTonePitch(tp.track.voice, p, false);
            }
          }
        }
      }
    });
    const end = (): void => {
      const tp = this.tonePaint;
      if (!tp) return;
      this.tonePaint = null;
      this.hideLoupe();
      if (tp.changed) {
        for (const e of tp.order) {
          if (tp.track.pitches) this.broadcast({ type: 'pitch', ti: tp.ti, s: e.s, pitch: tp.track.pitches[e.s] });
        }
        this.pushHash();
      } else if (tp.order.length === 0 && tp.semis !== 0) {
        // Vertical-only armed gesture: pitch was adjusted on starting cell without toggling.
        const info = this.toneCellInfo.get(tp.startCell);
        if (info && tp.track.pitches) {
          this.broadcast({ type: 'pitch', ti: tp.ti, s: info.s, pitch: tp.track.pitches[info.s] });
          this.pushHash();
        }
      }
    };
    document.addEventListener('pointerup', end);
    document.addEventListener('pointercancel', end);
  }

  findPrevPitch(ti: number, fromStep: number): number {
    const track = this.seq.tracks[ti];
    if (!track.pitches) return DEFAULT_PITCH;
    // search backward, wrapping around
    const n = track.steps.length;
    for (let i = 1; i < n; i++) {
      const j = (fromStep - i + n) % n;
      if (track.steps[j]) return track.pitches[j];
    }
    return DEFAULT_PITCH;
  }

  updatePitchDot(dot: HTMLDivElement, midi: number): void {
    const t = (midi - MIN_PITCH) / (MAX_PITCH - MIN_PITCH);
    // top: 0% = highest, 100% = lowest. Pad 8% on each side so dot stays in cell.
    const top = (1 - t) * 84 + 8;
    dot.style.top = `${top}%`;
    dot.title = midiName(midi);
  }

  // The desktop vertical bars bar shows "+ 2 BARS"; the mobile "+N" chip mirrors
  // it in compact form. Both cycle 1 → 2 → 4 on tap.
  renderBarsLabel(): void {
    const bars = this.seq.numSteps / DEFAULT_STEPS;
    this.barsBtn.textContent = `+ ${bars} BAR${bars > 1 ? 'S' : ''}`;
    this.barsBtn.setAttribute('aria-label', `${bars} bar${bars > 1 ? 's' : ''} — tap to cycle 1, 2, 4`);
    if (this.barsChipBtn) {
      this.barsChipBtn.innerHTML = barsChipMarkup(bars);
      this.barsChipBtn.setAttribute('aria-label', `${bars} bar${bars > 1 ? 's' : ''} — tap to cycle 1, 2, 4`);
      this.barsChipBtn.setAttribute('data-tip', 'Cycle bars: 1 → 2 → 4');
    }
  }

  // Shared click target for both the desktop vertical bars bar and the mobile
  // "+N" chip in the SOURCE bar — same behaviour, two entry points.
  handleBarsClick(): void {
    // 4→1 drops bars 2-4 — destructive. Confirm first.
    if (this.seq.numSteps === MAX_STEPS) {
      openConfirmModal({
        title: 'Drop to 1 bar?',
        message: 'Bars 2–4 will be discarded.',
        confirmLabel: 'Drop bars',
        onConfirm: () => this.cycleBars(),
      });
    } else {
      this.cycleBars();
    }
  }

  cycleBars(): void {
    const cur = this.seq.numSteps;
    let next = cur * 2;
    if (next > MAX_STEPS) next = DEFAULT_STEPS; // cycle 1→2→4→1
    this.seq.resize(next);
    if (this.seq.isPlaying()) this.setPlayUI(true); // resize() restarted the sequence
    this.renderGrid();
    this.renderBarsLabel();
    this.broadcastFull();
    this.pushHash();
  }

  // Helper: broadcast the entire current state as one `full` delta. Used
  // when a single mutation has whole-grid blast radius (bars cycle, clear,
  // preset/inspire load, undo, redo).
  private broadcastFull(): void {
    if (this.collabApplyingRemote) return;
    if (!this.collab.isConnected()) return;
    this.collab.send({ type: 'full', hash: this.currentHash() });
  }

  applyVolumes(): void {
    this.seq.tracks.forEach((t, i) => {
      const k = this.currentTrackDefs[i]?.key ?? '';
      const v = this.volumes[k] ?? DEFAULT_VOLUMES[k] ?? 0.75;
      t.voice.setVolume(v);
    });
  }

  loadFromHash(h: NonNullable<ReturnType<typeof parseHash>>): void {
    const wasPlaying = this.seq.isPlaying();
    this.seq.tracks[TONE_TRACK_IDX]?.voice.release?.();
    if (wasPlaying) {
      this.seq.stop();
      this.setPlayUI(false);
    }
    // Organic mode needs the CDN buffers — kick the fetch off (fire-and-forget;
    // the deferredVoice wrapper makes pre-load triggers no-ops).
    if (h.audioSource === 'organic' || h.toneSource === 'organic') void ensureSamplesLoaded();
    const prevSource = this.audioSource;
    const prevToneSource = this.toneSource;
    const prevTimbre = this.toneTimbre;
    this.audioSource = h.audioSource;
    this.applySourceAudibility();
    this.toneSource = h.toneSource;
    this.toneSourceLocked = h.toneSourceLocked;
    this.toneTimbre = h.toneTimbre;
    this.scale = h.scale; // adopt without snapping — incoming pitches are authoritative
    this.kick808Decay = h.kickDecay;
    this.drumDecay = { s: h.snareDecay, h: h.hatDecay };
    this.toneOctaveOffset = h.toneOctaveOffset;
    // Glide is no longer user-tunable — ignore any value carried by older
    // hashes / inbound sync messages and pin to the default.
    this.toneGlide = DEFAULT_GLIDE;
    this.currentBpm = h.bpm;
    this.bpmSlider.value = String(h.bpm);
    this.bpmDisplay.textContent = String(h.bpm);
    this.seq.setBpm(h.bpm);
    this.volumes = { ...h.volumes };
    this.seq.resize(h.numSteps);
    const engineChanged = prevSource !== this.audioSource;
    const toneEngineChanged = prevToneSource !== this.toneSource;
    this.seq.tracks.forEach((track, ti) => {
      const def = this.currentTrackDefs[ti];
      const timbreChanged = !!def.pitched && prevTimbre !== this.toneTimbre;
      const sourceChanged = def.pitched ? toneEngineChanged : engineChanged;
      if (!def.only808 && (sourceChanged || timbreChanged)) {
        const oldVoice = track.voice;
        oldVoice.release?.();
        const srcForVoice: AudioSource = def.pitched ? this.toneSource : this.audioSource;
        track.voice = def.voice(srcForVoice, this.toneTimbre);
        setTimeout(() => oldVoice.dispose?.(), VOICE_DISPOSE_DELAY_MS);
      }
      const steps = new Array(h.numSteps).fill(false);
      for (const i of h.patterns[ti] ?? []) if (i < h.numSteps) steps[i] = true;
      track.steps = steps;
      if (track.pitches) {
        track.pitches = new Array(h.numSteps).fill(DEFAULT_PITCH);
        for (const [s, p] of (h.pitches[ti] ?? new Map<number, number>())) if (s < h.numSteps) track.pitches[s] = p;
      }
    });
    this.renderGrid();
    this.applyVolumes();
    this.applyKickDecay();
    this.applyGlide();
    this.applyDrumDecay();
    this.applyToneOctave();
    this.renderBarsLabel();
    this.broadcastFull();
    this.pushHash();
    if (wasPlaying) {
      this.seq.start();
      this.setPlayUI(true);
    }
  }

  loadPreset(preset: Preset): void {
    const wasPlaying = this.seq.isPlaying();
    this.seq.tracks[TONE_TRACK_IDX]?.voice.release?.();
    if (wasPlaying) {
      this.seq.stop();
      this.setPlayUI(false);
    }
    // Swap tone voice if the preset specifies one (e.g. Bass for Billie Jean).
    // Suppress undo recording across the swap — the final pushHash() below
    // captures the complete preset state as a single undo entry.
    const wasApplying = this.isApplyingHistory;
    this.isApplyingHistory = true;
    if (preset.toneTimbre && preset.toneTimbre !== this.toneTimbre) {
      this.swapToneTimbre(preset.toneTimbre);
    }
    this.isApplyingHistory = wasApplying;
    const presetSteps = (preset.bars ?? 1) * DEFAULT_STEPS;
    this.seq.resize(presetSteps);
    // Apply across all 8 tracks (patterns may be a short array — the extra
    // TR-808 rows get cleared rather than left holding the previous beat).
    this.seq.tracks.forEach((track, ti) => {
      const pat = preset.patterns[ti] ?? [];
      const steps = new Array(presetSteps).fill(false);
      for (const i of pat) if (i < presetSteps) steps[i] = true;
      track.steps = steps;
      if (track.pitches) {
        track.pitches = new Array(presetSteps).fill(DEFAULT_PITCH);
        if (preset.tonePitches) {
          for (const [s, p] of Object.entries(preset.tonePitches)) {
            const idx = Number(s);
            if (idx < presetSteps) track.pitches[idx] = p;
          }
        }
      }
    });
    this.currentBpm = preset.bpm;
    this.bpmSlider.value = String(preset.bpm);
    this.bpmDisplay.textContent = String(preset.bpm);
    this.seq.setBpm(preset.bpm);
    // Preserve the user's current mix across preset/Inspire loads — only the
    // pattern, pitches, voice, and BPM should change.
    this.renderGrid();
    this.applyVolumes();
    this.renderBarsLabel();
    this.broadcastFull();
    this.pushHash();
    // Always start playing when a preset is loaded — user intent is to hear it.
    if (!this.seq.isPlaying()) {
      if (this.audioReady) {
        this.seq.start();
        this.setPlayUI(true);
      }
    }
  }

  // Pull a random beat from the Inspire pool (named presets + generated
  // grooves) and load it. Adds a brief shake/sparkle animation on the
  // triggering button so the action feels playful.
  loadInspire(btn?: HTMLElement): void {
    const { preset, generatorType, presetName } = pickInspirePattern();
    this.loadPreset(preset);
    track('inspire_used', generatorType ? { generator_type: generatorType } : { preset_name: presetName });
    if (btn) {
      btn.classList.remove('sparking');
      // Force reflow so the class re-add re-triggers the animation.
      void (btn as HTMLElement).offsetWidth;
      btn.classList.add('sparking');
      setTimeout(() => btn.classList.remove('sparking'), SPARK_FLASH_MS);
    }
  }

  // ==== JSON export / import ====
  // Export bundles ALL saved beats (each encoded as a URL hash carrying bpm,
  // bars, every track's steps, tone pitches, volumes, source, voice, scale,
  // kick decay, snare/hat decay and octave) into a portable JSON file. Import
  // reads such a bundle and additively merges its beats into the saved list,
  // deduping by hash. Neither touches the live beat being edited.
  exportBeatJson(): void {
    const saved = loadSavedBeats();
    if (saved.length === 0) {
      this.showToast('No saved beats to export', true);
      return;
    }
    const data = {
      format: 'beatswarm-bundle',
      version: 1,
      exportedAt: new Date().toISOString(),
      beats: saved.map((b) => ({
        name: b.label ?? '',
        savedAt: new Date(b.ts).toISOString(),
        hash: b.hash,
      })),
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `beatswarm-beats-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  importBeatJson(input: HTMLInputElement): void {
    const file = input.files?.[0];
    input.value = ''; // allow re-selecting the same file later
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const data = JSON.parse(String(reader.result)) as { beats?: unknown };
        const incoming = Array.isArray(data?.beats) ? data.beats : null;
        if (!incoming) throw new Error('not a beatswarm bundle');

        const existing = loadSavedBeats();
        const seen = new Set(existing.map((b) => b.hash));
        const merged: SavedBeat[] = [...existing];
        let added = 0;
        let skipped = 0;

        for (const entry of incoming) {
          const e = entry as { name?: unknown; savedAt?: unknown; hash?: unknown };
          const rawHash = typeof e?.hash === 'string' ? e.hash : null;
          if (!rawHash) { skipped++; continue; }
          const hash = rawHash.startsWith('#') ? rawHash : `#${rawHash}`;
          if (!parseHash(hash)) { skipped++; continue; }
          if (seen.has(hash)) { skipped++; continue; }
          seen.add(hash);
          const parsedTs = typeof e?.savedAt === 'string' ? Date.parse(e.savedAt) : NaN;
          const ts = Number.isFinite(parsedTs) ? parsedTs : Date.now();
          const url = window.location.origin + window.location.pathname + hash;
          const beat: SavedBeat = { url, hash, ts };
          const name = typeof e?.name === 'string' ? e.name.trim().slice(0, 30) : '';
          if (name) beat.label = name;
          merged.push(beat);
          added++;
        }

        merged.sort((a, b) => b.ts - a.ts);
        persistSavedBeats(merged.slice(0, SAVED_MAX));
        this.renderSavedBeats();

        if (added === 0 && skipped === 0) {
          this.showToast('No beats in that file', true);
          return;
        }
        const noun = (n: number) => `${n} beat${n === 1 ? '' : 's'}`;
        this.showToast(
          skipped > 0
            ? `Imported ${noun(added)}, ${skipped} skipped (duplicate)`
            : `Imported ${noun(added)}`,
        );
      } catch (err) {
        console.error('Beat import failed', err);
        this.showToast('Could not import that file', true);
      }
    };
    reader.onerror = () => this.showToast('Could not read that file', true);
    reader.readAsText(file);
  }

  pushHash(): void {
    const hash = this.currentHash();
    history.replaceState(null, '', this.withRoom(hash));
    this.recordHistory(hash);
    this.scheduleShortLinkPrime();
  }

  // Live URL update without snapshotting history. Used by slider 'input'
  // handlers (BPM, volume) so a drag doesn't fill the undo stack with every
  // intermediate value — the slider's 'change' handler calls pushHash() once
  // when the user releases.
  pushHashLive(): void {
    history.replaceState(null, '', this.withRoom(this.currentHash()));
  }

  // ==== Collab — outbound deltas ====
  // Tiny helper so every mutation site reads the same. Suppressed while a
  // remote delta is being applied locally (echo-loop guard).
  private broadcast(delta: Delta): void {
    if (this.collabApplyingRemote) return;
    this.collab.send(delta);
  }

  // ==== Collab — inbound deltas ====
  // Applies a delta from a remote peer to local state, animates the affected
  // UI with the peer's color, and avoids echoing back via the `collabApplyingRemote`
  // guard which mutation paths check before broadcasting.
  applyRemoteDelta(delta: Delta, from: Peer): void {
    this.collabApplyingRemote = true;
    try {
      switch (delta.type) {
        case 'step': {
          const track = this.seq.tracks[delta.ti];
          if (!track) break;
          if (delta.s < 0 || delta.s >= track.steps.length) break;
          track.steps[delta.s] = delta.on;
          let pitch: number | null = null;
          if (typeof delta.pitch === 'number' && isFinite(delta.pitch) && track.pitches) {
            pitch = Math.max(MIN_PITCH, Math.min(MAX_PITCH, Math.round(delta.pitch)));
            track.pitches[delta.s] = pitch;
          }
          // Attribute the step to the peer who turned it on (tints the cell);
          // clear ownership when they turn it off.
          this.setStepOwner(delta.ti, delta.s, delta.on ? from.color : null);
          const cell = this.stepCells[delta.ti]?.[delta.s];
          if (cell) {
            cell.classList.toggle('on', delta.on);
            const dot = this.pitchDots[delta.ti]?.[delta.s];
            if (dot && pitch != null) this.updatePitchDot(dot, pitch);
            this.flashRemote(cell, from.color);
          }
          this.pushHash();
          break;
        }
        case 'pitch': {
          const track = this.seq.tracks[delta.ti];
          if (!track || !track.pitches) break;
          if (delta.s < 0 || delta.s >= track.pitches.length) break;
          if (!isFinite(delta.pitch)) break;
          const pitch = Math.max(MIN_PITCH, Math.min(MAX_PITCH, Math.round(delta.pitch)));
          track.pitches[delta.s] = pitch;
          const dot = this.pitchDots[delta.ti]?.[delta.s];
          if (dot) this.updatePitchDot(dot, pitch);
          const cell = this.stepCells[delta.ti]?.[delta.s];
          if (cell) this.flashRemote(cell, from.color);
          this.pushHash();
          break;
        }
        case 'bpm': {
          const bpm = clampBpm(delta.bpm);
          if (bpm == null) break;
          this.currentBpm = bpm;
          this.bpmSlider.value = String(bpm);
          this.bpmDisplay.textContent = String(bpm);
          this.seq.setBpm(bpm);
          this.flashRemote(this.bpmSlider.parentElement as HTMLElement | null, from.color);
          this.pushHash();
          break;
        }
        case 'voice': {
          if (!TONE_TIMBRE_IDS.has(delta.timbre as ToneTimbre)) break;
          this.swapToneTimbre(delta.timbre as ToneTimbre);
          if (this.toneTimbreSelect) this.flashRemote(this.toneTimbreSelect, from.color);
          break;
        }
        case 'scale': {
          if (!SCALE_IDS.has(delta.scale)) break;
          this.setScale(delta.scale as ScaleId);
          if (this.scaleSelect) this.flashRemote(this.scaleSelect, from.color);
          break;
        }
        case 'tonesource': {
          if (!TONE_SOURCE_IDS.has(delta.source)) break;
          // Remote peer manually chose a tone source — adopt + lock locally.
          // `silent` because swapToneSource is async (await samples) and the
          // collabApplyingRemote echo guard doesn't survive the await.
          void this.swapToneSource(delta.source as ToneSource, true);
          if (this.toneSourceSelect) this.flashRemote(this.toneSourceSelect, from.color);
          break;
        }
        case 'volume': {
          if (!isFinite(delta.v)) break;
          const idx = this.currentTrackDefs.findIndex(d => d.key === delta.key);
          if (idx < 0) break;
          const v = Math.max(VOL_MIN, Math.min(VOL_MAX, delta.v));
          this.volumes[delta.key] = v;
          this.seq.tracks[idx].voice.setVolume(v);
          const slider = this.volSliders[idx];
          if (slider) {
            slider.value = String(v);
            this.flashRemote(slider.closest('.vol') as HTMLElement | null, from.color);
          }
          this.pushHash();
          break;
        }
        case 'decay': {
          this.kick808Decay = clampKickDecay(delta.d);
          this.applyKickDecay();
          if (this.decaySlider) {
            this.decaySlider.value = String(this.kick808Decay);
            this.flashRemote(this.decaySlider.closest('.vol') as HTMLElement | null, from.color);
          }
          this.pushHash();
          break;
        }
        case 'drumdecay': {
          const k = delta.key === 's' || delta.key === 'h' ? delta.key : null;
          if (k && isFinite(delta.d)) {
            const cfg = k === 's' ? SNARE_DECAY : HAT_DECAY;
            this.drumDecay[k] = Math.max(cfg.min, Math.min(cfg.max, delta.d));
            this.applyDrumDecay();
            const sl = this.drumDecaySliders[k];
            if (sl) {
              sl.value = String(this.drumDecay[k]);
              this.flashRemote(sl.closest('.vol') as HTMLElement | null, from.color);
            }
            this.pushHash();
          }
          break;
        }
        case 'octave': {
          this.toneOctaveOffset = clampOctaveSemis(delta.semis);
          this.applyToneOctave();
          if (this.octaveSlider) {
            this.octaveSlider.value = String(this.toneOctaveOffset);
            this.flashRemote(this.octaveSlider.closest('.vol') as HTMLElement | null, from.color);
          }
          this.pushHash();
          break;
        }
        case 'glide': {
          // Glide is no longer user-tunable; ignore remote tweaks but keep the
          // message in the wire protocol for backward compatibility with peers
          // on older builds.
          break;
        }
        case 'play': {
          this.peerPlaying.set(from.id, delta.playing);
          this.refreshPresencePlaying();
          break;
        }
        case 'name': {
          const n = String(delta.name ?? '').trim().slice(0, DISPLAY_NAME_MAX);
          if (n) this.peerNames.set(from.id, n);
          else this.peerNames.delete(from.id);
          // Fresh joiner whose name we were waiting on — throw the confetti now,
          // with their actual name rather than "Someone".
          if (this.pendingEntryCelebration.has(from.id)) {
            this.pendingEntryCelebration.delete(from.id);
            this.celebrateEntry(n || 'Someone', from.color);
          }
          this.renderPresenceRow(this.collab.me, this.collab.others);
          break;
        }
        case 'full': {
          this.applyFullHash(delta.hash);
          break;
        }
      }
    } finally {
      this.collabApplyingRemote = false;
    }
  }

  applyFullHash(hash: string): void {
    const parsed = parseHash(hash);
    if (!parsed) return;
    this.collabApplyingRemote = true;
    try {
      this.loadFromHash(parsed);
    } finally {
      this.collabApplyingRemote = false;
    }
  }

  // Brief colored ripple on a UI element when a remote peer edits it. Adds a
  // `.remote-edit` class + sets --remote-color so a CSS keyframe handles the
  // visual; auto-clears after the animation finishes.
  flashRemote(target: HTMLElement | null, color: string): void {
    if (!target) return;
    target.style.setProperty('--remote-color', color);
    // Reset any in-flight animation so back-to-back edits both ripple.
    target.classList.remove('remote-edit');
    void target.offsetWidth;
    target.classList.add('remote-edit');
    window.setTimeout(() => target.classList.remove('remote-edit'), REMOTE_EDIT_FLASH_MS);
  }

  // Collab button state. Idle = blue outline; while a live jam is active it
  // goes solid blue and pulses (the .active class drives the CSS animation)
  // and the tooltip flips to "stop" — clicking it again ends the jam.
  refreshCollabBtn(): void {
    if (!this.collabBtn) return;
    const inRoom = !!this.collab.roomId;
    this.collabBtn.classList.toggle('active', inRoom);
    this.collabBtn.innerHTML = ICON_COLLAB;
    this.collabBtn.dataset.tip = inRoom ? 'Stop live jam' : 'Start a live jam';
    this.collabBtn.setAttribute('aria-label', inRoom ? 'Stop the live jam' : 'Start a live collaboration session');
  }

  // Set (or clear, with color=null) the collab owner of a step. Updates the
  // ownership map and re-tints the cell. null = local user's step (default
  // orange) or empty.
  setStepOwner(ti: number, s: number, color: string | null): void {
    const arr = this.stepOwners[ti];
    if (arr && s >= 0 && s < arr.length) arr[s] = color;
    const cell = this.stepCells[ti]?.[s];
    if (!cell) return;
    if (color) {
      cell.style.setProperty('--owner-color', color);
      cell.classList.add('owned');
    } else {
      cell.style.removeProperty('--owner-color');
      cell.classList.remove('owned');
    }
  }

  // Instrument-label interaction: a quick tap toggles the row's monitoring
  // mute; a 500ms hold clears the whole row. Pointer events for touch+mouse.
  attachLabelHandlers(label: HTMLDivElement, ti: number): void {
    const HOLD_MS = 500;
    const MOVE_TOL = 8;
    let holdTimer: number | null = null;
    let cleared = false;
    let moved = false;
    let startX = 0, startY = 0;

    const stopHoldAnim = () => {
      if (holdTimer !== null) { clearTimeout(holdTimer); holdTimer = null; }
      label.classList.remove('holding');
    };
    const onMove = (e: PointerEvent) => {
      if (Math.abs(e.clientX - startX) > MOVE_TOL || Math.abs(e.clientY - startY) > MOVE_TOL) {
        moved = true;
        stopHoldAnim();
      }
    };
    const detach = () => {
      label.removeEventListener('pointermove', onMove);
      label.removeEventListener('pointerup', onUp);
      label.removeEventListener('pointercancel', onCancel);
    };
    const onUp = (e: PointerEvent) => {
      try { label.releasePointerCapture(e.pointerId); } catch { /* not captured */ }
      detach();
      const wasHolding = holdTimer !== null; // hold hadn't fired yet → it's a tap
      stopHoldAnim();
      if (cleared || moved) return;
      if (wasHolding) this.toggleRowMute(ti);
    };
    const onCancel = () => { detach(); stopHoldAnim(); };
    label.addEventListener('pointerdown', (e: PointerEvent) => {
      if (e.button !== 0) return;
      e.preventDefault();
      try { label.setPointerCapture(e.pointerId); } catch { /* unsupported */ }
      cleared = false; moved = false;
      startX = e.clientX; startY = e.clientY;
      label.classList.add('holding');
      holdTimer = window.setTimeout(() => {
        holdTimer = null;
        label.classList.remove('holding');
        cleared = true;
        this.clearRow(ti);
      }, HOLD_MS);
      label.addEventListener('pointermove', onMove);
      label.addEventListener('pointerup', onUp);
      label.addEventListener('pointercancel', onCancel);
    });
  }

  toggleRowMute(ti: number): void {
    const muted = !this.mutedRows.has(ti);
    if (muted) this.mutedRows.add(ti); else this.mutedRows.delete(ti);
    const track = this.seq.tracks[ti];
    if (track) {
      track.muted = muted;
      // A held/legato tonal voice keeps sounding until something releases it —
      // muting the row stops new triggers but won't silence a note already
      // ringing. Release it explicitly so mute is immediate.
      if (muted) track.voice.release?.();
    }
    this.rowEls[ti]?.classList.toggle('muted', muted);
  }

  // Hold-to-clear: turn off every step in the row, drop the mute, and flash the
  // label to confirm. The cleared steps are part of the pattern, so this DOES
  // sync (collab) and persist (URL hash) like any other edit.
  clearRow(ti: number): void {
    const track = this.seq.tracks[ti];
    if (!track) return;
    let changed = false;
    for (let s = 0; s < track.steps.length; s++) {
      if (track.steps[s]) {
        track.steps[s] = false;
        this.stepCells[ti]?.[s]?.classList.remove('on');
        this.setStepOwner(ti, s, null);
        changed = true;
      }
    }
    if (track.pitches) track.pitches.fill(DEFAULT_PITCH);
    // Silence any note still ringing from the row we just emptied (a held tone
    // voice won't stop on its own once its steps are gone).
    track.voice.release?.();
    if (this.mutedRows.has(ti)) {
      this.mutedRows.delete(ti);
      track.muted = false;
      this.rowEls[ti]?.classList.remove('muted');
    }
    // Confirm flash on the label (re-trigger the CSS animation).
    const labelEl = this.rowEls[ti]?.querySelector('.label') as HTMLElement | null;
    if (labelEl) {
      labelEl.classList.remove('cleared');
      void labelEl.offsetWidth;
      labelEl.classList.add('cleared');
    }
    if (changed) {
      this.broadcastFull();
      this.pushHash();
    }
  }

  // Short human label for a peer dot. Users can name themselves (click their
  // own chip); unnamed peers fall back to "You" / "Anon".
  private peerLabel(id: string, isYou: boolean): string {
    if (isYou) return this.collab.localName ?? 'You';
    return this.peerNames.get(id) ?? 'Anon';
  }

  private isPeerPlaying(peerId: string, isYou: boolean): boolean {
    return isYou ? this.seq.isPlaying() : (this.peerPlaying.get(peerId) ?? false);
  }

  // Notices new collaborators arriving and queues the celebratory entrance.
  // On the first roster we see for a room we just *seed* the known set (no
  // fanfare for people already there). After that, each new id gets a burst —
  // fired the moment their name delta lands, or after a short grace timeout
  // with a generic label if they never name themselves.
  private trackRoomEntries(you: Peer | null, others: Peer[], liveIds: Set<string>): void {
    if (this.collab.roomId !== this.celebrationRoomId) {
      this.celebrationRoomId = this.collab.roomId;
      this.seenPeerIds.clear();
      this.pendingEntryCelebration.clear();
      this.peerEntrySeeded = false;
    }
    if (!this.collab.roomId) return;
    // Bail until we've actually received the room `welcome` (which carries the
    // full existing roster). Some call sites fire renderPresenceRow with an
    // empty roster right after connect(); seeding off that would mark the room
    // "seeded" prematurely, then treat everyone already there as a fresh
    // arrival when the real roster lands — i.e. the joiner would see a
    // "joined the room" burst for each peer who was already present.
    if (!you) return;

    if (!this.peerEntrySeeded) {
      for (const o of others) this.seenPeerIds.add(o.id);
      this.peerEntrySeeded = true;
    } else {
      for (const o of others) {
        if (this.seenPeerIds.has(o.id)) continue;
        this.seenPeerIds.add(o.id);
        this.pendingEntryCelebration.add(o.id);
        const peerId = o.id;
        const color = o.color;
        window.setTimeout(() => {
          if (!this.pendingEntryCelebration.has(peerId)) return;
          this.pendingEntryCelebration.delete(peerId);
          this.celebrateEntry(this.peerNames.get(peerId) ?? 'Someone', color);
        }, 1600);
      }
    }
    for (const id of Array.from(this.seenPeerIds)) {
      if (!liveIds.has(id)) this.seenPeerIds.delete(id);
    }
    for (const id of Array.from(this.pendingEntryCelebration)) {
      if (!liveIds.has(id)) this.pendingEntryCelebration.delete(id);
    }
  }

  private celebrateEntry(name: string, color: string): void {
    const safe = name.length > 24 ? name.slice(0, 23) + '…' : name;
    const overlay = el('div', { className: 'entry-burst', attrs: { role: 'status', 'aria-live': 'polite' } });
    overlay.style.setProperty('--peer', color || 'var(--accent)');
    const card = el('div', { className: 'entry-burst-card' });
    card.innerHTML = '<span class="entry-name"></span> joined the room';
    card.querySelector('.entry-name')!.textContent = safe;
    const N = 18;
    for (let i = 0; i < N; i++) {
      const sp = el('div', { className: 'entry-spark' });
      const ang = (Math.PI * 2 * i) / N + (Math.random() - 0.5) * 0.5;
      const dist = 110 + Math.random() * 150;
      sp.style.setProperty('--dx', `${Math.cos(ang) * dist}px`);
      sp.style.setProperty('--dy', `${Math.sin(ang) * dist}px`);
      sp.style.setProperty('--rot', `${Math.random() * 720 - 360}deg`);
      sp.style.setProperty('--c', i % 3 === 0 ? '#ffffff' : (color || 'var(--accent)'));
      sp.style.animationDelay = `${40 + Math.random() * 80}ms`;
      card.appendChild(sp);
    }
    overlay.appendChild(card);
    document.body.appendChild(overlay);
    window.setTimeout(() => overlay.remove(), 2700);
  }

  // ==== Live emoji reactions ====
  // The compact picker strip that lives at the right edge of the presence row.
  private buildEmojiBar(): HTMLDivElement {
    const bar = el('div', { className: 'presence-emoji-bar', attrs: { role: 'group', 'aria-label': 'Send a reaction' } });
    for (const emoji of EMOJI_REACTIONS) {
      const btn = el('button', { className: 'emoji-react-btn', text: emoji, attrs: { type: 'button', 'aria-label': `React ${emoji}` } });
      btn.addEventListener('click', () => this.sendEmoji(emoji));
      bar.appendChild(btn);
    }
    return bar;
  }

  // Local click on a reaction: client-side debounce, then broadcast + float
  // our own emoji out of our chip immediately (the server doesn't echo to us).
  private sendEmoji(emoji: string): void {
    if (!EMOJI_REACTIONS.includes(emoji)) return;
    if (!this.collab.isConnected()) return;
    const now = Date.now();
    if (now - this.lastEmojiSentAt < EMOJI_SEND_COOLDOWN_MS) return;
    this.lastEmojiSentAt = now;
    track('emoji_sent', { emoji });
    this.collab.sendEmoji(emoji);
    if (this.collab.me) this.floatEmoji(this.collab.me.id, emoji);
  }

  // Incoming reaction from a peer: validate, drop spammy bursts (per-peer),
  // then float it out of that peer's chip.
  private handleIncomingEmoji(peerId: string, emoji: string): void {
    if (!EMOJI_REACTIONS.includes(emoji)) return;
    const now = Date.now();
    const last = this.lastEmojiRecvAt.get(peerId) ?? 0;
    if (now - last < EMOJI_RECV_COOLDOWN_MS) return;
    this.lastEmojiRecvAt.set(peerId, now);
    this.floatEmoji(peerId, emoji);
  }

  // Spawn an emoji element near a peer's name chip and let CSS float it up and
  // dissolve. Falls back to the presence row's center if the chip isn't found.
  private floatEmoji(peerId: string, emoji: string): void {
    const anchor =
      this.presenceDotsEl?.querySelector<HTMLElement>(`.presence-peer[data-id="${CSS.escape(peerId)}"]:not(.leaving)`) ??
      this.presenceDotsEl ?? this.presenceRowEl;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return;
    const n = this.emojiFloatSeq++;
    // Fan successive fires across a small horizontal spread so they don't stack.
    const spread = ((n % 5) - 2) * 13 + (Math.random() - 0.5) * 8;
    const drift = (Math.random() - 0.5) * 36;
    const fx = el('div', { className: 'emoji-float', text: emoji });
    fx.style.left = `${rect.left + rect.width / 2 + spread}px`;
    fx.style.top = `${rect.top + rect.height / 2}px`;
    fx.style.setProperty('--drift', `${drift}px`);
    fx.style.setProperty('--rot', `${(Math.random() - 0.5) * 28}deg`);
    document.body.appendChild(fx);
    window.setTimeout(() => fx.remove(), 1600);
  }

  // Full-width presence row above the sequencer. Shown whenever a room is
  // active (even solo); hidden otherwise. Reconciles dot elements by peer id
  // so joiners pop in and leavers fade out without disturbing the rest.
  renderPresenceRow(you: Peer | null, others: Peer[]): void {
    const rowEl = this.presenceRowEl;
    if (!rowEl) return;

    // Drop per-peer state for anyone no longer in the roster.
    const liveIds = new Set<string>();
    if (you) liveIds.add(you.id);
    for (const o of others) liveIds.add(o.id);
    for (const id of Array.from(this.peerPlaying.keys())) {
      if (!liveIds.has(id)) this.peerPlaying.delete(id);
    }
    for (const id of Array.from(this.peerNames.keys())) {
      if (!liveIds.has(id)) this.peerNames.delete(id);
    }

    this.trackRoomEntries(you, others, liveIds);

    if (!this.collab.roomId) {
      rowEl.classList.remove('visible');
      rowEl.innerHTML = '';
      this.refreshCollabBtn();
      return;
    }

    // Build the static scaffold once: [ROOM link] [ ...dots... ].
    if (!rowEl.querySelector('.presence-room-link')) {
      rowEl.innerHTML = '';
      const roomLink = el('button', { className: 'presence-room-link', attrs: { 'aria-label': 'Copy live jam link', 'data-tip': 'Copy live jam link' } });
      roomLink.innerHTML = `<span class="presence-room-icon">${ICON_LINK}</span><span>ROOM</span>`;
      roomLink.addEventListener('click', () => this.copyRoomLink(roomLink));
      const primeRoomLink = () => {
        if (this.collab.roomId) void this.shortLinks.prime(this.currentHash(), this.collab.roomId);
      };
      roomLink.addEventListener('pointerenter', primeRoomLink);
      roomLink.addEventListener('focus', primeRoomLink);
      rowEl.appendChild(roomLink);
      this.presenceDotsEl = el('div', { className: 'presence-dots' });
      rowEl.appendChild(this.presenceDotsEl);
      this.presenceEmojiBarEl = this.buildEmojiBar();
      rowEl.appendChild(this.presenceEmojiBarEl);
    }
    rowEl.classList.add('visible');
    const dotsEl = this.presenceDotsEl!;

    const desired: Array<{ id: string; color: string; isYou: boolean }> = [];
    if (you) desired.push({ id: you.id, color: you.color, isYou: true });
    for (const o of others) desired.push({ id: o.id, color: o.color, isYou: false });
    const desiredIds = new Set(desired.map(d => d.id));

    // Fade out departed dots.
    for (const chip of Array.from(dotsEl.querySelectorAll<HTMLElement>('.presence-peer'))) {
      const id = chip.dataset.id;
      if (id && !desiredIds.has(id) && !chip.classList.contains('leaving')) {
        chip.classList.add('leaving');
        window.setTimeout(() => chip.remove(), 280);
      }
    }

    // Add / update.
    for (const { id, color, isYou } of desired) {
      let chip = dotsEl.querySelector<HTMLElement>(`.presence-peer[data-id="${CSS.escape(id)}"]:not(.leaving)`);
      const label = this.peerLabel(id, isYou);
      if (!chip) {
        const c = el('span', {
          className: 'presence-peer joining' + (isYou ? ' you editable-name' : ''),
          attrs: { 'data-id': id, 'data-tip': isYou ? 'Click to set your name' : 'Collaborator' },
        });
        c.appendChild(el('span', { className: 'presence-dot' }));
        c.appendChild(el('span', { className: 'presence-name' }));
        dotsEl.appendChild(c);
        if (isYou) {
          c.setAttribute('role', 'button');
          c.setAttribute('tabindex', '0');
          const open = () => this.openNameModal();
          c.addEventListener('click', open);
          c.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); }
          });
          // Transient popover nudging anonymous users to set a name.
          if (!this.nameHintShown && !this.collab.localName && !loadDisplayName()) {
            this.nameHintShown = true;
            const hint = el('span', { className: 'name-hint', text: 'click to set your name' });
            c.appendChild(hint);
            window.setTimeout(() => {
              hint.classList.add('fade');
              window.setTimeout(() => hint.remove(), 350);
            }, 4500);
          }
        }
        // Commit the .joining (opacity:0 / shifted) state, then drop the class
        // so it transitions in.
        void c.offsetWidth;
        c.classList.remove('joining');
        chip = c;
      }
      chip.style.setProperty('--dot-color', color);
      const dot = chip.querySelector<HTMLElement>('.presence-dot');
      if (dot) dot.style.background = color;
      const name = chip.querySelector<HTMLElement>('.presence-name');
      if (name) name.textContent = label;
      chip.classList.toggle('playing', this.isPeerPlaying(id, isYou));
    }

    this.refreshCollabBtn();
  }

  // Centered modal asking the user for their display name. Semi-opaque dark
  // backdrop; commit on Enter or the "Set name" button, dismiss on Escape, a
  // backdrop click, or "Not now".
  openNameModal(): void {
    if (this.nameModalEl) {
      this.nameModalEl.querySelector<HTMLInputElement>('.name-modal-input')?.focus();
      return;
    }
    const overlay = el('div', { className: 'modal-overlay', attrs: { role: 'dialog', 'aria-modal': 'true', 'aria-label': 'Set your display name' } });
    const card = el('div', { className: 'modal-card name-modal' });
    card.appendChild(el('h3', { className: 'modal-title', text: 'What should we call you?' }));
    card.appendChild(el('p', { className: 'modal-sub', text: 'Your name shows next to your dot in the live jam.' }));
    const input = el('input', {
      className: 'name-modal-input',
      attrs: { type: 'text', maxlength: String(DISPLAY_NAME_MAX), placeholder: 'your name', 'aria-label': 'Your display name' },
    });
    input.value = this.collab.localName ?? '';
    card.appendChild(input);
    const actions = el('div', { className: 'modal-actions' });
    const skipBtn = el('button', { className: 'btn ghost modal-skip', text: 'Not now' });
    const setBtn = el('button', { className: 'btn primary modal-set', text: 'Set name' });
    actions.appendChild(skipBtn);
    actions.appendChild(setBtn);
    card.appendChild(actions);
    overlay.appendChild(card);
    document.body.appendChild(overlay);
    this.nameModalEl = overlay;
    void overlay.offsetWidth;
    overlay.classList.add('visible');
    input.focus();
    input.select();

    let closed = false;
    const close = () => {
      if (closed) return;
      closed = true;
      overlay.classList.remove('visible');
      window.setTimeout(() => overlay.remove(), MODAL_FADE_MS);
      if (this.nameModalEl === overlay) this.nameModalEl = null;
    };
    const commit = () => {
      const v = input.value.trim().slice(0, DISPLAY_NAME_MAX);
      close();
      if (v) this.setMyName(v);
    };
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
    card.addEventListener('click', (e) => e.stopPropagation());
    skipBtn.addEventListener('click', close);
    setBtn.addEventListener('click', commit);
    input.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Enter') { e.preventDefault(); commit(); }
      else if (e.key === 'Escape') { e.preventDefault(); close(); }
    });
  }

  setMyName(name: string): void {
    const trimmed = name.trim().slice(0, DISPLAY_NAME_MAX);
    if (!trimmed) return;
    saveDisplayName(trimmed);
    this.collab.setLocalName(trimmed);
    this.renderPresenceRow(this.collab.me, this.collab.others);
    this.showToast(`Name set · ${trimmed}`);
  }

  // Toggle the playing-pulse class on existing presence dots without a full
  // re-render (so we don't re-trigger join animations on every play/pause).
  refreshPresencePlaying(): void {
    const dotsEl = this.presenceDotsEl;
    if (!dotsEl) return;
    for (const chip of Array.from(dotsEl.querySelectorAll<HTMLElement>('.presence-peer'))) {
      const id = chip.dataset.id;
      if (!id) continue;
      const isYou = chip.classList.contains('you');
      chip.classList.toggle('playing', this.isPeerPlaying(id, isYou));
    }
  }

  // Tell the room whether we're currently playing. Suppressed while applying a
  // remote delta (echo guard inside broadcast()).
  broadcastPlayState(): void {
    this.broadcast({ type: 'play', playing: this.seq.isPlaying() });
  }

  // Append a snapshot to the undo history. Truncates the redo branch when a
  // new edit lands after an undo. Called from pushHash, so any code path that
  // mutates state already records itself for free.
  recordHistory(hash: string): void {
    if (this.isApplyingHistory) return;
    if (this.history[this.historyCursor] === hash) return;
    if (this.historyCursor < this.history.length - 1) {
      this.history = this.history.slice(0, this.historyCursor + 1);
    }
    this.history.push(hash);
    if (this.history.length > 100) this.history.shift();
    else this.historyCursor++;
    this.refreshUndoRedoUI();
  }

  undo(): void {
    if (this.historyCursor <= 0) return;
    this.historyCursor--;
    this.applyHistorySnapshot();
  }

  redo(): void {
    if (this.historyCursor >= this.history.length - 1) return;
    this.historyCursor++;
    this.applyHistorySnapshot();
  }

  private applyHistorySnapshot(): void {
    const hash = this.history[this.historyCursor];
    const parsed = parseHash(hash);
    if (!parsed) return;
    this.isApplyingHistory = true;
    try {
      this.loadFromHash(parsed);
    } finally {
      this.isApplyingHistory = false;
    }
    this.refreshUndoRedoUI();
  }

  refreshUndoRedoUI(): void {
    if (this.undoBtn) this.undoBtn.disabled = this.historyCursor <= 0;
    if (this.redoBtn) this.redoBtn.disabled = this.historyCursor >= this.history.length - 1;
  }

  saveBeat(): void {
    const beatOnlyHash = this.currentHash();
    const existing = loadSavedBeats();
    const presetLabel = matchedPresetLabel(this.seq.tracks, this.seq.numSteps, this.currentBpm, this.toneTimbre);
    const base = presetLabel ?? 'Beat';
    const autoLabel = uniqueSavedLabel(base, existing);
    const beatOnlyUrl = window.location.origin + window.location.pathname + beatOnlyHash;
    addSavedBeat(beatOnlyUrl, beatOnlyHash, autoLabel);
    track('beat_saved', { slot_index: 0 });
    this.renderSavedBeats();
    const btn = this.root.querySelector('.btn.save') as HTMLButtonElement;
    if (btn) {
      btn.innerHTML = ICON_CHECK;
      btn.classList.add('btn-saved');
      setTimeout(() => { btn.innerHTML = ICON_SAVE; btn.classList.remove('btn-saved'); }, 2000);
    }
    this.showToast(`Saved · "${autoLabel}"`);
  }

  // The beat-only URL (no room=) — what "Share" hands out so recipients open a
  // private copy, not a live jam they'd accidentally co-edit.
  private beatOnlyUrl(): string {
    return window.location.origin + window.location.pathname + this.currentHash();
  }

  // Prime a short snapshot URL after edits settle. Share/copy handlers only
  // read this cache; they never await the network before invoking a
  // gesture-gated browser API.
  private scheduleShortLinkPrime(): void {
    if (this.shortLinkPrimeTimer !== null) window.clearTimeout(this.shortLinkPrimeTimer);
    this.shortLinkPrimeTimer = window.setTimeout(() => {
      this.shortLinkPrimeTimer = null;
      void this.shortLinks.prime(this.currentHash());
    }, 2500);
  }

  private shareUrl(longUrl: string, beatHash: string, room?: string): { url: string; shortened: boolean } {
    const short = this.shortLinks.get(beatHash, room);
    return short ? { url: short, shortened: true } : { url: longUrl, shortened: false };
  }

  private reportShortenFailure(beatHash: string, kind: 'beat' | 'room', room?: string): void {
    void this.shortLinks.prime(beatHash, room).then((url) => {
      if (!url) track('share_failed', { kind, stage: 'shorten' });
    });
  }

  // "Share" — the quick, one-tap path. On touch devices with the Web Share API
  // we hand off to the native share sheet (great for texting / DMing a beat).
  // On desktop (no touch, or no share API) we just copy the link to the
  // clipboard and confirm with a toast — cleaner than an empty share dialog.
  shareBeat(btn: HTMLButtonElement): void {
    const beatHash = this.currentHash();
    const { url, shortened } = this.shareUrl(this.beatOnlyUrl(), beatHash);
    const okMsg = shortened ? 'Link copied!' : 'Full link copied (couldn’t shorten)';
    const isTouch = (navigator.maxTouchPoints ?? 0) > 0 || 'ontouchstart' in window;
    if (typeof navigator.share === 'function' && isTouch) {
      const shareData: ShareData = { title: 'Beat Swarm', text: 'Check out this beat I made on #BeatSwarm', url };
      // Crucially, share() is called in this synchronous click stack.
      let sharePromise: Promise<void>;
      try {
        sharePromise = navigator.share(shareData);
      } catch {
        sharePromise = Promise.reject(new Error('native share failed'));
      }
      void sharePromise.then(() => {
        track('beat_shared', { method: 'native' });
        this.flashCopied(btn);
      }).catch((err: unknown) => {
        // User dismissed the share sheet — nothing to report.
        if (err && (err as { name?: string }).name === 'AbortError') return;
        track('share_failed', { kind: 'beat', stage: 'native' });
        this.copyToClipboard(url, btn, okMsg, 'beat_shared', 'beat');
      });
    } else {
      // copyTextNow() also starts in this click stack; no network awaits first.
      this.copyToClipboard(url, btn, okMsg, 'beat_shared', 'beat');
    }
    if (!shortened) this.reportShortenFailure(beatHash, 'beat');
  }

  // Collab button — a toggle. Off → start a live jam; on → end it.
  toggleCollab(): void {
    if (this.collab.roomId) this.stopCollab();
    else this.startCollab();
  }

  // "Collab" — the deliberate path. Spins up (or reuses) a live room, puts the
  // room id in the URL, lights the button + presence row, and copies the
  // invite link so the user can paste it to whoever they want to jam with.
  // The button goes idle (blue outline) → active (solid blue, pulsing) in one
  // step — no green "copied" flash on it (the link copy is reported via toast).
  startCollab(): void {
    if (!this.collab.roomId) {
      this.collab.connect(generateRoomId());
      setCollabActive();
      track('collab_joined', { room_id: this.collab.roomId });
    }
    this.pushHash(); // URL now carries room=
    this.refreshCollabBtn();
    this.renderPresenceRow(this.collab.me, this.collab.others);
    const room = this.collab.roomId ?? undefined;
    const beatHash = this.currentHash();
    const { url, shortened } = this.shareUrl(window.location.href, beatHash, room);
    this.copyToClipboard(
      url,
      null,
      shortened ? 'Live jam started · invite link copied' : 'Live jam started · full link copied',
      'room_link_shared',
      'room',
    );
    if (!shortened) this.reportShortenFailure(beatHash, 'room', room);
  }

  // End the live jam: drop the socket, strip room= from the URL, hide the
  // presence row, and return the Collab button to its idle (non-pulsing) state.
  stopCollab(): void {
    this.collab.disconnect();
    this.peerPlaying.clear();
    this.peerNames.clear();
    this.renderPresenceRow(null, []);
    this.pushHash(); // URL no longer carries room=
    this.refreshCollabBtn();
    this.showToast('Live jam ended');
  }

  copyRoomLink(btn: HTMLElement): void {
    if (!this.collab.roomId) return;
    const beatHash = this.currentHash();
    const room = this.collab.roomId;
    const { url, shortened } = this.shareUrl(window.location.href, beatHash, room);
    const okMsg = shortened ? 'Live jam link copied' : 'Full jam link copied (couldn’t shorten)';
    const isTouch = (navigator.maxTouchPoints ?? 0) > 0 || 'ontouchstart' in window;
    if (typeof navigator.share === 'function' && isTouch) {
      const shareData: ShareData = { title: 'Beat Swarm', text: 'Join my live jam on #BeatSwarm', url };
      let sharePromise: Promise<void>;
      try {
        sharePromise = navigator.share(shareData);
      } catch {
        sharePromise = Promise.reject(new Error('native share failed'));
      }
      void sharePromise.then(() => {
        track('room_link_shared', { method: 'native' });
        this.flashCopied(btn);
      }).catch((err: unknown) => {
        if (err && (err as { name?: string }).name === 'AbortError') return;
        track('share_failed', { kind: 'room', stage: 'native' });
        this.copyToClipboard(url, btn, okMsg, 'room_link_shared', 'room');
      });
    } else {
      this.copyToClipboard(url, btn, okMsg, 'room_link_shared', 'room');
    }
    if (!shortened) this.reportShortenFailure(beatHash, 'room', room);
  }

  private copyToClipboard(
    url: string,
    btn: HTMLElement | null,
    okMsg: string,
    successEvent: 'beat_shared' | 'room_link_shared',
    kind: 'beat' | 'room',
  ): void {
    void copyTextNow(url).then((copied) => {
      if (copied) {
        track(successEvent, { method: 'clipboard' });
        this.showToast(okMsg);
        if (btn) this.flashCopied(btn);
        return;
      }
      track('share_failed', { kind, stage: 'clipboard' });
      this.showToast('Could not copy link — select it in the next dialog', true);
      window.prompt('Copy this link', url);
    });
  }

  // Brief "copied" affirmation on a button: icon buttons flash a check then
  // restore; text buttons (the ROOM link) just pulse the .copied class.
  private flashCopied(btn: HTMLElement): void {
    const isIcon = btn.classList.contains('icon-btn');
    const original = isIcon ? btn.innerHTML : null;
    if (isIcon) btn.innerHTML = ICON_CHECK;
    btn.classList.add('copied');
    setTimeout(() => {
      if (isIcon && original != null) btn.innerHTML = original;
      btn.classList.remove('copied');
    }, 2000);
  }

  // Lightweight toast pill anchored top-center. Auto-dismisses after 2.4s.
  // Single shared element; subsequent calls reset its content + timer.
  toastEl: HTMLDivElement | null = null;
  toastTimer: number | null = null;
  showToast = (message: string, isError = false): void => {
    if (!this.toastEl) {
      this.toastEl = el('div', { className: 'toast', attrs: { role: 'status', 'aria-live': 'polite' } });
      document.body.appendChild(this.toastEl);
    }
    this.toastEl.textContent = message;
    this.toastEl.classList.toggle('error', isError);
    this.toastEl.classList.add('visible');
    if (this.toastTimer != null) window.clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => {
      this.toastEl?.classList.remove('visible');
    }, TOAST_DURATION_MS);
  }

  // Renders a small inline error chip overlaid on a saved-beat card when WAV
  // bouncing fails. Auto-clears after 3.5s. Replaces the prior alert() so the
  // failure stays scoped to the failing beat rather than blocking the whole UI.
  showWavError(chip: HTMLElement, err: unknown): void {
    chip.querySelectorAll('.chip-error').forEach(n => n.remove());
    const detail = err instanceof Error ? err.message : String(err);
    const errEl = el('div', { className: 'chip-error', attrs: { role: 'alert', title: `WAV render failed: ${detail}` } });
    errEl.innerHTML = `${ICON_ALERT}<span>WAV failed</span>`;
    chip.appendChild(errEl);
    chip.classList.add('has-error');
    window.setTimeout(() => {
      errEl.remove();
      chip.classList.remove('has-error');
    }, WAV_ERROR_DURATION_MS);
  }

  renderSavedBeats(): void {
    if (!this.savedEl || !this.savedStripEl) return;
    const beats = loadSavedBeats();
    this.savedStripEl.innerHTML = '';
    if (beats.length === 0) {
      this.savedEl.classList.add('empty');
      return;
    }
    this.savedEl.classList.remove('empty');
    for (const [slotIndex, b] of beats.entries()) {
      const chip = el('a', { className: 'saved-chip', attrs: { href: b.url, title: b.url } });

      chip.appendChild(el('span', { className: 'ts', text: fmtSavedTs(b.ts) }));

      if (b.label) {
        chip.appendChild(el('span', { className: 'chip-label', text: b.label }));
      }

      // Pencil — inline label editing
      const pencilBtn = el('button', { className: 'chip-btn chip-rename', attrs: { 'aria-label': 'Rename beat', 'data-tip': 'Rename' } });
      pencilBtn.innerHTML = ICON_PENCIL;
      pencilBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const current = b.label ?? '';
        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'chip-label-input';
        input.value = current;
        input.maxLength = 30;
        input.placeholder = 'name…';
        chip.replaceChild(input, pencilBtn);
        input.focus();
        input.select();
        const commit = () => {
          renameSavedBeat(b.hash, input.value);
          this.renderSavedBeats();
        };
        input.addEventListener('blur', commit);
        input.addEventListener('keydown', (ke) => {
          if (ke.key === 'Enter') { ke.preventDefault(); commit(); }
          if (ke.key === 'Escape') { ke.preventDefault(); this.renderSavedBeats(); }
        });
      });
      chip.appendChild(pencilBtn);

      // Download — render to WAV
      const dlBtn = el('button', { className: 'chip-btn chip-download', attrs: { 'aria-label': 'Download WAV', 'data-tip': 'Download WAV' } });
      dlBtn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 4v12"/><path d="m6 12 6 6 6-6"/><path d="M5 20h14"/></svg>';
      dlBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (dlBtn.classList.contains('busy')) return;
        dlBtn.classList.add('busy');
        const prevHTML = dlBtn.innerHTML;
        dlBtn.textContent = '…';
        try {
          await downloadBeatAsWav(b);
        } catch (err) {
          console.error('WAV download failed', err);
          this.showWavError(chip, err);
        } finally {
          dlBtn.classList.remove('busy');
          dlBtn.innerHTML = prevHTML;
        }
      });
      chip.appendChild(dlBtn);

      // X — delete this beat
      const delBtn = el('button', { className: 'chip-btn chip-delete', attrs: { 'aria-label': 'Delete beat', 'data-tip': 'Delete' } });
      delBtn.innerHTML = ICON_X;
      delBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        openConfirmModal({
          title: 'Delete this beat?',
          message: b.label ? `"${b.label}" will be permanently deleted.` : 'This beat will be permanently deleted.',
          confirmLabel: 'Delete',
          onConfirm: () => {
            removeSavedBeat(b.hash);
            // Defer re-render by one tick so the touch event fully clears before
            // new chips appear — prevents the next chip inheriting the :active state.
            (document.activeElement as HTMLElement | null)?.blur();
            setTimeout(() => this.renderSavedBeats(), 0);
          },
        });
      });
      chip.appendChild(delBtn);

      // Use href so middle-click / cmd-click open in a new tab as expected.
      // For a normal click we route through hashchange so the page doesn't
      // reload — keeping playing audio alive is essential.
      chip.addEventListener('click', (e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || (e as MouseEvent).button !== 0) return;
        e.preventDefault();
        if (window.location.hash === b.hash) return;
        track('beat_loaded', { slot_index: slotIndex });
        window.location.hash = b.hash;
      });
      this.savedStripEl.appendChild(chip);
    }
  }

  // Resume the AudioContext (must run inside a user gesture) and do the
  // one-time post-resume setup. Idempotent — safe to call from the splash,
  // togglePlay, or the deferred first-gesture listener.
  async bootAudio(): Promise<void> {
    if (this.audioReady) return;
    await startAudio();
    this.audioReady = true;
    // Now that the AudioContext is running, push the BPM to the Transport.
    Tone.getTransport().bpm.value = this.currentBpm;
    // If we booted into organic mode from a shared URL, kick off the sample
    // fetch now so playback isn't silent on first tap. Buffers load async; a
    // brief silence on the first hit is acceptable and rare.
    if (this.audioSource === 'organic') void ensureSamplesLoaded();
  }

  async handleSplash(): Promise<void> {
    await this.bootAudio();
    markSplashSeen();
    this.splash.remove();
    // First-time tip: surface the drag-to-pitch interaction so it's discoverable
    // even on touch devices where the bottom keyboard hint is easy to miss.
    // Auto-dismisses on its own after TOAST_DURATION_MS, but also clears the
    // moment the user touches the grid so it never lingers over what they tap.
    if (!toneHintSeen()) {
      window.setTimeout(() => {
        this.showToast('Tip · drag tone cells up/down to change pitch');
        document.addEventListener('pointerdown', () => {
          this.toastEl?.classList.remove('visible');
          if (this.toastTimer != null) window.clearTimeout(this.toastTimer);
        }, { once: true });
      }, TIP_TOAST_DELAY_MS);
    }
  }

  releaseAllVoices(): void {
    this.seq.tracks.forEach(t => t.voice.release?.());
  }

  async togglePlay(): Promise<void> {
    if (!this.audioReady) {
      await this.bootAudio();
      this.splash?.remove();
    } else if (Tone.getContext().state !== 'running') {
      // iOS/Safari suspend ("interrupt") the AudioContext after a phone call,
      // Siri, or a long background stint and don't always resume it on their
      // own. Play is a user gesture, so resume here; cheap no-op otherwise.
      try { await startAudio(); } catch (err) { console.warn('AudioContext resume failed', err); }
    }
    if (this.seq.isPlaying()) {
      this.seq.stop();
      this.releaseAllVoices();
      this.setPlayUI(false);
      recordPlayStop();
      track('beat_stopped');
    } else {
      this.seq.start();
      this.setPlayUI(true);
      recordPlayStart();
      track('beat_played', {
        bpm: this.currentBpm,
        active_sources: this.activeSourceNames(),
        step_count: this.seq.numSteps,
      });
    }
  }

  // Names of the tracks (KICK / SNARE / …) that currently have at least one
  // active step — used as an analytics property.
  private activeSourceNames(): string[] {
    const names: string[] = [];
    this.seq.tracks.forEach((t, i) => {
      if (t.audible !== false && t.steps.some(Boolean)) names.push(this.currentTrackDefs[i].name);
    });
    return names;
  }

  // Updates the play/stop button in one place. The button shows ▶ at rest
  // and ■ while playing; the .playing class drives the green glow. Also the
  // single funnel for telling the room our play-state (echo-guarded) and
  // refreshing the pulse on our own presence dot.
  setPlayUI(playing: boolean): void {
    if (!this.playBtn) return;
    this.playBtn.innerHTML = playing ? ICON_PAUSE : ICON_PLAY;
    this.playBtn.dataset.tip = playing ? 'Pause' : 'Play';
    this.playBtn.setAttribute('aria-label', playing ? 'Pause' : 'Play');
    this.playBtn.classList.toggle('playing', playing);
    this.broadcastPlayState();
    this.refreshPresencePlaying();
  }

  onTick(step: number): void {
    for (const row of this.stepCells) {
      for (const cell of row) cell.classList.remove('playhead');
    }
    if (step < 0) return;
    for (const row of this.stepCells) {
      if (row[step]) row[step].classList.add('playhead');
    }
  }

  onBpmChange(commit: boolean): void {
    const bpm = Number(this.bpmSlider.value);
    this.currentBpm = bpm;
    this.bpmDisplay.textContent = String(bpm);
    this.seq.setBpm(bpm);
    if (commit) {
      this.broadcast({ type: 'bpm', bpm });
      this.pushHash();
      track('bpm_changed', { bpm });
    } else {
      this.pushHashLive();
    }
  }

  clearAll(): void {
    this.seq.clear();
    // Drop any held/legato note that's still ringing — clearing the grid
    // leaves a droning tone voice playing otherwise.
    this.releaseAllVoices();
    this.seq.tracks.forEach((t) => {
      if (t.pitches) t.pitches.fill(DEFAULT_PITCH);
    });
    this.renderGrid();
    this.broadcastFull();
    this.pushHash();
  }
}

function midiName(midi: number): string {
  const names = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
  const oct = Math.floor(midi / 12) - 1;
  return `${names[midi % 12]}${oct}`;
}

const root = document.getElementById('app');
if (root) new App(root);
